import * as React from 'react';
import React__default, { Component, useRef, useState, useEffect } from 'react';
import { legendImageStore, WMImageEventType, webmapUtils, getLegendGraphicURLForLayer, getMapImageStore, getWMJSMapById, debugLogger, DebugType, LayerType, WMLayer, registerWMLayer, WMBBOX, getWMLayerById, WMJSMap, tilesettings } from '@opengeoweb/webmap';
import { Paper, Box, Typography, Grid, TextField, FormControl, InputLabel, Select, MenuItem, FormLabel, Switch } from '@mui/material';
import { CustomTooltip, ToolContainerDraggable, dateUtils, CustomIconButton } from '@opengeoweb/shared';
import _, { cloneDeep } from 'lodash';
import proj4 from 'proj4';
import * as turf from '@turf/turf';
import { polygonToLineString, booleanClockwise, rewind } from '@turf/turf';
import { Delete, Edit, DrawRegion, DrawPolygon, Location, DrawFIRLand, ArrowUp, Home, Add, Minus } from '@opengeoweb/theme';
import produce from 'immer';
import { debounce } from 'throttle-debounce';
import { differenceInMinutes } from 'date-fns';
import { useResizeDetector } from 'react-resize-detector';

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * */
class CanvasComponent extends Component {
  constructor(props) {
    super(props);
    this.canvas = null;
    this.ctx = null;
    this.currentWidth = 1;
    this.currentHeight = 1;
    this.canvascontaineroutside = null;
    this.canvascontainer = null;
    this.timer = null;
    this.redrawTimer = null;
    this.loopHasStarted = false;
    this.mounted = false;
    this.updateCanvas = this.updateCanvas.bind(this);
    this.handleMouseMoveEvent = this.handleMouseMoveEvent.bind(this);
    this.handleMouseUpEvent = this.handleMouseUpEvent.bind(this);
    this.handleMouseDownEvent = this.handleMouseDownEvent.bind(this);
    this.resize = this.resize.bind(this);
    this._handleWindowResize = this._handleWindowResize.bind(this);
    this.handleClickEvent = this.handleClickEvent.bind(this);
    this.handleWheelEvent = this.handleWheelEvent.bind(this);
    this.handleTouchStartEvent = this.handleTouchStartEvent.bind(this);
    this.handleTouchEndEvent = this.handleTouchEndEvent.bind(this);
    this.handleTouchMoveEvent = this.handleTouchMoveEvent.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.startLoop = this.startLoop.bind(this);
    this._documentKeyDown = this._documentKeyDown.bind(this);
    this._isFocussed = false;
    this.canvas = null;
    this.canvascontaineroutside = /*#__PURE__*/React__default.createRef();
    this.canvascontainer = /*#__PURE__*/React__default.createRef();
  }
  componentDidMount() {
    this.mounted = true;
    if (this.canvas) {
      this.canvas.addEventListener('mousemove', this.handleMouseMoveEvent);
      this.canvas.addEventListener('mouseup', this.handleMouseUpEvent);
      this.canvas.addEventListener('mousedown', this.handleMouseDownEvent);
      this.canvas.addEventListener('touchstart', this.handleTouchStartEvent);
      this.canvas.addEventListener('touchend', this.handleTouchEndEvent);
      this.canvas.addEventListener('touchmove', this.handleTouchMoveEvent);
      this.canvas.addEventListener('wheel', this.handleWheelEvent);
      this.canvas.addEventListener('click', this.handleClickEvent);
      document.addEventListener('keydown', this._documentKeyDown, false);
      this.resize();
      this.updateCanvas();
    }
    const {
      onKeyUp,
      redrawInterval: loopInterval
    } = this.props;
    if (onKeyUp) {
      window.addEventListener('keyup', this.onKeyUp);
    }
    window.addEventListener('resize', this._handleWindowResize);
    if (this.canvascontaineroutside && this.canvascontaineroutside.current && onKeyUp) {
      this.canvascontaineroutside.current.focus();
    }
    this.timer = setInterval(() => {
      this.resize();
    }, 1000);
    if (loopInterval) {
      this.redrawTimer = setInterval(() => {
        this.updateCanvas();
      }, loopInterval);
    }
  }
  shouldComponentUpdate() {
    window.requestAnimationFrame(() => {
      this.updateCanvas();
    });
    return false;
  }
  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this._handleWindowResize);
    const {
      onKeyUp
    } = this.props;
    if (onKeyUp) {
      window.removeEventListener('keyup', this.onKeyUp);
    }
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this.handleMouseDownEvent);
      this.canvas.removeEventListener('mouseup', this.handleMouseUpEvent);
      this.canvas.removeEventListener('mousemove', this.handleMouseMoveEvent);
      this.canvas.removeEventListener('touchstart', this.handleTouchStartEvent);
      this.canvas.removeEventListener('touchend', this.handleTouchEndEvent);
      this.canvas.removeEventListener('touchmove', this.handleTouchMoveEvent);
      this.canvas.removeEventListener('wheel', this.handleWheelEvent);
      this.canvas.removeEventListener('click', this.handleClickEvent);
      document.removeEventListener('keydown', this._documentKeyDown);
    }
    clearInterval(this.timer);
    clearInterval(this.redrawTimer);
  }
  handleMouseMoveEvent(event) {
    const {
      onMouseMove,
      onCanvasClick
    } = this.props;
    const x = event.offsetX;
    const y = event.offsetY;
    if (event.buttons === 1) {
      onCanvasClick(x, y, this.currentWidth, this.currentHeight);
    }
    onMouseMove(x, y, event, this.currentWidth);
  }
  handleMouseUpEvent(event) {
    const {
      onMouseUp
    } = this.props;
    const x = event.offsetX;
    onMouseUp(x);
  }
  handleMouseDownEvent(event) {
    const {
      onMouseDown
    } = this.props;
    const x = event.offsetX;
    const y = event.offsetY;
    onMouseDown(x, y, this.currentWidth);
  }
  handleTouchStartEvent(event) {
    const {
      onTouchStart
    } = this.props;
    onTouchStart(event, this.currentWidth);
  }
  handleTouchEndEvent() {
    const {
      onTouchEnd
    } = this.props;
    onTouchEnd();
  }
  handleTouchMoveEvent(event) {
    const {
      onTouchMove,
      onCanvasClick
    } = this.props;
    const touches = event && event.touches && event.touches[0];
    if (touches && touches.clientX && touches.clientY) {
      const x2 = event.touches[0].clientX - event.touches[0].target.offsetLeft;
      const y2 = event.touches[0].clientY;
      onCanvasClick(x2, y2, this.currentWidth, this.currentHeight);
    }
    onTouchMove(event, this.currentWidth);
  }
  handleClickEvent(event) {
    const {
      onCanvasClick
    } = this.props;
    const x = event.offsetX;
    const y = event.offsetY;
    onCanvasClick(x, y, this.currentWidth, this.currentHeight);
  }
  handleWheelEvent(event) {
    const {
      onWheel
    } = this.props;
    onWheel({
      event,
      deltaY: event.deltaY,
      deltaX: event.deltaX,
      canvasWidth: event.srcElement.width,
      mouseX: event.offsetX
    });
  }
  onFocus() {
    const {
      isFocussed
    } = this.props;
    this._isFocussed = true;
    if (isFocussed) {
      isFocussed(true);
    }
  }
  onBlur() {
    const {
      isFocussed
    } = this.props;
    this._isFocussed = false;
    if (isFocussed) {
      isFocussed(false);
    }
  }
  onKeyUp(event) {
    const {
      onKeyUp
    } = this.props;
    if (onKeyUp) {
      onKeyUp(event);
    }
    if (this.canvascontaineroutside && this.canvascontaineroutside.current && onKeyUp) {
      if (event.code === 'Escape') {
        this.canvascontaineroutside.current.focus();
      }
    }
  }
  _documentKeyDown(event) {
    if (this._isFocussed) {
      const {
        onKeyDown
      } = this.props;
      if (onKeyDown) {
        onKeyDown(event);
      }
    }
  }
  _handleWindowResize() {
    this.resize();
  }
  startLoop() {
    const {
      onRenderCanvas
    } = this.props;
    if (this.mounted === false || this.loopHasStarted === false) {
      return;
    }
    if (this.canvas) {
      onRenderCanvas(this.ctx, this.currentWidth, this.currentHeight, this.canvas);
    }
    requestAnimationFrame(this.startLoop);
  }
  updateCanvas() {
    if (!this.canvas) {
      return;
    }
    const {
      onRenderCanvas,
      loop
    } = this.props;
    this.ctx = this.canvas.getContext('2d');
    if (!this.ctx) {
      return;
    }
    if (this.ctx.canvas.height !== this.currentHeight) {
      this.ctx.canvas.height = this.currentHeight;
    }
    if (this.ctx.canvas.width !== this.currentWidth) {
      this.ctx.canvas.width = this.currentWidth;
    }
    if (loop !== true) {
      this.loopHasStarted = false;
      onRenderCanvas(this.ctx, this.currentWidth, this.currentHeight, this.canvas);
    } else if (this.loopHasStarted === false) {
      this.loopHasStarted = true;
      this.startLoop();
    }
  }
  resize() {
    const {
      canvascontainer,
      props: {
        resizeCallback
      }
    } = this;
    if (canvascontainer && canvascontainer.current) {
      const newWidth = canvascontainer.current.clientWidth;
      const newHeight = canvascontainer.current.clientHeight;
      if (newWidth !== undefined && newHeight !== undefined && (this.currentWidth !== newWidth || this.currentHeight !== newHeight)) {
        this.currentWidth = newWidth;
        this.currentHeight = newHeight;
        this.updateCanvas();
        resizeCallback && resizeCallback(newWidth);
      }
    }
  }
  render() {
    this.updateCanvas();
    this._handleWindowResize();
    return /*#__PURE__*/React__default.createElement("div", {
      role: "button",
      ref: this.canvascontaineroutside,
      tabIndex: 0,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      style: {
        height: '100%',
        width: '100%',
        border: 'none',
        display: 'block',
        overflow: 'hidden'
      }
    }, /*#__PURE__*/React__default.createElement("div", {
      ref: this.canvascontainer,
      style: {
        minWidth: 'inherit',
        minHeight: 'inherit',
        width: 'inherit',
        height: 'inherit',
        overflow: 'hidden',
        display: 'block',
        border: 'none'
      },
      "data-testid": "canvas-container"
    }, /*#__PURE__*/React__default.createElement("div", {
      style: {
        overflow: 'visible',
        width: 0,
        height: 0
      },
      role: "presentation",
      "aria-label": "canvas"
    }, /*#__PURE__*/React__default.createElement("canvas", {
      ref: canvas => {
        this.canvas = canvas;
      }
    }))));
  }
}
CanvasComponent.defaultProps = {
  onRenderCanvas: () => {
    /* intentionally left blank */
  },
  onCanvasClick: () => {
    /* intentionally left blank */
  },
  onMouseMove: () => {
    /* intentionally left blank */
  },
  onMouseUp: () => {
    /* intentionally left blank */
  },
  onMouseDown: () => {
    /* intentionally left blank */
  },
  onTouchStart: () => {
    /* intentionally left blank */
  },
  onTouchEnd: () => {
    /* intentionally left blank */
  },
  onTouchMove: () => {
    /* intentionally left blank */
  },
  onWheel: () => {
    /* intentionally left blank */
  }
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var fails$i = function (exec) {
  try {
    return !!exec();
  } catch (error) {
    return true;
  }
};

var fails$h = fails$i;

var functionBindNative = !fails$h(function () {
  // eslint-disable-next-line es/no-function-prototype-bind -- safe
  var test = (function () { /* empty */ }).bind();
  // eslint-disable-next-line no-prototype-builtins -- safe
  return typeof test != 'function' || test.hasOwnProperty('prototype');
});

var NATIVE_BIND$3 = functionBindNative;

var FunctionPrototype$2 = Function.prototype;
var call$e = FunctionPrototype$2.call;
var uncurryThisWithBind = NATIVE_BIND$3 && FunctionPrototype$2.bind.bind(call$e, call$e);

var functionUncurryThis = NATIVE_BIND$3 ? uncurryThisWithBind : function (fn) {
  return function () {
    return call$e.apply(fn, arguments);
  };
};

var uncurryThis$j = functionUncurryThis;

var toString$6 = uncurryThis$j({}.toString);
var stringSlice$1 = uncurryThis$j(''.slice);

var classofRaw$2 = function (it) {
  return stringSlice$1(toString$6(it), 8, -1);
};

var uncurryThis$i = functionUncurryThis;
var fails$g = fails$i;
var classof$7 = classofRaw$2;

var $Object$4 = Object;
var split = uncurryThis$i(''.split);

// fallback for non-array-like ES3 and non-enumerable old V8 strings
var indexedObject = fails$g(function () {
  // throws an error in rhino, see https://github.com/mozilla/rhino/issues/346
  // eslint-disable-next-line no-prototype-builtins -- safe
  return !$Object$4('z').propertyIsEnumerable(0);
}) ? function (it) {
  return classof$7(it) === 'String' ? split(it, '') : $Object$4(it);
} : $Object$4;

// we can't use just `it == null` since of `document.all` special case
// https://tc39.es/ecma262/#sec-IsHTMLDDA-internal-slot-aec
var isNullOrUndefined$4 = function (it) {
  return it === null || it === undefined;
};

var isNullOrUndefined$3 = isNullOrUndefined$4;

var $TypeError$f = TypeError;

// `RequireObjectCoercible` abstract operation
// https://tc39.es/ecma262/#sec-requireobjectcoercible
var requireObjectCoercible$4 = function (it) {
  if (isNullOrUndefined$3(it)) throw new $TypeError$f("Can't call method on " + it);
  return it;
};

// toObject with fallback for non-array-like ES3 strings
var IndexedObject$2 = indexedObject;
var requireObjectCoercible$3 = requireObjectCoercible$4;

var toIndexedObject$5 = function (it) {
  return IndexedObject$2(requireObjectCoercible$3(it));
};

var check = function (it) {
  return it && it.Math === Math && it;
};

// https://github.com/zloirock/core-js/issues/86#issuecomment-115759028
var global$k =
  // eslint-disable-next-line es/no-global-this -- safe
  check(typeof globalThis == 'object' && globalThis) ||
  check(typeof window == 'object' && window) ||
  // eslint-disable-next-line no-restricted-globals -- safe
  check(typeof self == 'object' && self) ||
  check(typeof commonjsGlobal == 'object' && commonjsGlobal) ||
  check(typeof commonjsGlobal == 'object' && commonjsGlobal) ||
  // eslint-disable-next-line no-new-func -- fallback
  (function () { return this; })() || Function('return this')();

var shared$3 = {exports: {}};

var global$j = global$k;

// eslint-disable-next-line es/no-object-defineproperty -- safe
var defineProperty$6 = Object.defineProperty;

var defineGlobalProperty$3 = function (key, value) {
  try {
    defineProperty$6(global$j, key, { value: value, configurable: true, writable: true });
  } catch (error) {
    global$j[key] = value;
  } return value;
};

var global$i = global$k;
var defineGlobalProperty$2 = defineGlobalProperty$3;

var SHARED = '__core-js_shared__';
var store$3 = global$i[SHARED] || defineGlobalProperty$2(SHARED, {});

var sharedStore = store$3;

var store$2 = sharedStore;

(shared$3.exports = function (key, value) {
  return store$2[key] || (store$2[key] = value !== undefined ? value : {});
})('versions', []).push({
  version: '3.35.1',
  mode: 'global',
  copyright: '© 2014-2024 Denis Pushkarev (zloirock.ru)',
  license: 'https://github.com/zloirock/core-js/blob/v3.35.1/LICENSE',
  source: 'https://github.com/zloirock/core-js'
});

var requireObjectCoercible$2 = requireObjectCoercible$4;

var $Object$3 = Object;

// `ToObject` abstract operation
// https://tc39.es/ecma262/#sec-toobject
var toObject$5 = function (argument) {
  return $Object$3(requireObjectCoercible$2(argument));
};

var uncurryThis$h = functionUncurryThis;
var toObject$4 = toObject$5;

var hasOwnProperty = uncurryThis$h({}.hasOwnProperty);

// `HasOwnProperty` abstract operation
// https://tc39.es/ecma262/#sec-hasownproperty
// eslint-disable-next-line es/no-object-hasown -- safe
var hasOwnProperty_1 = Object.hasOwn || function hasOwn(it, key) {
  return hasOwnProperty(toObject$4(it), key);
};

var uncurryThis$g = functionUncurryThis;

var id = 0;
var postfix = Math.random();
var toString$5 = uncurryThis$g(1.0.toString);

var uid$2 = function (key) {
  return 'Symbol(' + (key === undefined ? '' : key) + ')_' + toString$5(++id + postfix, 36);
};

var engineUserAgent = typeof navigator != 'undefined' && String(navigator.userAgent) || '';

var global$h = global$k;
var userAgent$5 = engineUserAgent;

var process$3 = global$h.process;
var Deno$1 = global$h.Deno;
var versions = process$3 && process$3.versions || Deno$1 && Deno$1.version;
var v8 = versions && versions.v8;
var match, version;

if (v8) {
  match = v8.split('.');
  // in old Chrome, versions of V8 isn't V8 = Chrome / 10
  // but their correct versions are not interesting for us
  version = match[0] > 0 && match[0] < 4 ? 1 : +(match[0] + match[1]);
}

// BrowserFS NodeJS `process` polyfill incorrectly set `.v8` to `0.0`
// so check `userAgent` even if `.v8` exists, but 0
if (!version && userAgent$5) {
  match = userAgent$5.match(/Edge\/(\d+)/);
  if (!match || match[1] >= 74) {
    match = userAgent$5.match(/Chrome\/(\d+)/);
    if (match) version = +match[1];
  }
}

var engineV8Version = version;

/* eslint-disable es/no-symbol -- required for testing */
var V8_VERSION$1 = engineV8Version;
var fails$f = fails$i;
var global$g = global$k;

var $String$5 = global$g.String;

// eslint-disable-next-line es/no-object-getownpropertysymbols -- required for testing
var symbolConstructorDetection = !!Object.getOwnPropertySymbols && !fails$f(function () {
  var symbol = Symbol('symbol detection');
  // Chrome 38 Symbol has incorrect toString conversion
  // `get-own-property-symbols` polyfill symbols converted to object are not Symbol instances
  // nb: Do not call `String` directly to avoid this being optimized out to `symbol+''` which will,
  // of course, fail.
  return !$String$5(symbol) || !(Object(symbol) instanceof Symbol) ||
    // Chrome 38-40 symbols are not inherited from DOM collections prototypes to instances
    !Symbol.sham && V8_VERSION$1 && V8_VERSION$1 < 41;
});

/* eslint-disable es/no-symbol -- required for testing */
var NATIVE_SYMBOL$1 = symbolConstructorDetection;

var useSymbolAsUid = NATIVE_SYMBOL$1
  && !Symbol.sham
  && typeof Symbol.iterator == 'symbol';

var global$f = global$k;
var shared$2 = shared$3.exports;
var hasOwn$a = hasOwnProperty_1;
var uid$1 = uid$2;
var NATIVE_SYMBOL = symbolConstructorDetection;
var USE_SYMBOL_AS_UID$1 = useSymbolAsUid;

var Symbol$2 = global$f.Symbol;
var WellKnownSymbolsStore = shared$2('wks');
var createWellKnownSymbol = USE_SYMBOL_AS_UID$1 ? Symbol$2['for'] || Symbol$2 : Symbol$2 && Symbol$2.withoutSetter || uid$1;

var wellKnownSymbol$g = function (name) {
  if (!hasOwn$a(WellKnownSymbolsStore, name)) {
    WellKnownSymbolsStore[name] = NATIVE_SYMBOL && hasOwn$a(Symbol$2, name)
      ? Symbol$2[name]
      : createWellKnownSymbol('Symbol.' + name);
  } return WellKnownSymbolsStore[name];
};

// https://tc39.es/ecma262/#sec-IsHTMLDDA-internal-slot
var documentAll = typeof document == 'object' && document.all;

// `IsCallable` abstract operation
// https://tc39.es/ecma262/#sec-iscallable
// eslint-disable-next-line unicorn/no-typeof-undefined -- required for testing
var isCallable$j = typeof documentAll == 'undefined' && documentAll !== undefined ? function (argument) {
  return typeof argument == 'function' || argument === documentAll;
} : function (argument) {
  return typeof argument == 'function';
};

var isCallable$i = isCallable$j;

var isObject$a = function (it) {
  return typeof it == 'object' ? it !== null : isCallable$i(it);
};

var isObject$9 = isObject$a;

var $String$4 = String;
var $TypeError$e = TypeError;

// `Assert: Type(argument) is Object`
var anObject$c = function (argument) {
  if (isObject$9(argument)) return argument;
  throw new $TypeError$e($String$4(argument) + ' is not an object');
};

var objectDefineProperties = {};

var fails$e = fails$i;

// Detect IE8's incomplete defineProperty implementation
var descriptors = !fails$e(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty({}, 1, { get: function () { return 7; } })[1] !== 7;
});

var DESCRIPTORS$b = descriptors;
var fails$d = fails$i;

// V8 ~ Chrome 36-
// https://bugs.chromium.org/p/v8/issues/detail?id=3334
var v8PrototypeDefineBug = DESCRIPTORS$b && fails$d(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty(function () { /* empty */ }, 'prototype', {
    value: 42,
    writable: false
  }).prototype !== 42;
});

var objectDefineProperty = {};

var global$e = global$k;
var isObject$8 = isObject$a;

var document$3 = global$e.document;
// typeof document.createElement is 'object' in old IE
var EXISTS$1 = isObject$8(document$3) && isObject$8(document$3.createElement);

var documentCreateElement$2 = function (it) {
  return EXISTS$1 ? document$3.createElement(it) : {};
};

var DESCRIPTORS$a = descriptors;
var fails$c = fails$i;
var createElement$1 = documentCreateElement$2;

// Thanks to IE8 for its funny defineProperty
var ie8DomDefine = !DESCRIPTORS$a && !fails$c(function () {
  // eslint-disable-next-line es/no-object-defineproperty -- required for testing
  return Object.defineProperty(createElement$1('div'), 'a', {
    get: function () { return 7; }
  }).a !== 7;
});

var NATIVE_BIND$2 = functionBindNative;

var call$d = Function.prototype.call;

var functionCall = NATIVE_BIND$2 ? call$d.bind(call$d) : function () {
  return call$d.apply(call$d, arguments);
};

var global$d = global$k;
var isCallable$h = isCallable$j;

var aFunction = function (argument) {
  return isCallable$h(argument) ? argument : undefined;
};

var getBuiltIn$7 = function (namespace, method) {
  return arguments.length < 2 ? aFunction(global$d[namespace]) : global$d[namespace] && global$d[namespace][method];
};

var uncurryThis$f = functionUncurryThis;

var objectIsPrototypeOf = uncurryThis$f({}.isPrototypeOf);

var getBuiltIn$6 = getBuiltIn$7;
var isCallable$g = isCallable$j;
var isPrototypeOf$3 = objectIsPrototypeOf;
var USE_SYMBOL_AS_UID = useSymbolAsUid;

var $Object$2 = Object;

var isSymbol$2 = USE_SYMBOL_AS_UID ? function (it) {
  return typeof it == 'symbol';
} : function (it) {
  var $Symbol = getBuiltIn$6('Symbol');
  return isCallable$g($Symbol) && isPrototypeOf$3($Symbol.prototype, $Object$2(it));
};

var $String$3 = String;

var tryToString$5 = function (argument) {
  try {
    return $String$3(argument);
  } catch (error) {
    return 'Object';
  }
};

var isCallable$f = isCallable$j;
var tryToString$4 = tryToString$5;

var $TypeError$d = TypeError;

// `Assert: IsCallable(argument) is true`
var aCallable$a = function (argument) {
  if (isCallable$f(argument)) return argument;
  throw new $TypeError$d(tryToString$4(argument) + ' is not a function');
};

var aCallable$9 = aCallable$a;
var isNullOrUndefined$2 = isNullOrUndefined$4;

// `GetMethod` abstract operation
// https://tc39.es/ecma262/#sec-getmethod
var getMethod$3 = function (V, P) {
  var func = V[P];
  return isNullOrUndefined$2(func) ? undefined : aCallable$9(func);
};

var call$c = functionCall;
var isCallable$e = isCallable$j;
var isObject$7 = isObject$a;

var $TypeError$c = TypeError;

// `OrdinaryToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-ordinarytoprimitive
var ordinaryToPrimitive$1 = function (input, pref) {
  var fn, val;
  if (pref === 'string' && isCallable$e(fn = input.toString) && !isObject$7(val = call$c(fn, input))) return val;
  if (isCallable$e(fn = input.valueOf) && !isObject$7(val = call$c(fn, input))) return val;
  if (pref !== 'string' && isCallable$e(fn = input.toString) && !isObject$7(val = call$c(fn, input))) return val;
  throw new $TypeError$c("Can't convert object to primitive value");
};

var call$b = functionCall;
var isObject$6 = isObject$a;
var isSymbol$1 = isSymbol$2;
var getMethod$2 = getMethod$3;
var ordinaryToPrimitive = ordinaryToPrimitive$1;
var wellKnownSymbol$f = wellKnownSymbol$g;

var $TypeError$b = TypeError;
var TO_PRIMITIVE = wellKnownSymbol$f('toPrimitive');

// `ToPrimitive` abstract operation
// https://tc39.es/ecma262/#sec-toprimitive
var toPrimitive$1 = function (input, pref) {
  if (!isObject$6(input) || isSymbol$1(input)) return input;
  var exoticToPrim = getMethod$2(input, TO_PRIMITIVE);
  var result;
  if (exoticToPrim) {
    if (pref === undefined) pref = 'default';
    result = call$b(exoticToPrim, input, pref);
    if (!isObject$6(result) || isSymbol$1(result)) return result;
    throw new $TypeError$b("Can't convert object to primitive value");
  }
  if (pref === undefined) pref = 'number';
  return ordinaryToPrimitive(input, pref);
};

var toPrimitive = toPrimitive$1;
var isSymbol = isSymbol$2;

// `ToPropertyKey` abstract operation
// https://tc39.es/ecma262/#sec-topropertykey
var toPropertyKey$2 = function (argument) {
  var key = toPrimitive(argument, 'string');
  return isSymbol(key) ? key : key + '';
};

var DESCRIPTORS$9 = descriptors;
var IE8_DOM_DEFINE$1 = ie8DomDefine;
var V8_PROTOTYPE_DEFINE_BUG$1 = v8PrototypeDefineBug;
var anObject$b = anObject$c;
var toPropertyKey$1 = toPropertyKey$2;

var $TypeError$a = TypeError;
// eslint-disable-next-line es/no-object-defineproperty -- safe
var $defineProperty = Object.defineProperty;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var $getOwnPropertyDescriptor$1 = Object.getOwnPropertyDescriptor;
var ENUMERABLE = 'enumerable';
var CONFIGURABLE$1 = 'configurable';
var WRITABLE = 'writable';

// `Object.defineProperty` method
// https://tc39.es/ecma262/#sec-object.defineproperty
objectDefineProperty.f = DESCRIPTORS$9 ? V8_PROTOTYPE_DEFINE_BUG$1 ? function defineProperty(O, P, Attributes) {
  anObject$b(O);
  P = toPropertyKey$1(P);
  anObject$b(Attributes);
  if (typeof O === 'function' && P === 'prototype' && 'value' in Attributes && WRITABLE in Attributes && !Attributes[WRITABLE]) {
    var current = $getOwnPropertyDescriptor$1(O, P);
    if (current && current[WRITABLE]) {
      O[P] = Attributes.value;
      Attributes = {
        configurable: CONFIGURABLE$1 in Attributes ? Attributes[CONFIGURABLE$1] : current[CONFIGURABLE$1],
        enumerable: ENUMERABLE in Attributes ? Attributes[ENUMERABLE] : current[ENUMERABLE],
        writable: false
      };
    }
  } return $defineProperty(O, P, Attributes);
} : $defineProperty : function defineProperty(O, P, Attributes) {
  anObject$b(O);
  P = toPropertyKey$1(P);
  anObject$b(Attributes);
  if (IE8_DOM_DEFINE$1) try {
    return $defineProperty(O, P, Attributes);
  } catch (error) { /* empty */ }
  if ('get' in Attributes || 'set' in Attributes) throw new $TypeError$a('Accessors not supported');
  if ('value' in Attributes) O[P] = Attributes.value;
  return O;
};

var ceil = Math.ceil;
var floor$1 = Math.floor;

// `Math.trunc` method
// https://tc39.es/ecma262/#sec-math.trunc
// eslint-disable-next-line es/no-math-trunc -- safe
var mathTrunc = Math.trunc || function trunc(x) {
  var n = +x;
  return (n > 0 ? floor$1 : ceil)(n);
};

var trunc = mathTrunc;

// `ToIntegerOrInfinity` abstract operation
// https://tc39.es/ecma262/#sec-tointegerorinfinity
var toIntegerOrInfinity$2 = function (argument) {
  var number = +argument;
  // eslint-disable-next-line no-self-compare -- NaN check
  return number !== number || number === 0 ? 0 : trunc(number);
};

var toIntegerOrInfinity$1 = toIntegerOrInfinity$2;

var max = Math.max;
var min$1 = Math.min;

// Helper for a popular repeating case of the spec:
// Let integer be ? ToInteger(index).
// If integer < 0, let result be max((length + integer), 0); else let result be min(integer, length).
var toAbsoluteIndex$1 = function (index, length) {
  var integer = toIntegerOrInfinity$1(index);
  return integer < 0 ? max(integer + length, 0) : min$1(integer, length);
};

var toIntegerOrInfinity = toIntegerOrInfinity$2;

var min = Math.min;

// `ToLength` abstract operation
// https://tc39.es/ecma262/#sec-tolength
var toLength$1 = function (argument) {
  var len = toIntegerOrInfinity(argument);
  return len > 0 ? min(len, 0x1FFFFFFFFFFFFF) : 0; // 2 ** 53 - 1 == 9007199254740991
};

var toLength = toLength$1;

// `LengthOfArrayLike` abstract operation
// https://tc39.es/ecma262/#sec-lengthofarraylike
var lengthOfArrayLike$4 = function (obj) {
  return toLength(obj.length);
};

var toIndexedObject$4 = toIndexedObject$5;
var toAbsoluteIndex = toAbsoluteIndex$1;
var lengthOfArrayLike$3 = lengthOfArrayLike$4;

// `Array.prototype.{ indexOf, includes }` methods implementation
var createMethod$2 = function (IS_INCLUDES) {
  return function ($this, el, fromIndex) {
    var O = toIndexedObject$4($this);
    var length = lengthOfArrayLike$3(O);
    var index = toAbsoluteIndex(fromIndex, length);
    var value;
    // Array#includes uses SameValueZero equality algorithm
    // eslint-disable-next-line no-self-compare -- NaN check
    if (IS_INCLUDES && el !== el) while (length > index) {
      value = O[index++];
      // eslint-disable-next-line no-self-compare -- NaN check
      if (value !== value) return true;
    // Array#indexOf ignores holes, Array#includes - not
    } else for (;length > index; index++) {
      if ((IS_INCLUDES || index in O) && O[index] === el) return IS_INCLUDES || index || 0;
    } return !IS_INCLUDES && -1;
  };
};

var arrayIncludes = {
  // `Array.prototype.includes` method
  // https://tc39.es/ecma262/#sec-array.prototype.includes
  includes: createMethod$2(true),
  // `Array.prototype.indexOf` method
  // https://tc39.es/ecma262/#sec-array.prototype.indexof
  indexOf: createMethod$2(false)
};

var hiddenKeys$4 = {};

var uncurryThis$e = functionUncurryThis;
var hasOwn$9 = hasOwnProperty_1;
var toIndexedObject$3 = toIndexedObject$5;
var indexOf = arrayIncludes.indexOf;
var hiddenKeys$3 = hiddenKeys$4;

var push$1 = uncurryThis$e([].push);

var objectKeysInternal = function (object, names) {
  var O = toIndexedObject$3(object);
  var i = 0;
  var result = [];
  var key;
  for (key in O) !hasOwn$9(hiddenKeys$3, key) && hasOwn$9(O, key) && push$1(result, key);
  // Don't enum bug & hidden keys
  while (names.length > i) if (hasOwn$9(O, key = names[i++])) {
    ~indexOf(result, key) || push$1(result, key);
  }
  return result;
};

// IE8- don't enum bug keys
var enumBugKeys$3 = [
  'constructor',
  'hasOwnProperty',
  'isPrototypeOf',
  'propertyIsEnumerable',
  'toLocaleString',
  'toString',
  'valueOf'
];

var internalObjectKeys$1 = objectKeysInternal;
var enumBugKeys$2 = enumBugKeys$3;

// `Object.keys` method
// https://tc39.es/ecma262/#sec-object.keys
// eslint-disable-next-line es/no-object-keys -- safe
var objectKeys$2 = Object.keys || function keys(O) {
  return internalObjectKeys$1(O, enumBugKeys$2);
};

var DESCRIPTORS$8 = descriptors;
var V8_PROTOTYPE_DEFINE_BUG = v8PrototypeDefineBug;
var definePropertyModule$3 = objectDefineProperty;
var anObject$a = anObject$c;
var toIndexedObject$2 = toIndexedObject$5;
var objectKeys$1 = objectKeys$2;

// `Object.defineProperties` method
// https://tc39.es/ecma262/#sec-object.defineproperties
// eslint-disable-next-line es/no-object-defineproperties -- safe
objectDefineProperties.f = DESCRIPTORS$8 && !V8_PROTOTYPE_DEFINE_BUG ? Object.defineProperties : function defineProperties(O, Properties) {
  anObject$a(O);
  var props = toIndexedObject$2(Properties);
  var keys = objectKeys$1(Properties);
  var length = keys.length;
  var index = 0;
  var key;
  while (length > index) definePropertyModule$3.f(O, key = keys[index++], props[key]);
  return O;
};

var getBuiltIn$5 = getBuiltIn$7;

var html$2 = getBuiltIn$5('document', 'documentElement');

var shared$1 = shared$3.exports;
var uid = uid$2;

var keys = shared$1('keys');

var sharedKey$3 = function (key) {
  return keys[key] || (keys[key] = uid(key));
};

/* global ActiveXObject -- old IE, WSH */
var anObject$9 = anObject$c;
var definePropertiesModule = objectDefineProperties;
var enumBugKeys$1 = enumBugKeys$3;
var hiddenKeys$2 = hiddenKeys$4;
var html$1 = html$2;
var documentCreateElement$1 = documentCreateElement$2;
var sharedKey$2 = sharedKey$3;

var GT = '>';
var LT = '<';
var PROTOTYPE = 'prototype';
var SCRIPT = 'script';
var IE_PROTO$1 = sharedKey$2('IE_PROTO');

var EmptyConstructor = function () { /* empty */ };

var scriptTag = function (content) {
  return LT + SCRIPT + GT + content + LT + '/' + SCRIPT + GT;
};

// Create object with fake `null` prototype: use ActiveX Object with cleared prototype
var NullProtoObjectViaActiveX = function (activeXDocument) {
  activeXDocument.write(scriptTag(''));
  activeXDocument.close();
  var temp = activeXDocument.parentWindow.Object;
  activeXDocument = null; // avoid memory leak
  return temp;
};

// Create object with fake `null` prototype: use iframe Object with cleared prototype
var NullProtoObjectViaIFrame = function () {
  // Thrash, waste and sodomy: IE GC bug
  var iframe = documentCreateElement$1('iframe');
  var JS = 'java' + SCRIPT + ':';
  var iframeDocument;
  iframe.style.display = 'none';
  html$1.appendChild(iframe);
  // https://github.com/zloirock/core-js/issues/475
  iframe.src = String(JS);
  iframeDocument = iframe.contentWindow.document;
  iframeDocument.open();
  iframeDocument.write(scriptTag('document.F=Object'));
  iframeDocument.close();
  return iframeDocument.F;
};

// Check for document.domain and active x support
// No need to use active x approach when document.domain is not set
// see https://github.com/es-shims/es5-shim/issues/150
// variation of https://github.com/kitcambridge/es5-shim/commit/4f738ac066346
// avoid IE GC bug
var activeXDocument;
var NullProtoObject = function () {
  try {
    activeXDocument = new ActiveXObject('htmlfile');
  } catch (error) { /* ignore */ }
  NullProtoObject = typeof document != 'undefined'
    ? document.domain && activeXDocument
      ? NullProtoObjectViaActiveX(activeXDocument) // old IE
      : NullProtoObjectViaIFrame()
    : NullProtoObjectViaActiveX(activeXDocument); // WSH
  var length = enumBugKeys$1.length;
  while (length--) delete NullProtoObject[PROTOTYPE][enumBugKeys$1[length]];
  return NullProtoObject();
};

hiddenKeys$2[IE_PROTO$1] = true;

// `Object.create` method
// https://tc39.es/ecma262/#sec-object.create
// eslint-disable-next-line es/no-object-create -- safe
var objectCreate = Object.create || function create(O, Properties) {
  var result;
  if (O !== null) {
    EmptyConstructor[PROTOTYPE] = anObject$9(O);
    result = new EmptyConstructor();
    EmptyConstructor[PROTOTYPE] = null;
    // add "__proto__" for Object.getPrototypeOf polyfill
    result[IE_PROTO$1] = O;
  } else result = NullProtoObject();
  return Properties === undefined ? result : definePropertiesModule.f(result, Properties);
};

var wellKnownSymbol$e = wellKnownSymbol$g;
var create$1 = objectCreate;
var defineProperty$5 = objectDefineProperty.f;

var UNSCOPABLES = wellKnownSymbol$e('unscopables');
var ArrayPrototype$1 = Array.prototype;

// Array.prototype[@@unscopables]
// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
if (ArrayPrototype$1[UNSCOPABLES] === undefined) {
  defineProperty$5(ArrayPrototype$1, UNSCOPABLES, {
    configurable: true,
    value: create$1(null)
  });
}

// add a key to Array.prototype[@@unscopables]
var addToUnscopables$2 = function (key) {
  ArrayPrototype$1[UNSCOPABLES][key] = true;
};

var iterators = {};

var global$c = global$k;
var isCallable$d = isCallable$j;

var WeakMap$1 = global$c.WeakMap;

var weakMapBasicDetection = isCallable$d(WeakMap$1) && /native code/.test(String(WeakMap$1));

var createPropertyDescriptor$3 = function (bitmap, value) {
  return {
    enumerable: !(bitmap & 1),
    configurable: !(bitmap & 2),
    writable: !(bitmap & 4),
    value: value
  };
};

var DESCRIPTORS$7 = descriptors;
var definePropertyModule$2 = objectDefineProperty;
var createPropertyDescriptor$2 = createPropertyDescriptor$3;

var createNonEnumerableProperty$4 = DESCRIPTORS$7 ? function (object, key, value) {
  return definePropertyModule$2.f(object, key, createPropertyDescriptor$2(1, value));
} : function (object, key, value) {
  object[key] = value;
  return object;
};

var NATIVE_WEAK_MAP = weakMapBasicDetection;
var global$b = global$k;
var isObject$5 = isObject$a;
var createNonEnumerableProperty$3 = createNonEnumerableProperty$4;
var hasOwn$8 = hasOwnProperty_1;
var shared = sharedStore;
var sharedKey$1 = sharedKey$3;
var hiddenKeys$1 = hiddenKeys$4;

var OBJECT_ALREADY_INITIALIZED = 'Object already initialized';
var TypeError$2 = global$b.TypeError;
var WeakMap = global$b.WeakMap;
var set$1, get, has;

var enforce = function (it) {
  return has(it) ? get(it) : set$1(it, {});
};

var getterFor = function (TYPE) {
  return function (it) {
    var state;
    if (!isObject$5(it) || (state = get(it)).type !== TYPE) {
      throw new TypeError$2('Incompatible receiver, ' + TYPE + ' required');
    } return state;
  };
};

if (NATIVE_WEAK_MAP || shared.state) {
  var store$1 = shared.state || (shared.state = new WeakMap());
  /* eslint-disable no-self-assign -- prototype methods protection */
  store$1.get = store$1.get;
  store$1.has = store$1.has;
  store$1.set = store$1.set;
  /* eslint-enable no-self-assign -- prototype methods protection */
  set$1 = function (it, metadata) {
    if (store$1.has(it)) throw new TypeError$2(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    store$1.set(it, metadata);
    return metadata;
  };
  get = function (it) {
    return store$1.get(it) || {};
  };
  has = function (it) {
    return store$1.has(it);
  };
} else {
  var STATE = sharedKey$1('state');
  hiddenKeys$1[STATE] = true;
  set$1 = function (it, metadata) {
    if (hasOwn$8(it, STATE)) throw new TypeError$2(OBJECT_ALREADY_INITIALIZED);
    metadata.facade = it;
    createNonEnumerableProperty$3(it, STATE, metadata);
    return metadata;
  };
  get = function (it) {
    return hasOwn$8(it, STATE) ? it[STATE] : {};
  };
  has = function (it) {
    return hasOwn$8(it, STATE);
  };
}

var internalState = {
  set: set$1,
  get: get,
  has: has,
  enforce: enforce,
  getterFor: getterFor
};

var objectGetOwnPropertyDescriptor = {};

var objectPropertyIsEnumerable = {};

var $propertyIsEnumerable = {}.propertyIsEnumerable;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getOwnPropertyDescriptor$2 = Object.getOwnPropertyDescriptor;

// Nashorn ~ JDK8 bug
var NASHORN_BUG = getOwnPropertyDescriptor$2 && !$propertyIsEnumerable.call({ 1: 2 }, 1);

// `Object.prototype.propertyIsEnumerable` method implementation
// https://tc39.es/ecma262/#sec-object.prototype.propertyisenumerable
objectPropertyIsEnumerable.f = NASHORN_BUG ? function propertyIsEnumerable(V) {
  var descriptor = getOwnPropertyDescriptor$2(this, V);
  return !!descriptor && descriptor.enumerable;
} : $propertyIsEnumerable;

var DESCRIPTORS$6 = descriptors;
var call$a = functionCall;
var propertyIsEnumerableModule$1 = objectPropertyIsEnumerable;
var createPropertyDescriptor$1 = createPropertyDescriptor$3;
var toIndexedObject$1 = toIndexedObject$5;
var toPropertyKey = toPropertyKey$2;
var hasOwn$7 = hasOwnProperty_1;
var IE8_DOM_DEFINE = ie8DomDefine;

// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var $getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// `Object.getOwnPropertyDescriptor` method
// https://tc39.es/ecma262/#sec-object.getownpropertydescriptor
objectGetOwnPropertyDescriptor.f = DESCRIPTORS$6 ? $getOwnPropertyDescriptor : function getOwnPropertyDescriptor(O, P) {
  O = toIndexedObject$1(O);
  P = toPropertyKey(P);
  if (IE8_DOM_DEFINE) try {
    return $getOwnPropertyDescriptor(O, P);
  } catch (error) { /* empty */ }
  if (hasOwn$7(O, P)) return createPropertyDescriptor$1(!call$a(propertyIsEnumerableModule$1.f, O, P), O[P]);
};

var makeBuiltIn$3 = {exports: {}};

var DESCRIPTORS$5 = descriptors;
var hasOwn$6 = hasOwnProperty_1;

var FunctionPrototype$1 = Function.prototype;
// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getDescriptor = DESCRIPTORS$5 && Object.getOwnPropertyDescriptor;

var EXISTS = hasOwn$6(FunctionPrototype$1, 'name');
// additional protection from minified / mangled / dropped function names
var PROPER = EXISTS && (function something() { /* empty */ }).name === 'something';
var CONFIGURABLE = EXISTS && (!DESCRIPTORS$5 || (DESCRIPTORS$5 && getDescriptor(FunctionPrototype$1, 'name').configurable));

var functionName = {
  EXISTS: EXISTS,
  PROPER: PROPER,
  CONFIGURABLE: CONFIGURABLE
};

var uncurryThis$d = functionUncurryThis;
var isCallable$c = isCallable$j;
var store = sharedStore;

var functionToString = uncurryThis$d(Function.toString);

// this helper broken in `core-js@3.4.1-3.4.4`, so we can't use `shared` helper
if (!isCallable$c(store.inspectSource)) {
  store.inspectSource = function (it) {
    return functionToString(it);
  };
}

var inspectSource$3 = store.inspectSource;

var uncurryThis$c = functionUncurryThis;
var fails$b = fails$i;
var isCallable$b = isCallable$j;
var hasOwn$5 = hasOwnProperty_1;
var DESCRIPTORS$4 = descriptors;
var CONFIGURABLE_FUNCTION_NAME$1 = functionName.CONFIGURABLE;
var inspectSource$2 = inspectSource$3;
var InternalStateModule$2 = internalState;

var enforceInternalState = InternalStateModule$2.enforce;
var getInternalState$1 = InternalStateModule$2.get;
var $String$2 = String;
// eslint-disable-next-line es/no-object-defineproperty -- safe
var defineProperty$4 = Object.defineProperty;
var stringSlice = uncurryThis$c(''.slice);
var replace$1 = uncurryThis$c(''.replace);
var join = uncurryThis$c([].join);

var CONFIGURABLE_LENGTH = DESCRIPTORS$4 && !fails$b(function () {
  return defineProperty$4(function () { /* empty */ }, 'length', { value: 8 }).length !== 8;
});

var TEMPLATE = String(String).split('String');

var makeBuiltIn$2 = makeBuiltIn$3.exports = function (value, name, options) {
  if (stringSlice($String$2(name), 0, 7) === 'Symbol(') {
    name = '[' + replace$1($String$2(name), /^Symbol\(([^)]*)\).*$/, '$1') + ']';
  }
  if (options && options.getter) name = 'get ' + name;
  if (options && options.setter) name = 'set ' + name;
  if (!hasOwn$5(value, 'name') || (CONFIGURABLE_FUNCTION_NAME$1 && value.name !== name)) {
    if (DESCRIPTORS$4) defineProperty$4(value, 'name', { value: name, configurable: true });
    else value.name = name;
  }
  if (CONFIGURABLE_LENGTH && options && hasOwn$5(options, 'arity') && value.length !== options.arity) {
    defineProperty$4(value, 'length', { value: options.arity });
  }
  try {
    if (options && hasOwn$5(options, 'constructor') && options.constructor) {
      if (DESCRIPTORS$4) defineProperty$4(value, 'prototype', { writable: false });
    // in V8 ~ Chrome 53, prototypes of some methods, like `Array.prototype.values`, are non-writable
    } else if (value.prototype) value.prototype = undefined;
  } catch (error) { /* empty */ }
  var state = enforceInternalState(value);
  if (!hasOwn$5(state, 'source')) {
    state.source = join(TEMPLATE, typeof name == 'string' ? name : '');
  } return value;
};

// add fake Function#toString for correct work wrapped methods / constructors with methods like LoDash isNative
// eslint-disable-next-line no-extend-native -- required
Function.prototype.toString = makeBuiltIn$2(function toString() {
  return isCallable$b(this) && getInternalState$1(this).source || inspectSource$2(this);
}, 'toString');

var isCallable$a = isCallable$j;
var definePropertyModule$1 = objectDefineProperty;
var makeBuiltIn$1 = makeBuiltIn$3.exports;
var defineGlobalProperty$1 = defineGlobalProperty$3;

var defineBuiltIn$6 = function (O, key, value, options) {
  if (!options) options = {};
  var simple = options.enumerable;
  var name = options.name !== undefined ? options.name : key;
  if (isCallable$a(value)) makeBuiltIn$1(value, name, options);
  if (options.global) {
    if (simple) O[key] = value;
    else defineGlobalProperty$1(key, value);
  } else {
    try {
      if (!options.unsafe) delete O[key];
      else if (O[key]) simple = true;
    } catch (error) { /* empty */ }
    if (simple) O[key] = value;
    else definePropertyModule$1.f(O, key, {
      value: value,
      enumerable: false,
      configurable: !options.nonConfigurable,
      writable: !options.nonWritable
    });
  } return O;
};

var objectGetOwnPropertyNames = {};

var internalObjectKeys = objectKeysInternal;
var enumBugKeys = enumBugKeys$3;

var hiddenKeys = enumBugKeys.concat('length', 'prototype');

// `Object.getOwnPropertyNames` method
// https://tc39.es/ecma262/#sec-object.getownpropertynames
// eslint-disable-next-line es/no-object-getownpropertynames -- safe
objectGetOwnPropertyNames.f = Object.getOwnPropertyNames || function getOwnPropertyNames(O) {
  return internalObjectKeys(O, hiddenKeys);
};

var objectGetOwnPropertySymbols = {};

// eslint-disable-next-line es/no-object-getownpropertysymbols -- safe
objectGetOwnPropertySymbols.f = Object.getOwnPropertySymbols;

var getBuiltIn$4 = getBuiltIn$7;
var uncurryThis$b = functionUncurryThis;
var getOwnPropertyNamesModule = objectGetOwnPropertyNames;
var getOwnPropertySymbolsModule$1 = objectGetOwnPropertySymbols;
var anObject$8 = anObject$c;

var concat$1 = uncurryThis$b([].concat);

// all object keys, includes non-enumerable and symbols
var ownKeys$1 = getBuiltIn$4('Reflect', 'ownKeys') || function ownKeys(it) {
  var keys = getOwnPropertyNamesModule.f(anObject$8(it));
  var getOwnPropertySymbols = getOwnPropertySymbolsModule$1.f;
  return getOwnPropertySymbols ? concat$1(keys, getOwnPropertySymbols(it)) : keys;
};

var hasOwn$4 = hasOwnProperty_1;
var ownKeys = ownKeys$1;
var getOwnPropertyDescriptorModule = objectGetOwnPropertyDescriptor;
var definePropertyModule = objectDefineProperty;

var copyConstructorProperties$1 = function (target, source, exceptions) {
  var keys = ownKeys(source);
  var defineProperty = definePropertyModule.f;
  var getOwnPropertyDescriptor = getOwnPropertyDescriptorModule.f;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (!hasOwn$4(target, key) && !(exceptions && hasOwn$4(exceptions, key))) {
      defineProperty(target, key, getOwnPropertyDescriptor(source, key));
    }
  }
};

var fails$a = fails$i;
var isCallable$9 = isCallable$j;

var replacement = /#|\.prototype\./;

var isForced$2 = function (feature, detection) {
  var value = data[normalize(feature)];
  return value === POLYFILL ? true
    : value === NATIVE ? false
    : isCallable$9(detection) ? fails$a(detection)
    : !!detection;
};

var normalize = isForced$2.normalize = function (string) {
  return String(string).replace(replacement, '.').toLowerCase();
};

var data = isForced$2.data = {};
var NATIVE = isForced$2.NATIVE = 'N';
var POLYFILL = isForced$2.POLYFILL = 'P';

var isForced_1 = isForced$2;

var global$a = global$k;
var getOwnPropertyDescriptor$1 = objectGetOwnPropertyDescriptor.f;
var createNonEnumerableProperty$2 = createNonEnumerableProperty$4;
var defineBuiltIn$5 = defineBuiltIn$6;
var defineGlobalProperty = defineGlobalProperty$3;
var copyConstructorProperties = copyConstructorProperties$1;
var isForced$1 = isForced_1;

/*
  options.target         - name of the target object
  options.global         - target is the global object
  options.stat           - export as static methods of target
  options.proto          - export as prototype methods of target
  options.real           - real prototype method for the `pure` version
  options.forced         - export even if the native feature is available
  options.bind           - bind methods to the target, required for the `pure` version
  options.wrap           - wrap constructors to preventing global pollution, required for the `pure` version
  options.unsafe         - use the simple assignment of property instead of delete + defineProperty
  options.sham           - add a flag to not completely full polyfills
  options.enumerable     - export as enumerable property
  options.dontCallGetSet - prevent calling a getter on target
  options.name           - the .name of the function if it does not match the key
*/
var _export = function (options, source) {
  var TARGET = options.target;
  var GLOBAL = options.global;
  var STATIC = options.stat;
  var FORCED, target, key, targetProperty, sourceProperty, descriptor;
  if (GLOBAL) {
    target = global$a;
  } else if (STATIC) {
    target = global$a[TARGET] || defineGlobalProperty(TARGET, {});
  } else {
    target = global$a[TARGET] && global$a[TARGET].prototype;
  }
  if (target) for (key in source) {
    sourceProperty = source[key];
    if (options.dontCallGetSet) {
      descriptor = getOwnPropertyDescriptor$1(target, key);
      targetProperty = descriptor && descriptor.value;
    } else targetProperty = target[key];
    FORCED = isForced$1(GLOBAL ? key : TARGET + (STATIC ? '.' : '#') + key, options.forced);
    // contained in target
    if (!FORCED && targetProperty !== undefined) {
      if (typeof sourceProperty == typeof targetProperty) continue;
      copyConstructorProperties(sourceProperty, targetProperty);
    }
    // add a flag to not completely full polyfills
    if (options.sham || (targetProperty && targetProperty.sham)) {
      createNonEnumerableProperty$2(sourceProperty, 'sham', true);
    }
    defineBuiltIn$5(target, key, sourceProperty, options);
  }
};

var fails$9 = fails$i;

var correctPrototypeGetter = !fails$9(function () {
  function F() { /* empty */ }
  F.prototype.constructor = null;
  // eslint-disable-next-line es/no-object-getprototypeof -- required for testing
  return Object.getPrototypeOf(new F()) !== F.prototype;
});

var hasOwn$3 = hasOwnProperty_1;
var isCallable$8 = isCallable$j;
var toObject$3 = toObject$5;
var sharedKey = sharedKey$3;
var CORRECT_PROTOTYPE_GETTER = correctPrototypeGetter;

var IE_PROTO = sharedKey('IE_PROTO');
var $Object$1 = Object;
var ObjectPrototype = $Object$1.prototype;

// `Object.getPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.getprototypeof
// eslint-disable-next-line es/no-object-getprototypeof -- safe
var objectGetPrototypeOf = CORRECT_PROTOTYPE_GETTER ? $Object$1.getPrototypeOf : function (O) {
  var object = toObject$3(O);
  if (hasOwn$3(object, IE_PROTO)) return object[IE_PROTO];
  var constructor = object.constructor;
  if (isCallable$8(constructor) && object instanceof constructor) {
    return constructor.prototype;
  } return object instanceof $Object$1 ? ObjectPrototype : null;
};

var fails$8 = fails$i;
var isCallable$7 = isCallable$j;
var isObject$4 = isObject$a;
var getPrototypeOf$1 = objectGetPrototypeOf;
var defineBuiltIn$4 = defineBuiltIn$6;
var wellKnownSymbol$d = wellKnownSymbol$g;

var ITERATOR$6 = wellKnownSymbol$d('iterator');
var BUGGY_SAFARI_ITERATORS$1 = false;

// `%IteratorPrototype%` object
// https://tc39.es/ecma262/#sec-%iteratorprototype%-object
var IteratorPrototype$2, PrototypeOfArrayIteratorPrototype, arrayIterator;

/* eslint-disable es/no-array-prototype-keys -- safe */
if ([].keys) {
  arrayIterator = [].keys();
  // Safari 8 has buggy iterators w/o `next`
  if (!('next' in arrayIterator)) BUGGY_SAFARI_ITERATORS$1 = true;
  else {
    PrototypeOfArrayIteratorPrototype = getPrototypeOf$1(getPrototypeOf$1(arrayIterator));
    if (PrototypeOfArrayIteratorPrototype !== Object.prototype) IteratorPrototype$2 = PrototypeOfArrayIteratorPrototype;
  }
}

var NEW_ITERATOR_PROTOTYPE = !isObject$4(IteratorPrototype$2) || fails$8(function () {
  var test = {};
  // FF44- legacy iterators case
  return IteratorPrototype$2[ITERATOR$6].call(test) !== test;
});

if (NEW_ITERATOR_PROTOTYPE) IteratorPrototype$2 = {};

// `%IteratorPrototype%[@@iterator]()` method
// https://tc39.es/ecma262/#sec-%iteratorprototype%-@@iterator
if (!isCallable$7(IteratorPrototype$2[ITERATOR$6])) {
  defineBuiltIn$4(IteratorPrototype$2, ITERATOR$6, function () {
    return this;
  });
}

var iteratorsCore = {
  IteratorPrototype: IteratorPrototype$2,
  BUGGY_SAFARI_ITERATORS: BUGGY_SAFARI_ITERATORS$1
};

var defineProperty$3 = objectDefineProperty.f;
var hasOwn$2 = hasOwnProperty_1;
var wellKnownSymbol$c = wellKnownSymbol$g;

var TO_STRING_TAG$2 = wellKnownSymbol$c('toStringTag');

var setToStringTag$4 = function (target, TAG, STATIC) {
  if (target && !STATIC) target = target.prototype;
  if (target && !hasOwn$2(target, TO_STRING_TAG$2)) {
    defineProperty$3(target, TO_STRING_TAG$2, { configurable: true, value: TAG });
  }
};

var IteratorPrototype$1 = iteratorsCore.IteratorPrototype;
var create = objectCreate;
var createPropertyDescriptor = createPropertyDescriptor$3;
var setToStringTag$3 = setToStringTag$4;
var Iterators$4 = iterators;

var returnThis$1 = function () { return this; };

var iteratorCreateConstructor = function (IteratorConstructor, NAME, next, ENUMERABLE_NEXT) {
  var TO_STRING_TAG = NAME + ' Iterator';
  IteratorConstructor.prototype = create(IteratorPrototype$1, { next: createPropertyDescriptor(+!ENUMERABLE_NEXT, next) });
  setToStringTag$3(IteratorConstructor, TO_STRING_TAG, false);
  Iterators$4[TO_STRING_TAG] = returnThis$1;
  return IteratorConstructor;
};

var uncurryThis$a = functionUncurryThis;
var aCallable$8 = aCallable$a;

var functionUncurryThisAccessor = function (object, key, method) {
  try {
    // eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
    return uncurryThis$a(aCallable$8(Object.getOwnPropertyDescriptor(object, key)[method]));
  } catch (error) { /* empty */ }
};

var isObject$3 = isObject$a;

var isPossiblePrototype$1 = function (argument) {
  return isObject$3(argument) || argument === null;
};

var isPossiblePrototype = isPossiblePrototype$1;

var $String$1 = String;
var $TypeError$9 = TypeError;

var aPossiblePrototype$1 = function (argument) {
  if (isPossiblePrototype(argument)) return argument;
  throw new $TypeError$9("Can't set " + $String$1(argument) + ' as a prototype');
};

/* eslint-disable no-proto -- safe */
var uncurryThisAccessor = functionUncurryThisAccessor;
var anObject$7 = anObject$c;
var aPossiblePrototype = aPossiblePrototype$1;

// `Object.setPrototypeOf` method
// https://tc39.es/ecma262/#sec-object.setprototypeof
// Works with __proto__ only. Old v8 can't work with null proto objects.
// eslint-disable-next-line es/no-object-setprototypeof -- safe
var objectSetPrototypeOf = Object.setPrototypeOf || ('__proto__' in {} ? function () {
  var CORRECT_SETTER = false;
  var test = {};
  var setter;
  try {
    setter = uncurryThisAccessor(Object.prototype, '__proto__', 'set');
    setter(test, []);
    CORRECT_SETTER = test instanceof Array;
  } catch (error) { /* empty */ }
  return function setPrototypeOf(O, proto) {
    anObject$7(O);
    aPossiblePrototype(proto);
    if (CORRECT_SETTER) setter(O, proto);
    else O.__proto__ = proto;
    return O;
  };
}() : undefined);

var $$d = _export;
var call$9 = functionCall;
var FunctionName = functionName;
var isCallable$6 = isCallable$j;
var createIteratorConstructor = iteratorCreateConstructor;
var getPrototypeOf = objectGetPrototypeOf;
var setPrototypeOf$1 = objectSetPrototypeOf;
var setToStringTag$2 = setToStringTag$4;
var createNonEnumerableProperty$1 = createNonEnumerableProperty$4;
var defineBuiltIn$3 = defineBuiltIn$6;
var wellKnownSymbol$b = wellKnownSymbol$g;
var Iterators$3 = iterators;
var IteratorsCore = iteratorsCore;

var PROPER_FUNCTION_NAME$1 = FunctionName.PROPER;
var CONFIGURABLE_FUNCTION_NAME = FunctionName.CONFIGURABLE;
var IteratorPrototype = IteratorsCore.IteratorPrototype;
var BUGGY_SAFARI_ITERATORS = IteratorsCore.BUGGY_SAFARI_ITERATORS;
var ITERATOR$5 = wellKnownSymbol$b('iterator');
var KEYS = 'keys';
var VALUES = 'values';
var ENTRIES = 'entries';

var returnThis = function () { return this; };

var iteratorDefine = function (Iterable, NAME, IteratorConstructor, next, DEFAULT, IS_SET, FORCED) {
  createIteratorConstructor(IteratorConstructor, NAME, next);

  var getIterationMethod = function (KIND) {
    if (KIND === DEFAULT && defaultIterator) return defaultIterator;
    if (!BUGGY_SAFARI_ITERATORS && KIND && KIND in IterablePrototype) return IterablePrototype[KIND];

    switch (KIND) {
      case KEYS: return function keys() { return new IteratorConstructor(this, KIND); };
      case VALUES: return function values() { return new IteratorConstructor(this, KIND); };
      case ENTRIES: return function entries() { return new IteratorConstructor(this, KIND); };
    }

    return function () { return new IteratorConstructor(this); };
  };

  var TO_STRING_TAG = NAME + ' Iterator';
  var INCORRECT_VALUES_NAME = false;
  var IterablePrototype = Iterable.prototype;
  var nativeIterator = IterablePrototype[ITERATOR$5]
    || IterablePrototype['@@iterator']
    || DEFAULT && IterablePrototype[DEFAULT];
  var defaultIterator = !BUGGY_SAFARI_ITERATORS && nativeIterator || getIterationMethod(DEFAULT);
  var anyNativeIterator = NAME === 'Array' ? IterablePrototype.entries || nativeIterator : nativeIterator;
  var CurrentIteratorPrototype, methods, KEY;

  // fix native
  if (anyNativeIterator) {
    CurrentIteratorPrototype = getPrototypeOf(anyNativeIterator.call(new Iterable()));
    if (CurrentIteratorPrototype !== Object.prototype && CurrentIteratorPrototype.next) {
      if (getPrototypeOf(CurrentIteratorPrototype) !== IteratorPrototype) {
        if (setPrototypeOf$1) {
          setPrototypeOf$1(CurrentIteratorPrototype, IteratorPrototype);
        } else if (!isCallable$6(CurrentIteratorPrototype[ITERATOR$5])) {
          defineBuiltIn$3(CurrentIteratorPrototype, ITERATOR$5, returnThis);
        }
      }
      // Set @@toStringTag to native iterators
      setToStringTag$2(CurrentIteratorPrototype, TO_STRING_TAG, true);
    }
  }

  // fix Array.prototype.{ values, @@iterator }.name in V8 / FF
  if (PROPER_FUNCTION_NAME$1 && DEFAULT === VALUES && nativeIterator && nativeIterator.name !== VALUES) {
    if (CONFIGURABLE_FUNCTION_NAME) {
      createNonEnumerableProperty$1(IterablePrototype, 'name', VALUES);
    } else {
      INCORRECT_VALUES_NAME = true;
      defaultIterator = function values() { return call$9(nativeIterator, this); };
    }
  }

  // export additional methods
  if (DEFAULT) {
    methods = {
      values: getIterationMethod(VALUES),
      keys: IS_SET ? defaultIterator : getIterationMethod(KEYS),
      entries: getIterationMethod(ENTRIES)
    };
    if (FORCED) for (KEY in methods) {
      if (BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME || !(KEY in IterablePrototype)) {
        defineBuiltIn$3(IterablePrototype, KEY, methods[KEY]);
      }
    } else $$d({ target: NAME, proto: true, forced: BUGGY_SAFARI_ITERATORS || INCORRECT_VALUES_NAME }, methods);
  }

  // define iterator
  if (IterablePrototype[ITERATOR$5] !== defaultIterator) {
    defineBuiltIn$3(IterablePrototype, ITERATOR$5, defaultIterator, { name: DEFAULT });
  }
  Iterators$3[NAME] = defaultIterator;

  return methods;
};

// `CreateIterResultObject` abstract operation
// https://tc39.es/ecma262/#sec-createiterresultobject
var createIterResultObject$1 = function (value, done) {
  return { value: value, done: done };
};

var toIndexedObject = toIndexedObject$5;
var addToUnscopables$1 = addToUnscopables$2;
var Iterators$2 = iterators;
var InternalStateModule$1 = internalState;
var defineProperty$2 = objectDefineProperty.f;
var defineIterator = iteratorDefine;
var createIterResultObject = createIterResultObject$1;
var DESCRIPTORS$3 = descriptors;

var ARRAY_ITERATOR = 'Array Iterator';
var setInternalState$1 = InternalStateModule$1.set;
var getInternalState = InternalStateModule$1.getterFor(ARRAY_ITERATOR);

// `Array.prototype.entries` method
// https://tc39.es/ecma262/#sec-array.prototype.entries
// `Array.prototype.keys` method
// https://tc39.es/ecma262/#sec-array.prototype.keys
// `Array.prototype.values` method
// https://tc39.es/ecma262/#sec-array.prototype.values
// `Array.prototype[@@iterator]` method
// https://tc39.es/ecma262/#sec-array.prototype-@@iterator
// `CreateArrayIterator` internal method
// https://tc39.es/ecma262/#sec-createarrayiterator
var es_array_iterator = defineIterator(Array, 'Array', function (iterated, kind) {
  setInternalState$1(this, {
    type: ARRAY_ITERATOR,
    target: toIndexedObject(iterated), // target
    index: 0,                          // next index
    kind: kind                         // kind
  });
// `%ArrayIteratorPrototype%.next` method
// https://tc39.es/ecma262/#sec-%arrayiteratorprototype%.next
}, function () {
  var state = getInternalState(this);
  var target = state.target;
  var index = state.index++;
  if (!target || index >= target.length) {
    state.target = undefined;
    return createIterResultObject(undefined, true);
  }
  switch (state.kind) {
    case 'keys': return createIterResultObject(index, false);
    case 'values': return createIterResultObject(target[index], false);
  } return createIterResultObject([index, target[index]], false);
}, 'values');

// argumentsList[@@iterator] is %ArrayProto_values%
// https://tc39.es/ecma262/#sec-createunmappedargumentsobject
// https://tc39.es/ecma262/#sec-createmappedargumentsobject
var values = Iterators$2.Arguments = Iterators$2.Array;

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables$1('keys');
addToUnscopables$1('values');
addToUnscopables$1('entries');

// V8 ~ Chrome 45- bug
if (DESCRIPTORS$3 && values.name !== 'values') try {
  defineProperty$2(values, 'name', { value: 'values' });
} catch (error) { /* empty */ }

// iterable DOM collections
// flag - `iterable` interface - 'entries', 'keys', 'values', 'forEach' methods
var domIterables = {
  CSSRuleList: 0,
  CSSStyleDeclaration: 0,
  CSSValueList: 0,
  ClientRectList: 0,
  DOMRectList: 0,
  DOMStringList: 0,
  DOMTokenList: 1,
  DataTransferItemList: 0,
  FileList: 0,
  HTMLAllCollection: 0,
  HTMLCollection: 0,
  HTMLFormElement: 0,
  HTMLSelectElement: 0,
  MediaList: 0,
  MimeTypeArray: 0,
  NamedNodeMap: 0,
  NodeList: 1,
  PaintRequestList: 0,
  Plugin: 0,
  PluginArray: 0,
  SVGLengthList: 0,
  SVGNumberList: 0,
  SVGPathSegList: 0,
  SVGPointList: 0,
  SVGStringList: 0,
  SVGTransformList: 0,
  SourceBufferList: 0,
  StyleSheetList: 0,
  TextTrackCueList: 0,
  TextTrackList: 0,
  TouchList: 0
};

// in old WebKit versions, `element.classList` is not an instance of global `DOMTokenList`
var documentCreateElement = documentCreateElement$2;

var classList = documentCreateElement('span').classList;
var DOMTokenListPrototype$1 = classList && classList.constructor && classList.constructor.prototype;

var domTokenListPrototype = DOMTokenListPrototype$1 === Object.prototype ? undefined : DOMTokenListPrototype$1;

var global$9 = global$k;
var DOMIterables = domIterables;
var DOMTokenListPrototype = domTokenListPrototype;
var ArrayIteratorMethods = es_array_iterator;
var createNonEnumerableProperty = createNonEnumerableProperty$4;
var setToStringTag$1 = setToStringTag$4;
var wellKnownSymbol$a = wellKnownSymbol$g;

var ITERATOR$4 = wellKnownSymbol$a('iterator');
var ArrayValues = ArrayIteratorMethods.values;

var handlePrototype = function (CollectionPrototype, COLLECTION_NAME) {
  if (CollectionPrototype) {
    // some Chrome versions have non-configurable methods on DOMTokenList
    if (CollectionPrototype[ITERATOR$4] !== ArrayValues) try {
      createNonEnumerableProperty(CollectionPrototype, ITERATOR$4, ArrayValues);
    } catch (error) {
      CollectionPrototype[ITERATOR$4] = ArrayValues;
    }
    setToStringTag$1(CollectionPrototype, COLLECTION_NAME, true);
    if (DOMIterables[COLLECTION_NAME]) for (var METHOD_NAME in ArrayIteratorMethods) {
      // some Chrome versions have non-configurable methods on DOMTokenList
      if (CollectionPrototype[METHOD_NAME] !== ArrayIteratorMethods[METHOD_NAME]) try {
        createNonEnumerableProperty(CollectionPrototype, METHOD_NAME, ArrayIteratorMethods[METHOD_NAME]);
      } catch (error) {
        CollectionPrototype[METHOD_NAME] = ArrayIteratorMethods[METHOD_NAME];
      }
    }
  }
};

for (var COLLECTION_NAME in DOMIterables) {
  handlePrototype(global$9[COLLECTION_NAME] && global$9[COLLECTION_NAME].prototype, COLLECTION_NAME);
}

handlePrototype(DOMTokenListPrototype, 'DOMTokenList');

var DESCRIPTORS$2 = descriptors;
var uncurryThis$9 = functionUncurryThis;
var call$8 = functionCall;
var fails$7 = fails$i;
var objectKeys = objectKeys$2;
var getOwnPropertySymbolsModule = objectGetOwnPropertySymbols;
var propertyIsEnumerableModule = objectPropertyIsEnumerable;
var toObject$2 = toObject$5;
var IndexedObject$1 = indexedObject;

// eslint-disable-next-line es/no-object-assign -- safe
var $assign = Object.assign;
// eslint-disable-next-line es/no-object-defineproperty -- required for testing
var defineProperty$1 = Object.defineProperty;
var concat = uncurryThis$9([].concat);

// `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
var objectAssign = !$assign || fails$7(function () {
  // should have correct order of operations (Edge bug)
  if (DESCRIPTORS$2 && $assign({ b: 1 }, $assign(defineProperty$1({}, 'a', {
    enumerable: true,
    get: function () {
      defineProperty$1(this, 'b', {
        value: 3,
        enumerable: false
      });
    }
  }), { b: 2 })).b !== 1) return true;
  // should work with symbols and should have deterministic property order (V8 bug)
  var A = {};
  var B = {};
  // eslint-disable-next-line es/no-symbol -- safe
  var symbol = Symbol('assign detection');
  var alphabet = 'abcdefghijklmnopqrst';
  A[symbol] = 7;
  alphabet.split('').forEach(function (chr) { B[chr] = chr; });
  return $assign({}, A)[symbol] !== 7 || objectKeys($assign({}, B)).join('') !== alphabet;
}) ? function assign(target, source) { // eslint-disable-line no-unused-vars -- required for `.length`
  var T = toObject$2(target);
  var argumentsLength = arguments.length;
  var index = 1;
  var getOwnPropertySymbols = getOwnPropertySymbolsModule.f;
  var propertyIsEnumerable = propertyIsEnumerableModule.f;
  while (argumentsLength > index) {
    var S = IndexedObject$1(arguments[index++]);
    var keys = getOwnPropertySymbols ? concat(objectKeys(S), getOwnPropertySymbols(S)) : objectKeys(S);
    var length = keys.length;
    var j = 0;
    var key;
    while (length > j) {
      key = keys[j++];
      if (!DESCRIPTORS$2 || call$8(propertyIsEnumerable, S, key)) T[key] = S[key];
    }
  } return T;
} : $assign;

var $$c = _export;
var assign = objectAssign;

// `Object.assign` method
// https://tc39.es/ecma262/#sec-object.assign
// eslint-disable-next-line es/no-object-assign -- required for testing
$$c({ target: 'Object', stat: true, arity: 2, forced: Object.assign !== assign }, {
  assign: assign
});

const LegendLayout = ({
  title,
  name,
  dimensions,
  children,
  height,
  width,
  minWidth
}) => {
  return /*#__PURE__*/React__default.createElement(Paper, {
    "data-testid": "legend",
    sx: {
      padding: '10px',
      border: '1px solid grey',
      backgroundColor: '#FFF',
      overflow: 'hidden',
      marginBottom: 0.5
    }
  }, /*#__PURE__*/React__default.createElement(Box, {
    "data-testid": "legend-title"
  }, /*#__PURE__*/React__default.createElement(Typography, {
    sx: {
      fontFamily: 'Roboto, Helvetica, Arial',
      fontSize: '10px',
      minWidth: '100%',
      wordWrap: 'break-word',
      color: 'common.black',
      whiteSpace: 'nowrap'
    },
    variant: "h3"
  }, title)), /*#__PURE__*/React__default.createElement(CustomTooltip, {
    title: title || 'loading',
    placement: "top"
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      margin: '10px 0px 10px 0px',
      height: `${height}px`,
      width: `${width}px`
    }
  }, children)), /*#__PURE__*/React__default.createElement(Box, {
    "data-testid": "legend-info",
    sx: Object.assign({
      fontFamily: 'Roboto, Helvetica, Arial',
      fontSize: 10,
      color: 'common.black',
      overflowX: 'hidden',
      wordWrap: 'break-word',
      whiteSpace: 'nowrap'
    }, minWidth && {
      minWidth: `${minWidth}px`
    })
  }, dimensions && dimensions.map(dim => ( /*#__PURE__*/React__default.createElement(Box, {
    key: name + dim.name
  }, dim.name === 'time' ? `${dim.currentValue}` : ( /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Box, {
    style: {
      fontWeight: 500
    }
  }, dim.name, ":"), dim.currentValue, " ", dim.units)))))));
};

const defaultCanvasWidth = 150;
const defaultCanvasHeight = 30;
/**
 * Renders a legendgraphic image based on a WMImage.
 *
 * @param wmImage The wmImage element to render (from WMImage.js)
 * @param ctx The canvas to render the image on
 * @param width The width you want the image to have
 * @param height The height you want the image to have
 */
const renderLegendGraphic = (wmImage, ctx, width, height) => {
  ctx.beginPath();
  ctx.fillStyle = 'white';
  ctx.rect(0, 0, width, height);
  ctx.fill();
  if (wmImage) {
    if (wmImage.isLoading()) {
      /* Legend image is loading */
      ctx.fillStyle = 'blue';
      ctx.fillText('... loading ...', 10, 10);
    } else if (wmImage.hasError()) {
      /* Legend image has an error */
      ctx.fillStyle = 'red';
      ctx.fillText('error :(', 10, 10);
    } else {
      /* All OK, just draw the legend image */
      ctx.drawImage(wmImage.getElement(), 0, 0, Math.min(wmImage.getElement().width, width), Math.min(wmImage.getElement().height, height));
    }
  }
};
const Legend = ({
  layer
}) => {
  /* Variable to remember the previous image */
  const previousLegendImage = useRef(null);
  /* Function to force rerender of this component when legend image loads */
  const [, setState] = useState();
  const layerId = layer && layer.id;
  /* The canvas listens to the size of its container. We will update it according to the image size. */
  const [canvasContainerWidth, setCanvasContainerWidth] = useState(defaultCanvasWidth);
  const [canvasContainerHeight, setCanvasContainerHeight] = useState(defaultCanvasHeight);
  let imageWidth;
  let imageHeight;
  /* Register callback to legendImagestore to trigger rerender when image is loaded. */
  useEffect(() => {
    legendImageStore.addImageEventCallback((image, id, imageEventType) => {
      if (imageEventType === WMImageEventType.Loaded) {
        setState(null);
      }
    }, `Legend_${layerId}`);
    return () => {
      legendImageStore.removeEventCallback(`Legend_${layerId}`);
    };
  }, [layerId]);
  /* Should not do anything if no layer is given */
  if (!layer) {
    return null;
  }
  const isLayerEnabled = layer.enabled;
  if (!isLayerEnabled) {
    return null;
  }
  const layerOpacity = layer.opacity ? layer.opacity : 1;
  /* Get the wmLayer, it has more detailed info about the WMS service,
  like a title and the WMS legendgraphic url */
  const wmLayer = webmapUtils.getWMLayerById(layer.id);
  return /*#__PURE__*/React__default.createElement(LegendLayout, {
    title: wmLayer === null || wmLayer === void 0 ? void 0 : wmLayer.title,
    name: wmLayer === null || wmLayer === void 0 ? void 0 : wmLayer.name,
    dimensions: layer === null || layer === void 0 ? void 0 : layer.dimensions,
    height: canvasContainerHeight,
    width: canvasContainerWidth,
    minWidth: defaultCanvasWidth
  }, /*#__PURE__*/React__default.createElement(CanvasComponent, {
    redrawInterval: 500,
    onRenderCanvas: (ctx, width, height) => {
      /*
            Check if the wmLayer is available, this happens when the WMS GetCapabilities is loaded and parsed.
            Note that we cannot use the wmLayer variable above, as this one could contain a non initialized WMJSLayer.
           */
      const legendWmLayer = webmapUtils.getWMLayerById(layer.id);
      const legendUrl = getLegendGraphicURLForLayer(legendWmLayer);
      const image = legendImageStore.getImage(legendUrl);
      /* If there is no image for the given URL, we do not want to display a legend image (e.g. outside range) */
      if (!image) {
        previousLegendImage.current = null;
        setCanvasContainerWidth(defaultCanvasWidth);
        setCanvasContainerHeight(defaultCanvasHeight);
        ctx.beginPath();
        ctx.fillStyle = '#fff';
        ctx.rect(0, 0, width, height);
        ctx.fill();
        ctx.fillStyle = 'black';
        if (!legendUrl) {
          ctx.fillText('No legend graphic available', 5, 16);
        }
      } else {
        /* If there is an image for the given url, and has no error, but it is not yet loading or loaded, start loading it! */
        if (image && image.hasError() === false && image.isLoaded() === false && image.isLoading() === false) {
          image.load(); /* Will eventually trigger the callback used in the useEffect */
        }
        /* If the image is ready, we can show it in the component, store it in our ref, otherwise we keep the previous image to prevent flickering */
        if (image && image.isLoaded()) {
          previousLegendImage.current = image;
          /* Set the canvas container to the same size as the image */
          imageHeight = image.getHeight();
          setCanvasContainerHeight(imageHeight);
          imageWidth = image.getWidth();
          setCanvasContainerWidth(imageWidth);
          /* Give the canvas a white background before adding the image */
          ctx.fillStyle = '#fff';
          ctx.rect(0, 0, width, height);
          ctx.fill();
          /* Set the opacity used in the layer */
          ctx.globalAlpha = layerOpacity;
        }
        renderLegendGraphic(previousLegendImage.current, ctx, imageWidth, imageHeight);
      }
    }
  }));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
const LegendDialog = ({
  layers,
  isOpen,
  onClose,
  onMouseDown: _onMouseDown = () => {},
  mapId,
  showMapId: _showMapId = false,
  order: _order = 0,
  source: _source = 'app'
}) => {
  return /*#__PURE__*/React.createElement(ToolContainerDraggable, {
    startPosition: {
      right: 20,
      top: 50
    },
    minWidth: 100,
    onClose: onClose,
    title: _showMapId ? `Legend ${mapId}` : 'Legend',
    initialMaxHeight: 440,
    isOpen: isOpen,
    "data-testid": "moveable-legend",
    bounds: "parent",
    onMouseDown: _onMouseDown,
    order: _order,
    source: _source
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      padding: 0.5
    }
  }, layers && layers.length > 0 ? ( /*#__PURE__*/React.createElement(Box, {
    "data-testid": "LegendList",
    sx: {
      display: 'flex',
      flexDirection: 'column',
      '&>div:last-child': {
        marginBottom: 0
      }
    }
  }, layers.map(layer => ( /*#__PURE__*/React.createElement(Legend, {
    key: layer.id,
    layer: layer
  }))))) : ( /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "NoLayers",
    variant: "body1",
    style: {
      fontWeight: 500,
      fontSize: 14
    }
  }, "No layers"))));
};

var wellKnownSymbol$9 = wellKnownSymbol$g;

var TO_STRING_TAG$1 = wellKnownSymbol$9('toStringTag');
var test$2 = {};

test$2[TO_STRING_TAG$1] = 'z';

var toStringTagSupport = String(test$2) === '[object z]';

var TO_STRING_TAG_SUPPORT = toStringTagSupport;
var isCallable$5 = isCallable$j;
var classofRaw$1 = classofRaw$2;
var wellKnownSymbol$8 = wellKnownSymbol$g;

var TO_STRING_TAG = wellKnownSymbol$8('toStringTag');
var $Object = Object;

// ES3 wrong here
var CORRECT_ARGUMENTS = classofRaw$1(function () { return arguments; }()) === 'Arguments';

// fallback for IE11 Script Access Denied error
var tryGet = function (it, key) {
  try {
    return it[key];
  } catch (error) { /* empty */ }
};

// getting tag from ES6+ `Object.prototype.toString`
var classof$6 = TO_STRING_TAG_SUPPORT ? classofRaw$1 : function (it) {
  var O, tag, result;
  return it === undefined ? 'Undefined' : it === null ? 'Null'
    // @@toStringTag case
    : typeof (tag = tryGet(O = $Object(it), TO_STRING_TAG)) == 'string' ? tag
    // builtinTag case
    : CORRECT_ARGUMENTS ? classofRaw$1(O)
    // ES3 arguments fallback
    : (result = classofRaw$1(O)) === 'Object' && isCallable$5(O.callee) ? 'Arguments' : result;
};

var classof$5 = classof$6;

var $String = String;

var toString$4 = function (argument) {
  if (classof$5(argument) === 'Symbol') throw new TypeError('Cannot convert a Symbol value to a string');
  return $String(argument);
};

// a string of all valid unicode whitespaces
var whitespaces$2 = '\u0009\u000A\u000B\u000C\u000D\u0020\u00A0\u1680\u2000\u2001\u2002' +
  '\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029\uFEFF';

var uncurryThis$8 = functionUncurryThis;
var requireObjectCoercible$1 = requireObjectCoercible$4;
var toString$3 = toString$4;
var whitespaces$1 = whitespaces$2;

var replace = uncurryThis$8(''.replace);
var ltrim = RegExp('^[' + whitespaces$1 + ']+');
var rtrim = RegExp('(^|[^' + whitespaces$1 + '])[' + whitespaces$1 + ']+$');

// `String.prototype.{ trim, trimStart, trimEnd, trimLeft, trimRight }` methods implementation
var createMethod$1 = function (TYPE) {
  return function ($this) {
    var string = toString$3(requireObjectCoercible$1($this));
    if (TYPE & 1) string = replace(string, ltrim, '');
    if (TYPE & 2) string = replace(string, rtrim, '$1');
    return string;
  };
};

var stringTrim = {
  // `String.prototype.{ trimLeft, trimStart }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimstart
  start: createMethod$1(1),
  // `String.prototype.{ trimRight, trimEnd }` methods
  // https://tc39.es/ecma262/#sec-string.prototype.trimend
  end: createMethod$1(2),
  // `String.prototype.trim` method
  // https://tc39.es/ecma262/#sec-string.prototype.trim
  trim: createMethod$1(3)
};

var global$8 = global$k;
var fails$6 = fails$i;
var uncurryThis$7 = functionUncurryThis;
var toString$2 = toString$4;
var trim = stringTrim.trim;
var whitespaces = whitespaces$2;

var $parseInt$1 = global$8.parseInt;
var Symbol$1 = global$8.Symbol;
var ITERATOR$3 = Symbol$1 && Symbol$1.iterator;
var hex = /^[+-]?0x/i;
var exec$1 = uncurryThis$7(hex.exec);
var FORCED$2 = $parseInt$1(whitespaces + '08') !== 8 || $parseInt$1(whitespaces + '0x16') !== 22
  // MS Edge 18- broken with boxed symbols
  || (ITERATOR$3 && !fails$6(function () { $parseInt$1(Object(ITERATOR$3)); }));

// `parseInt` method
// https://tc39.es/ecma262/#sec-parseint-string-radix
var numberParseInt = FORCED$2 ? function parseInt(string, radix) {
  var S = trim(toString$2(string));
  return $parseInt$1(S, (radix >>> 0) || (exec$1(hex, S) ? 16 : 10));
} : $parseInt$1;

var $$b = _export;
var $parseInt = numberParseInt;

// `parseInt` method
// https://tc39.es/ecma262/#sec-parseint-string-radix
$$b({ global: true, forced: parseInt !== $parseInt }, {
  parseInt: $parseInt
});

var anObject$6 = anObject$c;

// `RegExp.prototype.flags` getter implementation
// https://tc39.es/ecma262/#sec-get-regexp.prototype.flags
var regexpFlags = function () {
  var that = anObject$6(this);
  var result = '';
  if (that.hasIndices) result += 'd';
  if (that.global) result += 'g';
  if (that.ignoreCase) result += 'i';
  if (that.multiline) result += 'm';
  if (that.dotAll) result += 's';
  if (that.unicode) result += 'u';
  if (that.unicodeSets) result += 'v';
  if (that.sticky) result += 'y';
  return result;
};

var call$7 = functionCall;
var hasOwn$1 = hasOwnProperty_1;
var isPrototypeOf$2 = objectIsPrototypeOf;
var regExpFlags = regexpFlags;

var RegExpPrototype$1 = RegExp.prototype;

var regexpGetFlags = function (R) {
  var flags = R.flags;
  return flags === undefined && !('flags' in RegExpPrototype$1) && !hasOwn$1(R, 'flags') && isPrototypeOf$2(RegExpPrototype$1, R)
    ? call$7(regExpFlags, R) : flags;
};

var PROPER_FUNCTION_NAME = functionName.PROPER;
var defineBuiltIn$2 = defineBuiltIn$6;
var anObject$5 = anObject$c;
var $toString = toString$4;
var fails$5 = fails$i;
var getRegExpFlags = regexpGetFlags;

var TO_STRING = 'toString';
var RegExpPrototype = RegExp.prototype;
var nativeToString = RegExpPrototype[TO_STRING];

var NOT_GENERIC = fails$5(function () { return nativeToString.call({ source: 'a', flags: 'b' }) !== '/a/b'; });
// FF44- RegExp#toString has a wrong name
var INCORRECT_NAME = PROPER_FUNCTION_NAME && nativeToString.name !== TO_STRING;

// `RegExp.prototype.toString` method
// https://tc39.es/ecma262/#sec-regexp.prototype.tostring
if (NOT_GENERIC || INCORRECT_NAME) {
  defineBuiltIn$2(RegExpPrototype, TO_STRING, function toString() {
    var R = anObject$5(this);
    var pattern = $toString(R.source);
    var flags = $toString(getRegExpFlags(R));
    return '/' + pattern + '/' + flags;
  }, { unsafe: true });
}

var tryToString$3 = tryToString$5;

var $TypeError$8 = TypeError;

var deletePropertyOrThrow$1 = function (O, P) {
  if (!delete O[P]) throw new $TypeError$8('Cannot delete property ' + tryToString$3(P) + ' of ' + tryToString$3(O));
};

var uncurryThis$6 = functionUncurryThis;

var arraySlice$2 = uncurryThis$6([].slice);

var arraySlice$1 = arraySlice$2;

var floor = Math.floor;

var sort = function (array, comparefn) {
  var length = array.length;

  if (length < 8) {
    // insertion sort
    var i = 1;
    var element, j;

    while (i < length) {
      j = i;
      element = array[i];
      while (j && comparefn(array[j - 1], element) > 0) {
        array[j] = array[--j];
      }
      if (j !== i++) array[j] = element;
    }
  } else {
    // merge sort
    var middle = floor(length / 2);
    var left = sort(arraySlice$1(array, 0, middle), comparefn);
    var right = sort(arraySlice$1(array, middle), comparefn);
    var llength = left.length;
    var rlength = right.length;
    var lindex = 0;
    var rindex = 0;

    while (lindex < llength || rindex < rlength) {
      array[lindex + rindex] = (lindex < llength && rindex < rlength)
        ? comparefn(left[lindex], right[rindex]) <= 0 ? left[lindex++] : right[rindex++]
        : lindex < llength ? left[lindex++] : right[rindex++];
    }
  }

  return array;
};

var arraySort = sort;

var fails$4 = fails$i;

var arrayMethodIsStrict$2 = function (METHOD_NAME, argument) {
  var method = [][METHOD_NAME];
  return !!method && fails$4(function () {
    // eslint-disable-next-line no-useless-call -- required for testing
    method.call(null, argument || function () { return 1; }, 1);
  });
};

var userAgent$4 = engineUserAgent;

var firefox = userAgent$4.match(/firefox\/(\d+)/i);

var engineFfVersion = !!firefox && +firefox[1];

var UA = engineUserAgent;

var engineIsIeOrEdge = /MSIE|Trident/.test(UA);

var userAgent$3 = engineUserAgent;

var webkit = userAgent$3.match(/AppleWebKit\/(\d+)\./);

var engineWebkitVersion = !!webkit && +webkit[1];

var $$a = _export;
var uncurryThis$5 = functionUncurryThis;
var aCallable$7 = aCallable$a;
var toObject$1 = toObject$5;
var lengthOfArrayLike$2 = lengthOfArrayLike$4;
var deletePropertyOrThrow = deletePropertyOrThrow$1;
var toString$1 = toString$4;
var fails$3 = fails$i;
var internalSort = arraySort;
var arrayMethodIsStrict$1 = arrayMethodIsStrict$2;
var FF = engineFfVersion;
var IE_OR_EDGE = engineIsIeOrEdge;
var V8 = engineV8Version;
var WEBKIT = engineWebkitVersion;

var test$1 = [];
var nativeSort = uncurryThis$5(test$1.sort);
var push = uncurryThis$5(test$1.push);

// IE8-
var FAILS_ON_UNDEFINED = fails$3(function () {
  test$1.sort(undefined);
});
// V8 bug
var FAILS_ON_NULL = fails$3(function () {
  test$1.sort(null);
});
// Old WebKit
var STRICT_METHOD = arrayMethodIsStrict$1('sort');

var STABLE_SORT = !fails$3(function () {
  // feature detection can be too slow, so check engines versions
  if (V8) return V8 < 70;
  if (FF && FF > 3) return;
  if (IE_OR_EDGE) return true;
  if (WEBKIT) return WEBKIT < 603;

  var result = '';
  var code, chr, value, index;

  // generate an array with more 512 elements (Chakra and old V8 fails only in this case)
  for (code = 65; code < 76; code++) {
    chr = String.fromCharCode(code);

    switch (code) {
      case 66: case 69: case 70: case 72: value = 3; break;
      case 68: case 71: value = 4; break;
      default: value = 2;
    }

    for (index = 0; index < 47; index++) {
      test$1.push({ k: chr + index, v: value });
    }
  }

  test$1.sort(function (a, b) { return b.v - a.v; });

  for (index = 0; index < test$1.length; index++) {
    chr = test$1[index].k.charAt(0);
    if (result.charAt(result.length - 1) !== chr) result += chr;
  }

  return result !== 'DGBEFHACIJK';
});

var FORCED$1 = FAILS_ON_UNDEFINED || !FAILS_ON_NULL || !STRICT_METHOD || !STABLE_SORT;

var getSortCompare = function (comparefn) {
  return function (x, y) {
    if (y === undefined) return -1;
    if (x === undefined) return 1;
    if (comparefn !== undefined) return +comparefn(x, y) || 0;
    return toString$1(x) > toString$1(y) ? 1 : -1;
  };
};

// `Array.prototype.sort` method
// https://tc39.es/ecma262/#sec-array.prototype.sort
$$a({ target: 'Array', proto: true, forced: FORCED$1 }, {
  sort: function sort(comparefn) {
    if (comparefn !== undefined) aCallable$7(comparefn);

    var array = toObject$1(this);

    if (STABLE_SORT) return comparefn === undefined ? nativeSort(array) : nativeSort(array, comparefn);

    var items = [];
    var arrayLength = lengthOfArrayLike$2(array);
    var itemsLength, index;

    for (index = 0; index < arrayLength; index++) {
      if (index in array) push(items, array[index]);
    }

    internalSort(items, getSortCompare(comparefn));

    itemsLength = lengthOfArrayLike$2(items);
    index = 0;

    while (index < itemsLength) array[index] = items[index++];
    while (index < arrayLength) deletePropertyOrThrow(array, index++);

    return array;
  }
});

var classof$4 = classofRaw$2;

// `IsArray` abstract operation
// https://tc39.es/ecma262/#sec-isarray
// eslint-disable-next-line es/no-array-isarray -- safe
var isArray$1 = Array.isArray || function isArray(argument) {
  return classof$4(argument) === 'Array';
};

var $$9 = _export;
var uncurryThis$4 = functionUncurryThis;
var isArray = isArray$1;

var nativeReverse = uncurryThis$4([].reverse);
var test = [1, 2];

// `Array.prototype.reverse` method
// https://tc39.es/ecma262/#sec-array.prototype.reverse
// fix for Safari 12.0 bug
// https://bugs.webkit.org/show_bug.cgi?id=188794
$$9({ target: 'Array', proto: true, forced: String(test) === String(test.reverse()) }, {
  reverse: function reverse() {
    // eslint-disable-next-line no-self-assign -- dirty hack
    if (isArray(this)) this.length = this.length;
    return nativeReverse(this);
  }
});

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
/* Function which calculates the distance between two points */
const distance = (a, b) => {
  return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
};
const checkHoverVertice = (feature, mouseX, mouseY, convertGeoCoordsToScreenCoords, ignoreCoordinateIndexInFeature) => {
  const maxDistance = 20;
  if (feature.geometry.type === 'Point') {
    const featureCoords = feature.geometry.coordinates;
    /* Get all vertexes */
    const XYCoords = convertGeoCoordsToScreenCoords([featureCoords]);
    if (XYCoords.length > 0 && distance(XYCoords[0], {
      x: mouseX,
      y: mouseY
    }) < maxDistance) {
      return {
        coordinateIndexInFeature: 0
      };
    }
  }
  if (feature.geometry.type === 'MultiPoint') {
    for (let polygonIndex = feature.geometry.coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
      const featureCoords = feature.geometry.coordinates[polygonIndex];
      if (featureCoords === undefined) {
        // eslint-disable-next-line no-continue
        continue;
      }
      /* Get all vertexes */
      const XYCoords = convertGeoCoordsToScreenCoords([featureCoords]);
      if (XYCoords.length > 0 && distance(XYCoords[0], {
        x: mouseX,
        y: mouseY
      }) < maxDistance) {
        return {
          coordinateIndexInFeature: polygonIndex
        };
      }
    }
  }
  if (feature.geometry.type === 'Polygon') {
    const point = [mouseX, mouseY];
    for (let polygonIndex = feature.geometry.coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
      const featureCoords = feature.geometry.coordinates[polygonIndex];
      if (featureCoords === undefined) {
        // eslint-disable-next-line no-continue
        continue;
      }
      /* Get all vertexes */
      const poly = [convertGeoCoordsToScreenCoords(featureCoords).map(coord => {
        return [coord.x, coord.y];
      })];
      try {
        const isPointInPoly = turf.booleanPointInPolygon(turf.point(point), turf.polygon(poly));
        if (isPointInPoly) {
          if (ignoreCoordinateIndexInFeature) {
            return {
              coordinateIndexInFeature: 0
            };
          }
          return {
            coordinateIndexInFeature: polygonIndex
          };
        }
      } catch (e) {
        // console.warn(e);
      }
    }
  }
  if (feature.geometry.type === 'MultiPolygon') {
    const point = [mouseX, mouseY];
    for (let polygonIndex = feature.geometry.coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
      const featureCoords = feature.geometry.coordinates[polygonIndex][0];
      if (featureCoords === undefined) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const poly = [convertGeoCoordsToScreenCoords(featureCoords).map(coord => {
        return [coord.x, coord.y];
      })];
      try {
        const isPointInPoly = turf.booleanPointInPolygon(turf.point(point), turf.polygon(poly));
        if (isPointInPoly) {
          return {
            coordinateIndexInFeature: polygonIndex
          };
        }
      } catch (e) {
        console.warn(e);
      }
    }
  }
  if (feature.geometry.type === 'LineString') {
    const featureCoords = feature.geometry.coordinates;
    /* Get all vertexes */
    const XYCoords = convertGeoCoordsToScreenCoords(featureCoords);
    /* Snap to the vertex closer than specified pixels */
    for (let j = 0; j < XYCoords.length; j += 1) {
      const coord = XYCoords[j];
      if (distance(coord, {
        x: mouseX,
        y: mouseY
      }) < maxDistance) {
        return {
          coordinateIndexInFeature: j
        };
      }
    }
  }
  return null;
};
const checkHoverFeatures = (geojson, mouseX, mouseY, convertGeoCoordsToScreenCoords, ignoreCoordinateIndexInFeature) => {
  for (let j = 0; j < geojson.features.length; j += 1) {
    const feature = geojson.features[j];
    const hoverResult = checkHoverVertice(feature, mouseX, mouseY, convertGeoCoordsToScreenCoords, ignoreCoordinateIndexInFeature);
    if (hoverResult != null) {
      return {
        coordinateIndexInFeature: hoverResult.coordinateIndexInFeature,
        featureIndex: j,
        feature
      };
    }
  }
  return null;
};
let generatedDrawFunctionIds = 0;
const generateDrawFunctionId = () => {
  generatedDrawFunctionIds += 1;
  return `drawFunctionId_${generatedDrawFunctionIds}`;
};
/**
 *  DrawFunction store for re-use of drawFunctions
 */
const drawFunctionStore = [];
const getDrawFunctionFromStore = id => {
  const drawFunction = drawFunctionStore.find(drawFunction => drawFunction.id === id);
  return drawFunction === null || drawFunction === void 0 ? void 0 : drawFunction.drawMethod;
};
const registerDrawFunction = (drawFunction = () => {}) => {
  const id = generateDrawFunctionId();
  const newFunction = {
    id,
    drawMethod: drawFunction
  };
  drawFunctionStore.push(newFunction);
  return id;
};
const NEW_LINESTRING_CREATED = 'new point in LineString created';
const NEW_FEATURE_CREATED = 'new feature created';
const NEW_POINT_CREATED = 'new point created';

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
const defaultGeoJSONStyleProperties = {
  fill: '#ff7800',
  'fill-opacity': 0.2,
  stroke: '#ff7800',
  'stroke-width': 2,
  'stroke-opacity': 1
};
const emptyGeoJSON = {
  type: 'FeatureCollection',
  features: []
};
const featurePoint = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'Point',
    coordinates: []
  }
};
const featureMultiPoint = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'MultiPoint',
    coordinates: [[]]
  }
};
const featurePolygon = {
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'Polygon',
    coordinates: [[]]
  }
};
const featureBox = {
  type: 'Feature',
  properties: {
    _type: 'box'
  },
  geometry: {
    type: 'Polygon',
    coordinates: [[]]
  }
};
const lineString = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      stroke: '#66F',
      'stroke-width': 5,
      'stroke-opacity': '1'
    },
    geometry: {
      type: 'LineString',
      coordinates: [[]]
    }
  }]
};

const colors = {
  main: '#ff7800',
  vertice: '#000'
};
const Proj4js = proj4;
// Cache for for storing and reusing Proj4 instances
const projectorCache = {};
// Ensure that you have a Proj4 object, pulling from the cache if necessary
const getProj4 = projection => {
  if (projection instanceof Proj4js.Proj) {
    return projection;
  }
  if (typeof projection === 'string' && projection in projectorCache) {
    return projectorCache[projection];
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  projectorCache[projection] = new Proj4js.Proj(projection);
  return projectorCache[projection];
};
var EDITMODE;
(function (EDITMODE) {
  EDITMODE["EMPTY"] = "EMPTY";
  EDITMODE["DELETE_FEATURES"] = "DELETE_FEATURES";
  EDITMODE["ADD_FEATURE"] = "ADD_FEATURE";
})(EDITMODE || (EDITMODE = {}));
var DRAWMODE;
(function (DRAWMODE) {
  DRAWMODE["POLYGON"] = "POLYGON";
  DRAWMODE["BOX"] = "BOX";
  DRAWMODE["MULTIPOINT"] = "MULTIPOINT";
  DRAWMODE["POINT"] = "POINT";
  DRAWMODE["LINESTRING"] = "LINESTRING";
})(DRAWMODE || (DRAWMODE = {}));
var VERTEX;
(function (VERTEX) {
  VERTEX["NONE"] = "NONE";
  VERTEX["MIDDLE_POINT_OF_FEATURE"] = "MIDDLE_POINT_OF_FEATURE";
})(VERTEX || (VERTEX = {}));
var EDGE;
(function (EDGE) {
  EDGE["NONE"] = "NONE";
})(EDGE || (EDGE = {}));
var SNAPPEDFEATURE;
(function (SNAPPEDFEATURE) {
  SNAPPEDFEATURE["NONE"] = "NONE";
})(SNAPPEDFEATURE || (SNAPPEDFEATURE = {}));
var DRAGMODE;
(function (DRAGMODE) {
  DRAGMODE["NONE"] = "NONE";
  DRAGMODE["VERTEX"] = "VERTEX";
  DRAGMODE["FEATURE"] = "FEATURE";
})(DRAGMODE || (DRAGMODE = {}));
class MapDraw extends React.PureComponent {
  constructor(props) {
    super(props);
    this.handleGeoJSONUpdate = newGeoJSON => {
      const {
        selectedFeatureIndex,
        mapId
      } = this.props;
      this.geojson = cloneDeep(newGeoJSON);
      /* Ensure that this.snappedPolygonIndex is still within the coordinates array */
      if (this.geojson && this.geojson.features && selectedFeatureIndex < this.geojson.features.length) {
        const feature = this.geojson.features[selectedFeatureIndex];
        if (feature.geometry) {
          const {
            coordinates
          } = feature.geometry;
          if (Number(this.snappedPolygonIndex) > coordinates.length - 1) {
            this.snappedPolygonIndex = coordinates.length - 1;
          }
        }
      }
      const webmapjs = webmapUtils.getWMJSMapById(mapId);
      webmapjs && webmapjs.draw('MapDraw::handleGeoJSONUpdate');
    };
    this.handleExitDrawMode = (reason = 'escaped') => {
      const {
        exitDrawModeCallback,
        selectedFeatureIndex
      } = this.props;
      // remove last vertice when pressing ESC while drawing a polygon
      if (this.myDrawMode === DRAWMODE.POLYGON && this.myEditMode === EDITMODE.ADD_FEATURE && reason === 'escaped') {
        const currentFeature = this.geojson.features[selectedFeatureIndex];
        const currentGeometry = currentFeature.geometry;
        const coordinates = currentGeometry.coordinates[0];
        if (currentGeometry.type === 'Polygon') {
          coordinates.splice(coordinates.length - 1, 1);
        }
      }
      if (exitDrawModeCallback && (this.myEditMode === EDITMODE.EMPTY || this.myEditMode === EDITMODE.DELETE_FEATURES)) {
        exitDrawModeCallback(reason);
      }
      this.cancelEdit(this.myDrawMode !== DRAWMODE.BOX);
      if (this.myDrawMode === DRAWMODE.POLYGON && exitDrawModeCallback) {
        exitDrawModeCallback(reason);
      }
      if (this.myDrawMode === DRAWMODE.LINESTRING && exitDrawModeCallback) {
        exitDrawModeCallback(reason);
      }
    };
    this.myEditMode = EDITMODE.EMPTY;
    this.myDrawMode = DRAWMODE.POLYGON;
    this.beforeDraw = this.beforeDraw.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseDown = this.mouseDown.bind(this);
    this.deleteFeature = this.deleteFeature.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
    this.cancelEdit = this.cancelEdit.bind(this);
    this.distance = this.distance.bind(this);
    this.isBetween = this.isBetween.bind(this);
    this.checkDist = this.checkDist.bind(this);
    this.hoverEdge = this.hoverEdge.bind(this);
    this.drawPolygon = this.drawPolygon.bind(this);
    this.moveVertex = this.moveVertex.bind(this);
    this.featureHasChanged = this.featureHasChanged.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.hoverVertex = this.hoverVertex.bind(this);
    this.transposePolygon = this.transposePolygon.bind(this);
    this.transposeVertex = this.transposeVertex.bind(this);
    this.triggerMouseDownTimer = this.triggerMouseDownTimer.bind(this);
    this.mouseDoubleClick = this.mouseDoubleClick.bind(this);
    this.handleDrawMode = this.handleDrawMode.bind(this);
    this.componentCleanup = this.componentCleanup.bind(this);
    this.initializeFeature = this.initializeFeature.bind(this);
    this.validatePolys = this.validatePolys.bind(this);
    this.insertVertexInEdge = this.insertVertexInEdge.bind(this);
    this.createNewFeature = this.createNewFeature.bind(this);
    this.addPointToMultiPointFeature = this.addPointToMultiPointFeature.bind(this);
    this.addVerticesToPolygonFeature = this.addVerticesToPolygonFeature.bind(this);
    this.checkIfFeatureIsBox = this.checkIfFeatureIsBox.bind(this);
    this.handleGeoJSONUpdate = this.handleGeoJSONUpdate.bind(this);
    this.handleDrawMode(props.drawMode);
    this.textPositions = [];
    this.mouseOverPolygonCoordinates = [];
    this.defaultPolyProps = {
      stroke: '#000',
      'stroke-width': 0.4,
      'stroke-opacity': 1,
      fill: '#33cc00',
      'fill-opacity': 1
    };
    this.defaultLineStringProps = {
      stroke: '#000',
      'stroke-width': 1.5,
      'stroke-opacity': 1,
      fill: '#33cc00',
      'fill-opacity': 1
    };
    this.defaultIconProps = {
      imageWidth: 30,
      imageHeight: 30
    };
    this.somethingWasDragged = DRAGMODE.NONE;
    this.mouseIsOverVertexNr = VERTEX.NONE;
    this.selectedEdge = EDGE.NONE;
    if (props.geojson) {
      this.geojson = cloneDeep(props.geojson);
    } else {
      this.geojson = cloneDeep(emptyGeoJSON);
      this.featureHasChanged('new');
    }
    this.validatePolys(true);
  }
  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    const {
      mapId,
      isInDeleteMode
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.disabled === undefined) {
      this.disabled = isInDeleteMode;
    }
    if (webmapjs !== undefined && this.listenersInitialized !== true) {
      this.listenersInitialized = true;
      webmapjs.addListener('beforecanvasdisplay', this.beforeDraw, true);
      webmapjs.addListener('beforemousemove', this.mouseMove, true);
      webmapjs.addListener('beforemousedown', this.mouseDown, true);
      webmapjs.addListener('beforemouseup', this.mouseUp, true);
      this.disabled = false;
    }
    if (webmapjs !== undefined) {
      webmapjs.draw();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      geojson,
      isInEditMode,
      isInDeleteMode,
      drawMode,
      mapId
    } = this.props;
    const prevGeojson = prevProps && prevProps.geojson;
    const prevIsInEditMode = prevProps && prevProps.isInEditMode;
    const prevIsInDeleteMode = prevProps && prevProps.isInDeleteMode;
    const prevDrawMode = prevProps && prevProps.drawMode;
    /* Handle geojson update */
    if (geojson !== prevGeojson) {
      this.handleGeoJSONUpdate(geojson);
    }
    /* Handle toggle edit */
    if (isInEditMode !== prevIsInEditMode) {
      if (isInEditMode === false && this.myEditMode !== EDITMODE.EMPTY) {
        this.cancelEdit(true); /* Throw away last vertice */
        if (this.myEditMode === EDITMODE.DELETE_FEATURES) {
          this.myEditMode = EDITMODE.EMPTY;
          return;
        }
      }
      webmapUtils.getWMJSMapById(mapId).draw('MapDraw::componentDidUpdateIsInEditMode');
    }
    /* Handle toggle delete */
    if (isInDeleteMode !== prevIsInDeleteMode) {
      if (isInDeleteMode === true) {
        this.myEditMode = EDITMODE.DELETE_FEATURES;
      } else if (this.myEditMode === EDITMODE.DELETE_FEATURES) {
        this.myEditMode = EDITMODE.EMPTY;
      }
    }
    if (isInEditMode !== prevIsInEditMode || isInDeleteMode !== prevIsInDeleteMode) {
      if (isInEditMode === false && isInDeleteMode === false) {
        this.myEditMode = EDITMODE.EMPTY;
      }
    }
    /* Handle new drawmode */
    if (drawMode !== prevDrawMode) {
      /* Handle drawmode */
      this.handleDrawMode(drawMode);
    }
  }
  componentWillUnmount() {
    this.componentCleanup();
  }
  handleKeyDown(event) {
    const ESCAPE_KEY = 27;
    if (event.keyCode === ESCAPE_KEY) {
      this.handleExitDrawMode('escaped');
    }
  }
  /* Converts string input into right drawmode */
  handleDrawMode(_drawMode) {
    if (_drawMode) {
      const drawMode = _drawMode.toUpperCase();
      if (drawMode === 'POINT') {
        this.myDrawMode = DRAWMODE.POINT;
      }
      if (drawMode === 'MULTIPOINT') {
        this.myDrawMode = DRAWMODE.MULTIPOINT;
      }
      if (drawMode === 'BOX') {
        this.myDrawMode = DRAWMODE.BOX;
      }
      if (drawMode === 'POLYGON') {
        this.myDrawMode = DRAWMODE.POLYGON;
      }
      if (drawMode === 'LINESTRING') {
        this.myDrawMode = DRAWMODE.LINESTRING;
      }
    }
  }
  getPixelCoordFromGeoCoord(featureCoords) {
    const {
      mapId
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    const {
      width,
      height
    } = webmapjs.getSize();
    const bbox = webmapjs.getBBOX();
    const proj = webmapjs.getProj4();
    const XYCoords = [];
    const from = getProj4(proj.lonlat);
    const to = getProj4(proj.crs);
    for (let j = 0; j < featureCoords.length; j += 1) {
      if (featureCoords[j].length < 2) {
        // eslint-disable-next-line no-continue
        continue;
      }
      let coordinates = {
        x: featureCoords[j][0],
        y: featureCoords[j][1]
      };
      coordinates = proj.proj4.transform(from, to, coordinates);
      const x = width * (coordinates.x - bbox.left) / (bbox.right - bbox.left);
      const y = height * (coordinates.y - bbox.top) / (bbox.bottom - bbox.top);
      XYCoords.push({
        x,
        y
      });
    }
    return XYCoords;
  }
  hoverVertex(feature, mouseX, mouseY) {
    let foundVertex = VERTEX.NONE;
    this.mouseIsOverVertexNr = VERTEX.NONE;
    if (feature.geometry.type === 'Point') {
      this.snappedPolygonIndex = SNAPPEDFEATURE.NONE;
      const featureCoords = feature.geometry.coordinates;
      /* Get all vertexes */
      const XYCoords = this.convertGeoCoordsToScreenCoords([featureCoords]);
      if (XYCoords.length > 0 && this.checkDist(XYCoords[0], 0, mouseX, mouseY)) {
        this.mouseIsOverVertexNr = 0;
        return;
      }
    }
    if (feature.geometry.type === 'MultiPoint') {
      this.snappedPolygonIndex = SNAPPEDFEATURE.NONE;
      for (let polygonIndex = feature.geometry.coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
        const featureCoords = feature.geometry.coordinates[polygonIndex];
        if (featureCoords === undefined) {
          // eslint-disable-next-line no-continue
          continue;
        }
        /* Get all vertexes */
        const XYCoords = this.convertGeoCoordsToScreenCoords([featureCoords]);
        if (XYCoords.length > 0 && this.checkDist(XYCoords[0], polygonIndex, mouseX, mouseY)) {
          this.mouseIsOverVertexNr = polygonIndex;
          return;
        }
      }
    }
    if (feature.geometry.type === 'Polygon') {
      for (let polygonIndex = feature.geometry.coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
        const featureCoords = feature.geometry.coordinates[polygonIndex];
        if (featureCoords === undefined) {
          // eslint-disable-next-line no-continue
          continue;
        }
        /* Get all vertexes */
        const XYCoords = this.convertGeoCoordsToScreenCoords(featureCoords);
        const middle = {
          x: 0,
          y: 0
        };
        /* Snap to the vertex closer than specified pixels */
        for (let j = 0; j < XYCoords.length - 1; j += 1) {
          const coord = XYCoords[j];
          middle.x += coord.x;
          middle.y += coord.y;
          if (this.checkDist(coord, polygonIndex, mouseX, mouseY)) {
            foundVertex = j;
            break;
          }
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        middle.x = parseInt(middle.x / (XYCoords.length - 1), 10);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        middle.y = parseInt(middle.y / (XYCoords.length - 1), 10);
        /* Check if the mouse hovers the middle vertex */
        if (foundVertex === VERTEX.NONE && this.checkDist(middle, polygonIndex, mouseX, mouseY)) {
          foundVertex = VERTEX.MIDDLE_POINT_OF_FEATURE;
        }
        this.mouseIsOverVertexNr = foundVertex;
      }
    }
    if (feature.geometry.type === 'LineString') {
      const lineStringIndex = 0;
      const featureCoords = feature.geometry.coordinates;
      /* Get all vertexes */
      const XYCoords = this.convertGeoCoordsToScreenCoords(featureCoords);
      /* Snap to the vertex closer than specified pixels */
      for (let j = 0; j < XYCoords.length; j += 1) {
        const coord = XYCoords[j];
        // if (!coord || coord.length < 2) continue;
        if (this.checkDist(coord, lineStringIndex, mouseX, mouseY)) {
          foundVertex = j;
          break;
        }
      }
      this.mouseIsOverVertexNr = foundVertex;
    }
  }
  mouseMove(event) {
    const {
      isInEditMode,
      selectedFeatureIndex,
      onHoverFeature
    } = this.props;
    if (event && event.rightButton === true) {
      return undefined;
    }
    /* mouseMove is an event callback function which is triggered when the mouse moves over the map
      This event is only triggered if the map is in hover state.
      E.g. when the map is dragging/panning, this event is not triggerd
    */
    const {
      mouseX,
      mouseY,
      mouseDown
    } = event;
    this.mouseX = mouseX;
    this.mouseY = mouseY;
    if (onHoverFeature) {
      const handleMouseStoppedTimer = () => {
        const result = checkHoverFeatures(this.geojson, this.mouseX, this.mouseY, coordinates => {
          return this.convertGeoCoordsToScreenCoords(coordinates);
        });
        if (result) {
          this.featureEvent = {
            coordinateIndexInFeature: result.coordinateIndexInFeature,
            featureIndex: result.featureIndex,
            mouseX,
            mouseY,
            isInEditMode,
            feature: result.feature
          };
          onHoverFeature(this.featureEvent);
        } else {
          this.featureEvent = undefined;
        }
        const {
          mapId
        } = this.props;
        const webmapjs = webmapUtils.getWMJSMapById(mapId);
        webmapjs && webmapjs.draw('MapDraw::onHoverFeature');
      };
      window.clearTimeout(this.mouseStoppedTimer);
      this.mouseStoppedTimer = window.setTimeout(handleMouseStoppedTimer, 20);
    }
    if (isInEditMode === false) {
      return undefined;
    }
    const feature = this.geojson.features[selectedFeatureIndex];
    if (!feature) {
      return undefined;
    }
    const {
      mapId
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
      x: mouseX,
      y: mouseY
    });
    /* The mouse is hovering a vertice, and the mousedown is into effect, move vertice accordingly */
    const ret = this.moveVertex(feature, mouseDown);
    if (ret === false) {
      webmapjs.draw('MapDraw::mouseMove');
      // eslint-disable-next-line consistent-return
      return false;
    }
    /* Check if the mouse hovers any vertice of any polygon */
    this.hoverVertex(feature, mouseX, mouseY);
    if (this.mouseIsOverVertexNr !== VERTEX.NONE) {
      /* We found a vertex */
      this.selectedEdge = EDGE.NONE; /* We found a vertex, not an edge: reset it */
      webmapjs.setCursor('move');
      webmapjs.draw('MapDraw::hoverVertex');
      // eslint-disable-next-line consistent-return
      return false;
    }
    webmapjs.setCursor();
    /* Check if the mouse hovers an edge of a polygon */
    this.selectedEdge = EDGE.NONE;
    if (this.myEditMode !== EDITMODE.DELETE_FEATURES && !this.checkIfFeatureIsBox(feature)) {
      const retObj = this.hoverEdge(feature.geometry, mouseX, mouseY);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.selectedEdge = retObj.selectedEdge;
      this.snappedPolygonIndex = retObj.snappedPolygonIndex;
    }
    if (this.selectedEdge !== EDGE.NONE) {
      webmapjs.draw('MapDraw::mouseMove');
      // eslint-disable-next-line consistent-return
      return false;
    }
    if (this.myEditMode === EDITMODE.ADD_FEATURE) {
      // eslint-disable-next-line consistent-return
      return false;
    }
    /* We did not find anything under the mousecursor,
      return true means that the map will continue with its own mousehandling
    */
    if (this.mouseIsOverVertexNr === VERTEX.NONE && this.selectedEdge === EDGE.NONE) {
      webmapjs.draw('MapDraw::mouseMove');
      // eslint-disable-next-line consistent-return
      return true; /* False means that this component will take over entire controll.
                     True means that it is still possible to pan and drag the map while editing */
    }
    return undefined;
  }
  /* Returns true if double click is detected */
  triggerMouseDownTimer(event) {
    if (!this.doubleClickTimer) {
      this.doubleClickTimer = {};
    }
    const {
      mouseX,
      mouseY
    } = event;
    const checkTimeOut = () => {
      this.doubleClickTimer.isRunning = false;
    };
    /* Reset the timer */
    if (this.doubleClickTimer.timer) {
      clearTimeout(this.doubleClickTimer.timer);
      this.doubleClickTimer.timer = null;
    }
    /* Check if double click on this location occured */
    if (this.doubleClickTimer.isRunning === true) {
      if (mouseX === this.doubleClickTimer.mouseX && mouseY === this.doubleClickTimer.mouseY) {
        this.doubleClickTimer.isRunning = false;
        return true;
      }
    }
    /* Create new timer */
    this.doubleClickTimer.isRunning = true;
    this.doubleClickTimer.mouseX = mouseX;
    this.doubleClickTimer.mouseY = mouseY;
    this.doubleClickTimer.timer = window.setTimeout(checkTimeOut, 300);
    /* No double click detected, return false */
    return false;
  }
  mouseDoubleClick() {
    this.handleExitDrawMode('doubleClicked');
  }
  /* Insert a new vertex into an edge, e.g a line is clicked and a point is added */
  insertVertexInEdge(event) {
    const {
      mouseX,
      mouseY
    } = event;
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.myDrawMode === DRAWMODE.POLYGON) {
      if (this.selectedEdge !== EDGE.NONE && this.myEditMode !== EDITMODE.DELETE_FEATURES) {
        this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
          x: mouseX,
          y: mouseY
        });
        const feature = this.geojson.features[selectedFeatureIndex];
        if (this.checkIfFeatureIsBox(feature)) {
          return false;
        }
        const featureCoords = feature.geometry.coordinates[this.snappedPolygonIndex];
        if (featureCoords === undefined) {
          return false;
        }
        featureCoords.splice(this.selectedEdge + 1, 0, [this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.featureHasChanged('insert vertex into line');
        this.mouseMove(event);
        return false;
      }
    }
    if (this.myDrawMode === DRAWMODE.LINESTRING) {
      if (this.selectedEdge !== EDGE.NONE && this.myEditMode !== EDITMODE.DELETE_FEATURES) {
        this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
          x: mouseX,
          y: mouseY
        });
        const feature = this.geojson.features[selectedFeatureIndex];
        if (this.checkIfFeatureIsBox(feature)) {
          return false;
        }
        const featureCoords = feature.geometry.coordinates;
        if (featureCoords === undefined) {
          return false;
        }
        featureCoords.splice(this.selectedEdge + 1, 0, [this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.featureHasChanged('insert vertex into line');
        this.mouseMove(event);
        return false;
      }
    }
    return true;
  }
  /* This is trigged when a new feature is created.  */
  createNewFeature(event) {
    const {
      mouseX,
      mouseY
    } = event;
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.myEditMode === EDITMODE.EMPTY) {
      this.myEditMode = EDITMODE.ADD_FEATURE;
      let feature = this.geojson.features[selectedFeatureIndex];
      // eslint-disable-next-line no-multi-assign
      feature = this.geojson.features[selectedFeatureIndex] = this.initializeFeature(feature, this.myDrawMode);
      this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
        x: mouseX,
        y: mouseY
      });
      if (this.myDrawMode === DRAWMODE.POINT || this.myDrawMode === DRAWMODE.MULTIPOINT) {
        /* Create points */
        const pointGeometry = feature.geometry;
        if (pointGeometry) {
          if (pointGeometry.coordinates === undefined) {
            pointGeometry.coordinates = [];
          }
        }
        const multiPointGeometry = feature.geometry;
        if (multiPointGeometry) {
          if (multiPointGeometry.coordinates === undefined) {
            multiPointGeometry.coordinates = [[]];
          }
        }
        if (this.myDrawMode === DRAWMODE.POINT) {
          const featureCoords = pointGeometry.coordinates;
          featureCoords[0] = this.mouseGeoCoord.x;
          featureCoords[1] = this.mouseGeoCoord.y;
          this.snappedPolygonIndex = pointGeometry.coordinates.length - 1;
          /* For type POINT it is not possible to add multiple points to the same feature */
          this.myEditMode = EDITMODE.EMPTY;
          this.featureHasChanged(NEW_POINT_CREATED);
        }
        if (this.myDrawMode === DRAWMODE.MULTIPOINT) {
          this.myEditMode = EDITMODE.EMPTY;
          let featureCoords = multiPointGeometry.coordinates;
          if (featureCoords === undefined) {
            featureCoords = [];
          }
          if (featureCoords[0] === undefined || featureCoords[0].length === 0) {
            featureCoords[0] = []; /* Used to create the first polygon */
          } else {
            featureCoords.push([]); /* Used to create extra polygons in the same feature */
          }
          // featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
          featureCoords[featureCoords.length - 1] = [this.mouseGeoCoord.x, this.mouseGeoCoord.y];
          this.snappedPolygonIndex = multiPointGeometry.coordinates.length - 1;
          this.featureHasChanged('new point in multipoint created');
        }
      }
      if (this.myDrawMode === DRAWMODE.POLYGON || this.myDrawMode === DRAWMODE.BOX) {
        /* Create poly's and boxes */
        this.myEditMode = EDITMODE.ADD_FEATURE;
        if (feature.geometry) {
          if (feature.geometry.coordinates === undefined) {
            feature.geometry.coordinates = [[]];
          }
        }
        if (feature.geometry.coordinates[0] === undefined || feature.geometry.coordinates[0].length === 0) {
          feature.geometry.coordinates[0] = []; /* Used to create the first polygon */
        } else {
          feature.geometry.coordinates.push([]); /* Used to create extra polygons in the same feature */
        }
        this.snappedPolygonIndex = feature.geometry.coordinates.length - 1;
        const featureCoords = feature.geometry.coordinates[this.snappedPolygonIndex];
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        /* This is triggered when a bounding box is created. Five points are added at once */
        if (this.myDrawMode === DRAWMODE.BOX) {
          featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
          featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
          featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        }
        if (this.myDrawMode === DRAWMODE.BOX) {
          this.mouseIsOverVertexNr = 3;
        } else {
          this.mouseIsOverVertexNr = featureCoords.length - 1;
        }
        this.featureHasChanged(NEW_FEATURE_CREATED);
      }
      if (this.myDrawMode === DRAWMODE.LINESTRING) {
        this.myEditMode = EDITMODE.EMPTY;
        /* Create linestring */
        this.myEditMode = EDITMODE.ADD_FEATURE;
        let featureCoords = feature.geometry.coordinates;
        if (featureCoords === undefined || featureCoords.length === 0) {
          featureCoords = [[this.mouseGeoCoord.x, this.mouseGeoCoord.y]];
        }
        if (featureCoords[0].length === 0) {
          featureCoords[0] = [this.mouseGeoCoord.x, this.mouseGeoCoord.y];
        }
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.snappedPolygonIndex = 0;
        this.featureHasChanged(NEW_LINESTRING_CREATED);
        this.mouseIsOverVertexNr = featureCoords.length - 1;
      }
      webmapjs.draw('MapDraw::mouseDown');
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return true;
  }
  /* This is triggered when new points are added during the addmultipoint mode. One point is added per time */
  addPointToMultiPointFeature(event) {
    const {
      mouseX,
      mouseY
    } = event;
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.myDrawMode === DRAWMODE.MULTIPOINT) {
      if (this.myEditMode === EDITMODE.ADD_FEATURE && this.snappedPolygonIndex !== SNAPPEDFEATURE.NONE) {
        this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
          x: mouseX,
          y: mouseY
        });
        const feature = this.geojson.features[selectedFeatureIndex];
        const featureCoords = feature.geometry.coordinates;
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.featureHasChanged('point added to multipoint');
        this.mouseIsOverVertexNr = VERTEX.NONE;
        this.mouseMove(event);
        return false;
      }
    }
    return true;
  }
  addVerticesToPolygonFeature(event) {
    const {
      mouseX,
      mouseY
    } = event;
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.myDrawMode === DRAWMODE.POLYGON) {
      if (this.myEditMode === EDITMODE.ADD_FEATURE && this.snappedPolygonIndex !== SNAPPEDFEATURE.NONE) {
        this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
          x: mouseX,
          y: mouseY
        });
        const feature = this.geojson.features[selectedFeatureIndex];
        const featureCoords = feature.geometry.coordinates[this.snappedPolygonIndex];
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.featureHasChanged('vertex added to polygon');
        this.mouseIsOverVertexNr = featureCoords.length - 1;
        this.mouseMove(event);
        return false;
      }
    }
    return undefined;
  }
  addPointToLineStringFeature(event) {
    const {
      mouseX,
      mouseY
    } = event;
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.myDrawMode === DRAWMODE.LINESTRING) {
      if (this.myEditMode === EDITMODE.ADD_FEATURE) {
        this.mouseGeoCoord = webmapjs.getLatLongFromPixelCoord({
          x: mouseX,
          y: mouseY
        });
        const feature = this.geojson.features[selectedFeatureIndex];
        const featureCoords = feature.geometry.coordinates;
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
        this.featureHasChanged('vertex added to LineString');
        this.mouseIsOverVertexNr = featureCoords.length - 1;
        this.mouseMove(event);
        return false;
      }
    }
    return true;
  }
  mouseDown(event) {
    const {
      isInEditMode
    } = this.props;
    if (event && event.rightButton === true) {
      return;
    }
    if (isInEditMode === false) {
      return;
    }
    if (this.triggerMouseDownTimer(event)) {
      // eslint-disable-next-line consistent-return
      return this.mouseDoubleClick();
    }
    if (this.myEditMode === EDITMODE.ADD_FEATURE && this.myDrawMode === DRAWMODE.BOX) {
      // eslint-disable-next-line consistent-return
      return this.mouseDoubleClick();
    }
    this.somethingWasDragged = DRAGMODE.NONE;
    if (this.mouseIsOverVertexNr !== VERTEX.NONE && this.myEditMode === EDITMODE.EMPTY) {
      // eslint-disable-next-line consistent-return
      return false;
    }
    /* Insert a new vertex into an edge, e.g a line is clicked and a point is added */
    // eslint-disable-next-line consistent-return
    if (this.insertVertexInEdge(event) === false) {
      return false;
    }
    /* This is trigged when a new feature is created.  */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (this.createNewFeature(event) === false) {
      /* Start with mouse move from this mouse down location,  enables sizing the box while moving the mouse */
      this.mouseMove(event);
      return false;
    }
    /* This is triggered when new points are added during the addmultipoint mode. One point is added per time */
    if (this.addPointToMultiPointFeature(event) === false) {
      return false;
    }
    /* This is triggered when new points are added during the addpolygon mode. One point is added per time */
    if (this.addVerticesToPolygonFeature(event) === false) {
      return false;
    }
    /* This is triggered when new points are added to a linestring */
    if (this.addPointToLineStringFeature(event) === false) {
      return false;
    }
    return false; /* False means that this component will take over entire controll.
                     True means that it is still possible to pan and drag the map while editing */
  }
  deletePolygon(index) {
    const {
      selectedFeatureIndex,
      deletePolygonCallback
    } = this.props;
    const feature = this.geojson.features[selectedFeatureIndex];
    feature.geometry.coordinates.splice(index, 1);
    if (deletePolygonCallback) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      deletePolygonCallback();
    }
  }
  deleteFeature() {
    /* Deletes any features under the mousecursor */
    const {
      mapId,
      selectedFeatureIndex
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (this.mouseIsOverVertexNr === VERTEX.NONE) {
      return;
    }
    const feature = this.geojson.features[selectedFeatureIndex];
    const featureCoords = feature.geometry.coordinates[this.snappedPolygonIndex];
    if (featureCoords === undefined) {
      return;
    }
    if (this.mouseIsOverVertexNr !== VERTEX.MIDDLE_POINT_OF_FEATURE) {
      /* Remove edge of polygon */
      if (featureCoords.length <= 3) {
        /* Remove the polygon completely if it can not have an area */
        this.deletePolygon(this.snappedPolygonIndex);
      } else {
        /* Remove edge of polygon */
        // eslint-disable-next-line no-lonely-if
        if (!this.checkIfFeatureIsBox(feature)) {
          if (Array.isArray(featureCoords) && typeof this.mouseIsOverVertexNr === 'number') {
            featureCoords.splice(this.mouseIsOverVertexNr, 1);
          }
        }
      }
    } else {
      /* Remove the polygon completely */
      this.deletePolygon(this.snappedPolygonIndex);
    }
    this.featureHasChanged('deleteFeature');
    this.selectedEdge = EDGE.NONE;
    this.mouseIsOverVertexNr = VERTEX.NONE;
    webmapjs.draw('MapDraw::deletefeatures');
  }
  mouseUp(event) {
    const {
      isInEditMode,
      onClickFeature
    } = this.props;
    if (event && event.rightButton === true) {
      return;
    }
    if (onClickFeature) {
      const {
        mouseX,
        mouseY
      } = event;
      const ignoreCoordinateIndexInFeature = true;
      const result = checkHoverFeatures(this.geojson, mouseX, mouseY, coordinates => {
        return this.convertGeoCoordsToScreenCoords(coordinates);
      }, ignoreCoordinateIndexInFeature);
      if (result) {
        const featureEvent = {
          coordinateIndexInFeature: result.coordinateIndexInFeature,
          featureIndex: result.featureIndex,
          mouseX,
          mouseY,
          isInEditMode,
          feature: result.feature
        };
        onClickFeature(featureEvent);
      } else {
        onClickFeature(null);
      }
    }
    if (isInEditMode === false) {
      return;
    }
    /* Keep the mouse down to drag a box */
    if (this.myDrawMode === DRAWMODE.BOX && this.myEditMode === EDITMODE.ADD_FEATURE) {
      this.cancelEdit(false);
      return false;
    }
    if (this.somethingWasDragged !== DRAGMODE.NONE) {
      this.featureHasChanged(`A ${this.somethingWasDragged.toString()} was dragged`);
    }
    /* Delete a vertex or feature on mouseUp */
    if (this.myEditMode === EDITMODE.DELETE_FEATURES) {
      this.deleteFeature();
      return false;
    }
    if (this.mouseIsOverVertexNr !== VERTEX.NONE || this.selectedEdge !== EDGE.NONE) {
      return false;
    }
    if (this.myEditMode === EDITMODE.ADD_FEATURE) {
      return false;
    }
  }
  cancelEdit(cancelLastPoint) {
    const {
      isInEditMode,
      selectedFeatureIndex
    } = this.props;
    if (isInEditMode === false) {
      return;
    }
    const {
      mapId
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    /* When in addpolygon mode, finish the polygon */
    if (this.myEditMode === EDITMODE.ADD_FEATURE) {
      this.myEditMode = EDITMODE.EMPTY;
      if (this.drawMode === DRAWMODE.POLYGON || this.drawMode === DRAWMODE.BOX) {
        if (this.snappedPolygonIndex === SNAPPEDFEATURE.NONE) {
          return;
        }
        const feature = this.geojson.features[selectedFeatureIndex];
        const {
          coordinates
        } = feature.geometry;
        const polygon = coordinates[this.snappedPolygonIndex];
        if (!polygon) {
          coordinates[this.snappedPolygonIndex] = [];
          return;
        }
        if (polygon.length === 0) {
          return;
        }
        if (!this.checkIfFeatureIsBox(feature)) {
          if (cancelLastPoint === true) {
            polygon.pop();
          }
          if (polygon.length < 3) {
            coordinates.pop();
          }
        }
      }
      if (this.myDrawMode === DRAWMODE.LINESTRING) {
        const feature = this.geojson.features[selectedFeatureIndex];
        const featureCoords = feature.geometry.coordinates;
        featureCoords.pop();
      }
      this.featureHasChanged('cancelEdit');
      webmapjs.draw('MapDraw::cancelEdit');
    } else {
      /* When in deletefeatures mode, remove any vertex under the mousecursor */
      // eslint-disable-next-line no-lonely-if
      if (this.myEditMode === EDITMODE.DELETE_FEATURES) {
        this.deleteFeature();
      }
    }
  }
  moveVertex(feature, mouseDown) {
    const featureType = feature.geometry.type;
    let featureCoords = feature.geometry.coordinates[this.snappedPolygonIndex];
    if (featureType === 'Point' || featureType === 'MultiPoint' || featureType === 'LineString') {
      featureCoords = feature.geometry.coordinates;
    }
    if (!featureCoords) {
      return undefined;
    }
    const vertexSelected = mouseDown === true && this.mouseIsOverVertexNr !== VERTEX.NONE;
    if (vertexSelected || this.myEditMode === EDITMODE.ADD_FEATURE) {
      if (featureType === 'LineString') {
        this.transposeVertex(featureCoords);
        // eslint-disable-next-line consistent-return
        return false;
      }
      /* In case middle point is selected, transpose whole polygon */
      if (this.mouseIsOverVertexNr === VERTEX.MIDDLE_POINT_OF_FEATURE && this.snappedGeoCoords) {
        this.transposePolygon(featureCoords);
      } else if (this.mouseIsOverVertexNr !== VERTEX.NONE) {
        /* Transpose polygon vertex */
        this.transposeVertex(featureCoords);
      }
      // eslint-disable-next-line consistent-return
      return false;
    }
    return undefined;
  }
  transposePolygon(featureCoords) {
    const incX = this.mouseGeoCoord.x - this.snappedGeoCoords.x;
    const incY = this.mouseGeoCoord.y - this.snappedGeoCoords.y;
    this.snappedGeoCoords.x = this.mouseGeoCoord.x;
    this.snappedGeoCoords.y = this.mouseGeoCoord.y;
    for (let j = 0; j < featureCoords.length; j += 1) {
      /* eslint-disable-next-line no-param-reassign */
      featureCoords[j][0] += incX;
      /* eslint-disable-next-line no-param-reassign */
      featureCoords[j][1] += incY;
    }
    if (this.myEditMode !== EDITMODE.ADD_FEATURE) {
      this.somethingWasDragged = DRAGMODE.FEATURE;
    }
  }
  transposeVertex(featureCoords) {
    if (this.myEditMode !== EDITMODE.ADD_FEATURE) {
      this.somethingWasDragged = DRAGMODE.VERTEX;
    }
    if (this.myDrawMode === DRAWMODE.POINT) {
      /* eslint-disable-next-line no-param-reassign */
      featureCoords[0] = this.mouseGeoCoord.x;
      /* eslint-disable-next-line no-param-reassign */
      featureCoords[1] = this.mouseGeoCoord.y;
      return;
    }
    if (this.myDrawMode === DRAWMODE.LINESTRING) {
      // eslint-disable-next-line no-param-reassign
      featureCoords[this.mouseIsOverVertexNr][0] = this.mouseGeoCoord.x;
      // eslint-disable-next-line no-param-reassign
      featureCoords[this.mouseIsOverVertexNr][1] = this.mouseGeoCoord.y;
      return;
    }
    if (this.myDrawMode === DRAWMODE.MULTIPOINT) {
      if (this.mouseIsOverVertexNr !== SNAPPEDFEATURE.NONE) {
        // eslint-disable-next-line no-param-reassign
        featureCoords[this.mouseIsOverVertexNr][0] = this.mouseGeoCoord.x;
        // eslint-disable-next-line no-param-reassign
        featureCoords[this.mouseIsOverVertexNr][1] = this.mouseGeoCoord.y;
      }
      return;
    }
    if (this.myDrawMode === DRAWMODE.BOX && (featureCoords.length === 4 || featureCoords.length === 5)) {
      while (featureCoords.length < 4) {
        featureCoords.push([this.mouseGeoCoord.x, this.mouseGeoCoord.y]);
      }
      if (this.mouseIsOverVertexNr === 0) {
        featureCoords[0][0] = this.mouseGeoCoord.x;
        featureCoords[0][1] = this.mouseGeoCoord.y;
        featureCoords[1][0] = this.mouseGeoCoord.x;
        featureCoords[3][1] = this.mouseGeoCoord.y;
      }
      if (this.mouseIsOverVertexNr === 1) {
        featureCoords[1][0] = this.mouseGeoCoord.x;
        featureCoords[1][1] = this.mouseGeoCoord.y;
        featureCoords[0][0] = this.mouseGeoCoord.x;
        featureCoords[4][0] = this.mouseGeoCoord.x;
        featureCoords[2][1] = this.mouseGeoCoord.y;
      }
      if (this.mouseIsOverVertexNr === 2) {
        featureCoords[2][0] = this.mouseGeoCoord.x;
        featureCoords[2][1] = this.mouseGeoCoord.y;
        featureCoords[1][1] = this.mouseGeoCoord.y;
        featureCoords[3][0] = this.mouseGeoCoord.x;
      }
      if (this.mouseIsOverVertexNr === 3) {
        featureCoords[3][0] = this.mouseGeoCoord.x;
        featureCoords[3][1] = this.mouseGeoCoord.y;
        featureCoords[0][1] = this.mouseGeoCoord.y;
        featureCoords[4][1] = this.mouseGeoCoord.y;
        featureCoords[2][0] = this.mouseGeoCoord.x;
      }
      /* Ensure the box square shape */
      /* A: Left edge should be vertical */
      const [[featureCoord0X, featureCoord0Y], [featureCoord1X], [featureCoord2X, featureCoord2Y], [, featureCoord3Y]] = featureCoords;
      featureCoords[0][0] = featureCoord1X;
      /* B: Top edge should be horizontal */
      featureCoords[0][1] = featureCoord3Y;
      /* C: Right edge should be vertical */
      featureCoords[3][0] = featureCoord2X;
      /* D: Bottom Edge should be horizontal */
      featureCoords[1][1] = featureCoord2Y;
      /* Close the box: Last point (point 5 of the box) should have the same coordinates as the starting point */
      featureCoords[4][0] = featureCoord0X;
      featureCoords[4][1] = featureCoord0Y;
    }
    if (this.myDrawMode === DRAWMODE.POLYGON) {
      featureCoords[this.mouseIsOverVertexNr][0] = this.mouseGeoCoord.x;
      featureCoords[this.mouseIsOverVertexNr][1] = this.mouseGeoCoord.y;
      /* Transpose begin and end vertice */
      if (this.mouseIsOverVertexNr === 0) {
        featureCoords[featureCoords.length - 1][0] = this.mouseGeoCoord.x;
        featureCoords[featureCoords.length - 1][1] = this.mouseGeoCoord.y;
      }
    }
  }
  componentCleanup() {
    // this will hold the cleanup code
    document.removeEventListener('keydown', this.handleKeyDown);
    const {
      mapId
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    if (webmapjs !== undefined && this.listenersInitialized === true) {
      this.listenersInitialized = undefined;
      webmapjs.removeListener('beforecanvasdisplay', this.beforeDraw);
      webmapjs.removeListener('beforemousemove', this.mouseMove);
      webmapjs.removeListener('beforemousedown', this.mouseDown);
      webmapjs.removeListener('beforemouseup', this.mouseUp);
      webmapjs.draw();
    }
  }
  // eslint-disable-next-line class-methods-use-this
  validateFeature(feature) {
    if (!feature.properties) {
      feature.properties = {};
    }
    if (!feature.geometry) {
      feature.geometry = {};
    }
    if (!feature.type) {
      feature.type = 'Feature';
    }
    if (!feature.geometry.coordinates) {
      feature.geometry.coordinates = [];
    }
  }
  validatePolys(fixPolys) {
    if (!this.geojson || !this.geojson.features || !this.geojson.features.length) {
      return;
    }
    for (let featureIndex = 0; featureIndex < this.geojson.features.length; featureIndex += 1) {
      if (!this.geojson.features) {
        this.geojson.features = [];
      }
      const feature = this.geojson.features[featureIndex];
      this.validateFeature(feature);
      const featureType = feature.geometry.type;
      if (featureType === 'Polygon') {
        /* Loop through all polygons of the same feature */
        const polygonGeometry = feature.geometry;
        for (let polygonIndex = 0; polygonIndex < polygonGeometry.coordinates.length; polygonIndex += 1) {
          let featureCoords = polygonGeometry.coordinates[polygonIndex];
          /* Remove duplicates */
          if (!this.checkIfFeatureIsBox(feature)) {
            for (let coordIndex = featureCoords.length - 2; coordIndex >= 0; coordIndex -= 1) {
              const _startCoord = featureCoords[coordIndex];
              const _endCoord = featureCoords[coordIndex + 1];
              if (_startCoord && _endCoord) {
                if (_startCoord[0] === _endCoord[0] && _startCoord[1] === _endCoord[1]) {
                  featureCoords.splice(coordIndex, 1);
                }
              }
            }
            /* Add begin to end to make closed polygon */
            const startCoord = featureCoords[0];
            const endCoord = featureCoords[featureCoords.length - 1];
            if (startCoord && endCoord) {
              if (startCoord[0] !== endCoord[0] || startCoord[1] !== endCoord[1]) {
                featureCoords.push(cloneDeep(startCoord));
                if (this.mouseIsOverVertexNr !== VERTEX.NONE && this.mouseIsOverVertexNr !== VERTEX.MIDDLE_POINT_OF_FEATURE) {
                  /* This means that our selected vertex is one lower */
                  this.mouseIsOverVertexNr -= 1;
                }
              }
            }
          }
          /* Sort clockwise */
          if (fixPolys) {
            if (this.checkIfFeatureIsBox(feature)) {
              // A box should have:
              // - topleft index 0 and 4
              // - bottomleft index 1
              // - bottomright index 2
              // - topright index 3
              // This represents counter-clockwise ordering and represents an area (https://datatracker.ietf.org/doc/html/rfc7946#section-3.1.6)
              // Make sure the coordinate order in a box is always the same
              const boxCoords = feature.geometry.coordinates[0];
              if (boxCoords.length === 5) {
                // Copy the first four coordinates and sort them according Y and then X
                const sortedYthenX = boxCoords.slice(0, 4).sort((a, b) => {
                  return b[1] - a[1]; // Sort on Y
                }).sort((a, b) => {
                  return a[0] - b[0]; // Sort on X
                });
                // Assign the ordered coordinates back to the geometry of the feature
                feature.geometry.coordinates[0] = [[...sortedYthenX[0]], [...sortedYthenX[1]], [...sortedYthenX[3]], [...sortedYthenX[2]], [...sortedYthenX[0]]];
              }
            } else {
              const checkClockwiseOrder = featureCoordinates => {
                let sum = 0;
                for (let j = 0; j < featureCoordinates.length - 1; j += 1) {
                  const currentPoint = featureCoordinates[j];
                  const nextPoint = featureCoordinates[(j + 1) % featureCoordinates.length];
                  sum += (nextPoint[0] - currentPoint[0]) * (nextPoint[1] + currentPoint[1]);
                }
                return sum;
              };
              const sum = checkClockwiseOrder(featureCoords);
              if (sum < 0) {
                featureCoords = featureCoords.reverse();
                /* The lastly selected vertex is now aways the second in the array */
                if (this.mouseIsOverVertexNr !== VERTEX.NONE && this.mouseIsOverVertexNr !== VERTEX.MIDDLE_POINT_OF_FEATURE) {
                  this.mouseIsOverVertexNr = 1;
                }
              }
            }
          }
        }
      }
    }
  }
  beforeDraw(ctx) {
    /* beforeDraw is an event callback function which is triggered
     just before viewer will flip the back canvas buffer to the front.
     You are free to draw anything you like on the canvas.
    */
    const {
      selectedFeatureIndex,
      isInEditMode
    } = this.props;
    if (!this.geojson || !this.geojson.features || !this.geojson.features.length) {
      return;
    }
    this.textPositions = [];
    this.mouseOverPolygonCoordinates = [];
    this.mouseOverPolygonFeatureIndex = -1;
    /* Current selected feature from GeoJSON */
    for (let featureIndex = 0; featureIndex < this.geojson.features.length; featureIndex += 1) {
      const feature = this.geojson.features[featureIndex];
      const featureType = feature.geometry.type;
      const totalmiddle = {
        x: 0,
        y: 0,
        nr: 0
      };
      if (featureType === 'Point') {
        const featureCoords = feature.geometry.coordinates;
        const XYCoords = this.getPixelCoordFromGeoCoord([featureCoords]);
        if (XYCoords.length === 0) {
          // eslint-disable-next-line no-continue
          continue;
        }
        for (let j = 0; j < XYCoords.length; j += 1) {
          this.drawPoint(ctx, XYCoords[j], this.mouseIsOverVertexNr === j && selectedFeatureIndex === featureIndex, false, isInEditMode && selectedFeatureIndex === featureIndex, feature, featureIndex);
        }
      }
      if (featureType === 'MultiPoint') {
        const featureCoords = feature.geometry.coordinates;
        const XYCoords = this.getPixelCoordFromGeoCoord(featureCoords);
        if (XYCoords.length === 0) {
          // eslint-disable-next-line no-continue
          continue;
        }
        for (let j = 0; j < XYCoords.length; j += 1) {
          this.drawPoint(ctx, XYCoords[j], this.mouseIsOverVertexNr === j && selectedFeatureIndex === featureIndex, false, isInEditMode && selectedFeatureIndex === featureIndex, feature, featureIndex);
        }
      }
      if (featureType === 'MultiPolygon') {
        const multiPolygonGeometry = feature.geometry;
        for (let multiPolygonIndex = 0; multiPolygonIndex < multiPolygonGeometry.coordinates.length; multiPolygonIndex += 1) {
          const multiPoly = multiPolygonGeometry.coordinates[multiPolygonIndex];
          for (let polygonIndex = 0; polygonIndex < multiPoly.length; polygonIndex += 1) {
            const featureCoords = multiPoly[polygonIndex];
            const XYCoords = this.getPixelCoordFromGeoCoord(featureCoords);
            /* Only draw if there is stuff to show */
            if (XYCoords.length === 0) {
              // eslint-disable-next-line no-continue
              continue;
            }
            const middle = this.drawPolygon(ctx, XYCoords, featureIndex, polygonIndex);
            if (middle) {
              totalmiddle.x += middle.x * middle.nr;
              totalmiddle.y += middle.y * middle.nr;
              totalmiddle.nr += middle.nr;
            }
            if (isInEditMode) {
              /* Draw all vertices on the edges of the polygons */
              for (let j = 0; j < XYCoords.length; j += 1) {
                this.drawVertice(ctx, XYCoords[j], this.snappedPolygonIndex === polygonIndex && this.mouseIsOverVertexNr === j && selectedFeatureIndex === featureIndex, false, isInEditMode && selectedFeatureIndex === featureIndex);
              }
              if (middle && isInEditMode === true && XYCoords.length >= 3) {
                /* Draw middle vertice for the poly if poly covers an area, e.g. when it contains more than three points */
                this.drawVertice(ctx, middle, this.snappedPolygonIndex === polygonIndex && this.mouseIsOverVertexNr === VERTEX.MIDDLE_POINT_OF_FEATURE && selectedFeatureIndex === featureIndex, true, isInEditMode && selectedFeatureIndex === featureIndex);
              }
            }
          }
        }
        if (totalmiddle.nr > 0) {
          const mx = totalmiddle.x / totalmiddle.nr;
          const my = totalmiddle.y / totalmiddle.nr;
          if (feature.properties && feature.properties.text) {
            this.textPositions.push({
              x: mx,
              y: my,
              text: feature.properties.text
            });
          }
        }
      }
      if (featureType === 'Polygon') {
        const polygonGeometry = feature.geometry;
        /* Loop through all polygons of the same feature */
        for (let polygonIndex = 0; polygonIndex < polygonGeometry.coordinates.length; polygonIndex += 1) {
          const featureCoords = polygonGeometry.coordinates[polygonIndex];
          const XYCoords = this.getPixelCoordFromGeoCoord(featureCoords);
          /* Only draw if there is stuff to show */
          if (XYCoords.length === 0) {
            // eslint-disable-next-line no-continue
            continue;
          }
          const middle = this.drawPolygon(ctx, XYCoords, featureIndex, polygonIndex);
          if (middle && feature.properties && feature.properties.text) {
            this.textPositions.push({
              x: middle.x,
              y: middle.y,
              text: feature.properties.text
            });
          }
          if (isInEditMode) {
            /* Draw all vertices on the edges of the polygons */
            for (let j = 0; j < XYCoords.length; j += 1) {
              this.drawVertice(ctx, XYCoords[j], this.snappedPolygonIndex === polygonIndex && this.mouseIsOverVertexNr === j && selectedFeatureIndex === featureIndex, false, isInEditMode && selectedFeatureIndex === featureIndex);
              // Enable the following to debug vertice numbers in the screen
              // ctx.font = '20px Arial';
              // ctx.fillText(j, XYCoords[j].x - 10, XYCoords[j].y - 10);
            }
            if (middle && isInEditMode === true && XYCoords.length >= 3) {
              /* Draw middle vertice for the poly if poly covers an area, e.g. when it contains more than three points */
              this.drawVertice(ctx, middle, this.snappedPolygonIndex === polygonIndex && this.mouseIsOverVertexNr === VERTEX.MIDDLE_POINT_OF_FEATURE && selectedFeatureIndex === featureIndex, true, isInEditMode && selectedFeatureIndex === featureIndex);
            }
          }
        }
      }
      if (featureType === 'LineString') {
        /* Loop through all line pints of the same feature */
        const featureCoords = feature.geometry.coordinates;
        if (!featureCoords || !featureCoords.length) {
          // eslint-disable-next-line no-continue
          continue;
        }
        const XYCoords = this.getPixelCoordFromGeoCoord(featureCoords);
        /* Only draw if there is stuff to show */
        if (XYCoords.length === 0) {
          // eslint-disable-next-line no-continue
          continue;
        }
        const middle = this.drawLine(ctx, XYCoords, featureIndex, 0);
        if (middle && feature.properties && feature.properties.text) {
          this.textPositions.push({
            x: middle.x,
            y: middle.y,
            text: feature.properties.text
          });
        }
        if (isInEditMode) {
          /* Draw all vertices on the edges of the polygons */
          for (let j = 0; j < XYCoords.length; j += 1) {
            this.drawVertice(ctx, XYCoords[j], this.mouseIsOverVertexNr === j && selectedFeatureIndex === featureIndex, false, isInEditMode && selectedFeatureIndex === featureIndex);
          }
        }
      }
    }
    /* Higlight polygon with mousehover */
    if (isInEditMode === true && this.mouseOverPolygonFeatureIndex === selectedFeatureIndex && this.mouseIsOverVertexNr === VERTEX.NONE && this.snappedPolygonIndex === SNAPPEDFEATURE.NONE && this.selectedEdge === EDGE.NONE || this.mouseIsOverVertexNr === VERTEX.MIDDLE_POINT_OF_FEATURE) {
      const feature = this.geojson.features[selectedFeatureIndex];
      const featureProperties = feature.properties;
      if (this.mouseOverPolygonCoordinates.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(this.mouseOverPolygonCoordinates[0].x, this.mouseOverPolygonCoordinates[0].y);
        for (let j = 1; j < this.mouseOverPolygonCoordinates.length; j += 1) {
          const coord = this.mouseOverPolygonCoordinates[j];
          ctx.lineTo(coord.x, coord.y);
        }
        const fillOpacity = featureProperties && featureProperties['fill-opacity'] || defaultGeoJSONStyleProperties && defaultGeoJSONStyleProperties['fill-opacity'];
        ctx.closePath();
        ctx.strokeStyle = (featureProperties === null || featureProperties === void 0 ? void 0 : featureProperties.stroke) || (defaultGeoJSONStyleProperties === null || defaultGeoJSONStyleProperties === void 0 ? void 0 : defaultGeoJSONStyleProperties.stroke);
        ctx.lineWidth = defaultGeoJSONStyleProperties && defaultGeoJSONStyleProperties['stroke-width'];
        ctx.fillStyle = (featureProperties === null || featureProperties === void 0 ? void 0 : featureProperties.fill) || (defaultGeoJSONStyleProperties === null || defaultGeoJSONStyleProperties === void 0 ? void 0 : defaultGeoJSONStyleProperties.fill);
        ctx.globalAlpha = Number(fillOpacity) + 0.2; // Fill gets +0.2 opacity on hover
        ctx.fill();
        ctx.globalAlpha = defaultGeoJSONStyleProperties && defaultGeoJSONStyleProperties['stroke-opacity'];
        ctx.stroke();
      }
    }
    /* Draw labels */
    for (let j = 0; j < this.textPositions.length; j += 1) {
      const {
        x,
        y,
        text
      } = this.textPositions[j];
      ctx.fillStyle = 'black';
      ctx.font = '12px Arial';
      ctx.fillText(text, x, y);
    }
  }
  /* Function which calculates the distance between two points */
  // eslint-disable-next-line class-methods-use-this
  distance(a, b) {
    return Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
  }
  /* Function which calculates if a point is between two other points */
  isBetween(a, c, b) {
    const da = this.distance(a, c) + this.distance(c, b);
    const db = this.distance(a, b);
    return Math.abs(da - db) < 1;
  }
  /* Checks if mouse is in proximity of given coordinate */
  checkDist(coord, polygonIndex, mouseX, mouseY) {
    const VERTEX = this.distance(coord, {
      x: mouseX,
      y: mouseY
    });
    if (VERTEX < 10) {
      this.snappedGeoCoords = Object.assign({}, this.mouseGeoCoord);
      this.snappedPolygonIndex = polygonIndex;
      return true;
    }
    return false;
  }
  hoverEdge(geometry, mouseX, mouseY) {
    const {
      coordinates,
      type
    } = geometry;
    if (type === 'Polygon') {
      for (let polygonIndex = coordinates.length - 1; polygonIndex >= 0; polygonIndex -= 1) {
        const featureCoords = coordinates[polygonIndex];
        if (featureCoords === undefined) {
          // eslint-disable-next-line no-continue
          continue;
        }
        /* Get all vertexes */
        const XYCoords = this.convertGeoCoordsToScreenCoords(featureCoords);
        for (let j = 0; j < XYCoords.length; j += 1) {
          const startV = XYCoords[j];
          const stopV = XYCoords[(j + 1) % XYCoords.length];
          if (this.isBetween(startV, {
            x: mouseX,
            y: mouseY
          }, stopV)) {
            return {
              selectedEdge: j,
              snappedPolygonIndex: polygonIndex
            };
          }
        }
      }
      return {
        selectedEdge: EDGE.NONE,
        snappedPolygonIndex: SNAPPEDFEATURE.NONE
      };
    }
    if (type === 'LineString') {
      const featureCoords = coordinates;
      if (featureCoords === undefined) {
        return {
          selectedEdge: EDGE.NONE,
          snappedPolygonIndex: SNAPPEDFEATURE.NONE
        };
      }
      /* Get all vertexes */
      const XYCoords = this.convertGeoCoordsToScreenCoords(featureCoords);
      for (let j = 0; j < XYCoords.length - 1; j += 1) {
        const startV = XYCoords[j];
        const stopV = XYCoords[(j + 1) % XYCoords.length];
        if (this.isBetween(startV, {
          x: mouseX,
          y: mouseY
        }, stopV)) {
          return {
            selectedEdge: j,
            snappedPolygonIndex: 0
          };
        }
      }
      return {
        selectedEdge: EDGE.NONE,
        snappedPolygonIndex: SNAPPEDFEATURE.NONE
      };
    }
    return {
      selectedEdge: EDGE.NONE,
      snappedPolygonIndex: SNAPPEDFEATURE.NONE
    };
  }
  // eslint-disable-next-line class-methods-use-this
  drawVertice(ctx, _coord, selected, middle, isInEditMode) {
    let w = 7;
    const coord = {
      x: parseInt(_coord.x, 10),
      y: parseInt(_coord.y, 10)
    };
    if (isInEditMode === false) {
      return;
    }
    if (selected === false) {
      if (middle === true) {
        /* Style for middle editable vertice */
        ctx.strokeStyle = '#000';
        ctx.fillStyle = '#D87502';
        ctx.lineWidth = 1.0;
      } else {
        /* Style for standard editable vertice */
        ctx.strokeStyle = '#000';
        ctx.fillStyle = colors.vertice;
        ctx.lineWidth = 1.0;
      }
    } else {
      /* Style for selected editable vertice */
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#FF0';
      ctx.lineWidth = 1.0;
      w = 11;
    }
    ctx.globalAlpha = 1.0;
    ctx.fillRect(coord.x - w / 2, coord.y - w / 2, w, w);
    ctx.strokeRect(coord.x - w / 2, coord.y - w / 2, w, w);
    if (isInEditMode) {
      ctx.strokeRect(coord.x - 0.5, coord.y - 0.5, 1, 1);
    }
  }
  drawPoint(ctx, _coord, selected, middle, isInEditMode, feature, featureIndex) {
    const {
      properties: featureProperties
    } = feature;
    if (featureProperties && featureProperties.imageURL) {
      this.drawIcon(ctx, _coord, featureProperties);
    }
    const drawStyledMarker = featureProperties ? featureProperties.stroke || featureProperties['stroke-width'] || featureProperties.fill : null;
    const drawMarkerByDefault = !featureProperties || !featureProperties.imageURL || !featureProperties.drawFunctionId;
    if (featureProperties.drawFunctionId) {
      const drawFunction = getDrawFunctionFromStore(featureProperties.drawFunctionId);
      if (drawFunction) {
        const isHovered = this.featureEvent && this.featureEvent.feature === feature;
        drawFunction({
          context: ctx,
          featureIndex,
          coord: _coord,
          selected,
          isInEditMode,
          feature,
          mouseX: this.mouseX,
          mouseY: this.mouseY,
          isHovered
        });
      }
    } else if (drawStyledMarker || drawMarkerByDefault) {
      this.drawMarker(ctx, _coord, selected, middle, isInEditMode, featureProperties);
    }
  }
  drawIcon(ctx, _coord, featureProperties) {
    const image = getMapImageStore.getImage(featureProperties.imageURL);
    if (image.isLoaded() === false && image.hasError() === false && image.isLoading() === false) {
      image.load();
      /* After the image is loaded the canvas will be redrawn and drawIcon will be triggered again
      via the beforecanvasdisplay listener. */
      return undefined;
    }
    if (image.isLoaded()) {
      const imageWidth = featureProperties && featureProperties.imageWidth || this.defaultIconProps.imageWidth;
      const imageHeight = featureProperties && featureProperties.imageHeight || this.defaultIconProps.imageHeight;
      ctx.drawImage(image.getElement(), parseInt(_coord.x, 10) - imageWidth / 2, parseInt(_coord.y, 10) - imageHeight / 2, imageWidth, imageHeight);
      if (featureProperties.imageBorderWidth && featureProperties.imageBorderWidth > 0) {
        ctx.lineWidth = featureProperties.imageBorderWidth;
        ctx.strokeRect(parseInt(_coord.x, 10) - imageWidth / 2, parseInt(_coord.y, 10) - imageHeight / 2, imageWidth, imageHeight);
      }
    }
    return undefined;
  }
  drawMarker(ctx, _coord, selected, middle, isInEditMode, featureProperties) {
    const strokeStyle = featureProperties && featureProperties.stroke || this.defaultPolyProps.stroke;
    const lineWidth = featureProperties && featureProperties['stroke-width'] || this.defaultPolyProps['stroke-width'];
    const fillStyle = featureProperties && featureProperties.fill || this.defaultPolyProps.fill;
    if (isInEditMode === false) {
      /* Standard style, no editing, just display location of vertices */
      ctx.strokeStyle = strokeStyle;
      ctx.fillStyle = fillStyle;
      ctx.lineWidth = lineWidth;
    } else if (selected === false) {
      /* Style for standard editable vertice */
      ctx.strokeStyle = '#000';
      ctx.fillStyle = colors.main;
      ctx.lineWidth = 1.0;
    } else {
      /* Style for selected editable vertice */
      ctx.strokeStyle = '#000';
      ctx.fillStyle = '#FF0';
      ctx.lineWidth = 1.0;
    }
    const coord = {
      x: parseInt(_coord.x, 10),
      y: parseInt(_coord.y, 10)
    };
    ctx.globalAlpha = 1.0;
    ctx.beginPath();
    const topRadius = 9;
    const topHeight = 2.5 * topRadius;
    ctx.arc(coord.x, coord.y - topHeight, topRadius, Math.PI, Math.PI * 2);
    ctx.bezierCurveTo(coord.x + topRadius, coord.y - topHeight, coord.x + topRadius / 1.6, coord.y - topRadius, coord.x, coord.y);
    ctx.bezierCurveTo(coord.x, coord.y, coord.x - topRadius / 1.6, coord.y - topRadius, coord.x - topRadius, coord.y - topHeight);
    ctx.stroke();
    ctx.fill();
    /* Fill center circle */
    ctx.fillStyle = '#FFF';
    ctx.beginPath();
    ctx.arc(coord.x, coord.y - topHeight, topRadius / 2, Math.PI * 2, 0);
    ctx.fill();
    /* Fill marker exact location */
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.arc(coord.x, coord.y, 2, Math.PI * 2, 0);
    ctx.fill();
  }
  drawLine(ctx, XYCoords, featureIndex, lineStringIndex) {
    const {
      isInEditMode,
      selectedFeatureIndex
    } = this.props;
    const feature = this.geojson.features[featureIndex];
    if (!feature || !feature.geometry) {
      return;
    }
    if (feature.geometry.type !== 'LineString') {
      return;
    }
    let lineProps = feature.properties;
    if (!lineProps) {
      lineProps = this.defaultLineStringProps;
    }
    /* Draw polygons and calculate center of poly */
    const middle = {
      x: 0,
      y: 0,
      nr: 0
    };
    ctx.beginPath();
    ctx.strokeStyle = lineProps.stroke || this.defaultLineStringProps.stroke;
    ctx.lineWidth = lineProps['stroke-width'] || this.defaultLineStringProps['stroke-width'];
    ctx.fillStyle = lineProps.fill || this.defaultLineStringProps.fill;
    const startCoord = XYCoords[0];
    ctx.moveTo(startCoord.x, startCoord.y);
    middle.x += startCoord.x;
    middle.y += startCoord.y;
    for (let j = 1; j < XYCoords.length; j += 1) {
      const coord = XYCoords[j];
      ctx.lineTo(coord.x, coord.y);
      if (j < XYCoords.length - 1) {
        middle.x += coord.x;
        middle.y += coord.y;
      }
    }
    ctx.globalAlpha = lineProps['fill-opacity'] || this.defaultLineStringProps['fill-opacity'];
    if (lineProps['fill-opacity'] === 0) {
      ctx.globalAlpha = 0;
    }
    // ctx.fill();
    ctx.globalAlpha = lineProps['stroke-opacity'] || this.defaultLineStringProps['stroke-opacity'];
    ctx.stroke();
    // let test = ctx.isPointInPath(this.mouseX, this.mouseY);
    // if (test) {
    this.mouseOverPolygonCoordinates = 0;
    this.mouseOverPolygonFeatureIndex = 0;
    // }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    middle.x = parseInt(middle.x / (XYCoords.length - 1), 10);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    middle.y = parseInt(middle.y / (XYCoords.length - 1), 10);
    if (isInEditMode === true && this.snappedPolygonIndex === lineStringIndex && this.selectedEdge !== EDGE.NONE && selectedFeatureIndex === featureIndex) {
      /* Higlight selected edge of a LineString, previousely detected by mouseover event */
      ctx.beginPath();
      ctx.strokeStyle = '#FF0';
      ctx.lineWidth = 5;
      ctx.moveTo(XYCoords[this.selectedEdge].x, XYCoords[this.selectedEdge].y);
      ctx.lineTo(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      XYCoords[(this.selectedEdge + 1) % XYCoords.length].x,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      XYCoords[(this.selectedEdge + 1) % XYCoords.length].y);
      ctx.stroke();
    }
    middle.nr = XYCoords.length - 1;
    return middle;
  }
  drawPolygon(ctx, XYCoords, featureIndex, polygonIndex) {
    const {
      isInEditMode,
      selectedFeatureIndex
    } = this.props;
    const feature = this.geojson.features[featureIndex];
    if (!feature || !feature.geometry) {
      return;
    }
    if (feature.geometry.type !== 'Polygon' && feature.geometry.type !== 'MultiPolygon') {
      return;
    }
    let polyProps = feature.properties;
    if (!polyProps) {
      polyProps = this.defaultPolyProps;
    }
    /* Draw polygons and calculate center of poly */
    const middle = {
      x: 0,
      y: 0,
      nr: 0
    };
    ctx.beginPath();
    ctx.strokeStyle = polyProps.stroke || this.defaultPolyProps.stroke;
    ctx.lineWidth = polyProps['stroke-width'] || this.defaultPolyProps['stroke-width'];
    ctx.fillStyle = polyProps.fill || this.defaultPolyProps.fill;
    const startCoord = XYCoords[0];
    ctx.moveTo(startCoord.x, startCoord.y);
    middle.x += startCoord.x;
    middle.y += startCoord.y;
    for (let j = 1; j < XYCoords.length; j += 1) {
      const coord = XYCoords[j];
      ctx.lineTo(coord.x, coord.y);
      if (j < XYCoords.length - 1) {
        middle.x += coord.x;
        middle.y += coord.y;
      }
    }
    ctx.closePath();
    ctx.globalAlpha = polyProps['fill-opacity'] || this.defaultPolyProps['fill-opacity'];
    if (polyProps['fill-opacity'] === 0) {
      ctx.globalAlpha = 0;
    }
    ctx.fill();
    ctx.globalAlpha = polyProps['stroke-opacity'] || this.defaultPolyProps['stroke-opacity'];
    ctx.stroke();
    const test = ctx.isPointInPath(this.mouseX, this.mouseY);
    if (test) {
      this.mouseOverPolygonCoordinates = XYCoords;
      this.mouseOverPolygonFeatureIndex = featureIndex;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    middle.x = parseInt(middle.x / (XYCoords.length - 1), 10);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    middle.y = parseInt(middle.y / (XYCoords.length - 1), 10);
    if (isInEditMode === true && this.snappedPolygonIndex === polygonIndex && this.selectedEdge !== EDGE.NONE && selectedFeatureIndex === featureIndex) {
      /* Higlight selected edge of a polygon, previousely detected by mouseover event */
      ctx.strokeStyle = '#FF0';
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(XYCoords[this.selectedEdge].x, XYCoords[this.selectedEdge].y);
      ctx.lineTo(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      XYCoords[(this.selectedEdge + 1) % XYCoords.length].x,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      XYCoords[(this.selectedEdge + 1) % XYCoords.length].y);
      ctx.stroke();
    }
    middle.nr = XYCoords.length - 1;
    return middle;
  }
  convertGeoCoordsToScreenCoords(featureCoords) {
    const {
      mapId
    } = this.props;
    const webmapjs = webmapUtils.getWMJSMapById(mapId);
    const XYCoords = [];
    for (let j = 0; j < featureCoords.length; j += 1) {
      if (featureCoords[j].length < 2) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const coord = webmapjs.getPixelCoordFromLatLong({
        x: featureCoords[j][0],
        y: featureCoords[j][1]
      });
      XYCoords.push(coord);
    }
    return XYCoords;
  }
  initializeFeature(feature, type) {
    if (!feature) {
      switch (type) {
        case DRAWMODE.POINT:
          return cloneDeep(featurePoint);
        case DRAWMODE.MULTIPOINT:
          return cloneDeep(featureMultiPoint);
        case DRAWMODE.POLYGON:
          return cloneDeep(featurePolygon);
        case DRAWMODE.BOX:
          return cloneDeep(featureBox);
        case DRAWMODE.LINESTRING:
          return cloneDeep(lineString);
      }
    } else {
      this.validateFeature(feature);
      // eslint-disable-next-line no-underscore-dangle
      if (feature.properties._type) {
        delete feature.properties._type;
      }
      // eslint-disable-next-line default-case
      switch (type) {
        case DRAWMODE.POINT:
          feature.geometry.type = 'Point';
          break;
        case DRAWMODE.MULTIPOINT:
          feature.geometry.type = 'MultiPoint';
          if (feature.geometry.coordinates.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            feature.geometry.coordinates.push([]);
          }
          break;
        case DRAWMODE.BOX:
          feature.geometry.type = 'Polygon';
          // eslint-disable-next-line no-underscore-dangle
          feature.properties._type = 'box';
          break;
        case DRAWMODE.POLYGON:
          feature.geometry.type = 'Polygon';
          if (feature.geometry.coordinates.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            feature.geometry.coordinates.push([]);
          }
          break;
        case DRAWMODE.LINESTRING:
          feature.geometry.type = 'LineString';
          if (feature.geometry.coordinates.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            feature.geometry.coordinates.push([]);
          }
          break;
      }
    }
    return feature;
  }
  // eslint-disable-next-line class-methods-use-this
  checkIfFeatureIsBox(feature) {
    if (!feature || !feature.properties || !feature.properties._type) {
      return false;
    }
    return feature.properties._type === 'box';
  }
  featureHasChanged(text) {
    const {
      updateGeojson
    } = this.props;
    if (text !== NEW_FEATURE_CREATED) {
      this.validatePolys(text === 'cancelEdit');
    }
    if (updateGeojson && this.geojson) {
      updateGeojson(cloneDeep(this.geojson), text);
    }
  }
  render() {
    return /*#__PURE__*/React.createElement("div", null);
  }
}
// eslint-disable-next-line react/static-property-placement
MapDraw.defaultProps = {
  isInEditMode: false,
  isInDeleteMode: false,
  webmapjs: undefined,
  selectedFeatureIndex: 0
};

const MapDrawContainer = ({
  featureLayers,
  mapId
}) => ( /*#__PURE__*/React.createElement(React.Fragment, null, featureLayers.map(layer => {
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": `featureLayer-${layer.id}`,
    key: layer.id,
    style: {
      display: 'none'
    }
  }, /*#__PURE__*/React.createElement(MapDraw, {
    geojson: layer.geojson,
    isInEditMode: layer.isInEditMode,
    isInDeleteMode: layer.isInDeleteMode,
    drawMode: layer.drawMode,
    mapId: mapId,
    onHoverFeature: hoverInfo => {
      if (layer.onHoverFeature) {
        layer.onHoverFeature(hoverInfo);
      }
    },
    onClickFeature: hoverInfo => {
      if (layer.onClickFeature) {
        layer.onClickFeature(hoverInfo);
      }
    },
    updateGeojson: (geojson, reason) => {
      if (layer.updateGeojson) {
        layer.updateGeojson(geojson, reason);
      }
    },
    exitDrawModeCallback: reason => {
      if (layer.exitDrawModeCallback) {
        layer.exitDrawModeCallback(reason);
      }
    },
    selectedFeatureIndex: parseInt(layer.selectedFeatureIndex || '0', 10)
  }));
})));

const GeoJSONTextField = ({
  onChangeGeoJSON,
  geoJSON,
  title: _title = 'GeoJSON text',
  sx,
  maxRows: _maxRows = 18
}) => {
  const [isValid, setValidity] = useState(true);
  const onChange = event => {
    try {
      onChangeGeoJSON(JSON.parse(event.target.value));
      setValidity(true);
    } catch (e) {
      onChangeGeoJSON(null);
      setValidity(false);
    }
  };
  return /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: 12,
    sx: sx
  }, /*#__PURE__*/React__default.createElement(TextField, {
    sx: {
      padding: 0,
      border: 'none',
      width: '100%',
      height: '100%',
      fontSize: '10px',
      '.MuiInputBase-root': {
        height: '100%',
        alignItems: 'baseline'
      }
    },
    multiline: true,
    error: !isValid,
    maxRows: _maxRows,
    label: !isValid ? 'GeoJSON not valid' : _title,
    variant: "filled",
    onChange: onChange,
    value: JSON.stringify(geoJSON, null, 2),
    disabled: !onChangeGeoJSON
  }));
};

const FeatureLayers = ({
  geojson,
  onChangeLayerIndex,
  activeFeatureLayerIndex
}) => {
  const featureLayerList = geojson ? Array.from(Array(geojson.features.length).keys()).map(index => ({
    key: index,
    value: `feature ${index + 1}: ${geojson.features[index].geometry.type}`
  })) : [];
  return /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    sm: 12
  }, /*#__PURE__*/React__default.createElement(FormControl, {
    disabled: !featureLayerList.length,
    variant: "filled",
    sx: {
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(InputLabel, {
    id: "demo-feature-number"
  }, "Features"), /*#__PURE__*/React__default.createElement(Select, {
    labelId: "demo-feature-type",
    value: (featureLayerList.length ? activeFeatureLayerIndex : '').toString(),
    onChange: event => {
      const featureNr = parseInt(event.target.value, 10);
      onChangeLayerIndex(featureNr);
    }
  }, featureLayerList.map(listItem => {
    return /*#__PURE__*/React__default.createElement(MenuItem, {
      key: listItem.key,
      value: listItem.key
    }, listItem.value);
  }))));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
const EditModeButton = ({
  isInEditMode,
  drawMode,
  onToggleEditMode
}) => {
  return /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React__default.createElement(CustomTooltip, {
    title: "Switch mode"
  }, /*#__PURE__*/React__default.createElement("span", null, /*#__PURE__*/React__default.createElement(FormLabel, null, "View"), /*#__PURE__*/React__default.createElement(Switch, {
    "data-testid": "switchMode",
    checked: isInEditMode,
    onClick: () => onToggleEditMode(!isInEditMode)
  }), /*#__PURE__*/React__default.createElement(FormLabel, null, "Edit"), drawMode && ( /*#__PURE__*/React__default.createElement(Typography, {
    component: "span",
    sx: {
      display: 'inline-block',
      marginLeft: 2,
      top: -1,
      position: 'relative',
      fontSize: 14
    }
  }, "press ESC to exit draw mode")))));
};

const simplePolygonGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      stroke: '#8F8',
      'stroke-width': 4,
      'stroke-opacity': 1,
      fill: '#33ccFF',
      'fill-opacity': 0.5,
      country: 'Netherlands',
      details: [{
        event: 'Gale',
        sent: '2022-04-06T06:42:09+00:00',
        expires: '2022-04-06T14:00:00+00:00',
        area: '',
        web: '',
        feedAddress: 'https://alerts.fmi.fi/cap/feed/rss_en-GB.rss',
        identifier: '1.578.0.220406064209738.1298'
      }]
    },
    geometry: {
      type: 'Polygon',
      coordinates: [[[2.0167417739837967, 51.17995697758499], [4.257084968220221, 49.29134268590647], [8.635937575136854, 51.624654070318876], [8.228602448912055, 53.72352125601693], [6.191926817788037, 53.5423727705048], [2.0167417739837967, 51.17995697758499]]]
    }
  }, {
    type: 'Feature',
    properties: {
      stroke: '#8F8',
      'stroke-width': 4,
      'stroke-opacity': 1,
      fill: '#33FFcc',
      'fill-opacity': 0.5,
      country: 'Finland',
      details: [{
        event: 'Gale',
        sent: '2022-04-06T06:42:09+00:00',
        expires: '2022-04-06T14:00:00+00:00',
        area: 'Helsinki',
        web: '',
        feedAddress: 'https://alerts.fmi.fi/cap/feed/rss_en-GB.rss',
        identifier: '2.49.0.1.578.0.220406064209738'
      }]
    },
    geometry: {
      type: 'Polygon',
      coordinates: [[[26.253181784359604, 68.27806559514066], [20.855991361880957, 59.89443696044347], [29.308195231045634, 60.19950189496858], [33.78888161951847, 69.41694899886294], [28.493524978596028, 70.4304787447596], [26.253181784359604, 68.27806559514066]]]
    }
  }, {
    type: 'Feature',
    properties: {
      stroke: '#8F8',
      'stroke-width': 4,
      'stroke-opacity': 1,
      fill: '#ffcc33',
      'fill-opacity': 0.5,
      country: 'Norway',
      warning: 'Wind',
      details: [{
        event: 'Gale',
        sent: '2022-04-06T06:42:09+00:00',
        expires: '2022-04-06T14:00:00+00:00',
        area: 'Andenes - Hekkingen',
        web: 'https://www.met.no/vaer-og-klima/Ekstremvaervarsler-og-andre-farevarsler',
        feedAddress: 'https://alerts.fmi.fi/cap/feed/rss_en-GB.rss',
        identifier: '2.49.0.1.578.0.220406064209738.1298'
      }]
    },
    geometry: {
      type: 'Polygon',
      coordinates: [[[5.377256565338432, 62.16231256814888], [5.580924128450831, 58.111828399316614], [10.061610516923672, 58.751545357585904], [20.75415758032477, 69.91236644763461], [15.967969847183323, 68.83644729778501], [5.377256565338432, 62.16231256814888]]]
    }
  }]
};
const simplePointsGeojson = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      name: 'First point'
    },
    geometry: {
      type: 'Point',
      coordinates: [3.1640625, 53.12040528310657]
    }
  }, {
    type: 'Feature',
    properties: {
      name: 'Second point'
    },
    geometry: {
      type: 'Point',
      coordinates: [-2.4609375, 48.22467264956519]
    }
  }, {
    type: 'Feature',
    properties: {
      name: 'Third point'
    },
    geometry: {
      type: 'Point',
      coordinates: [5.9765625, 48.922499263758255]
    }
  }, {
    type: 'Feature',
    properties: {
      name: 'Fourth point'
    },
    geometry: {
      type: 'Point',
      coordinates: [1.40625, 48.922499263758255]
    }
  }]
};
const simpleSmallLineStringGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      stroke: '#66F',
      'stroke-width': 5,
      'stroke-opacity': '1'
    },
    geometry: {
      type: 'LineString',
      coordinates: [[13.005656257245073, 53.618161003652624], [8.73272402958337, 48.28535054578368]]
    }
  }]
};
const simpleFlightRoutePointsGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [9.988228, 53.630389]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [9.683522, 52.460214]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [8.570456, 50.033306]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [9.221964, 48.689878]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [11.078008, 49.4987]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [11.786086, 48.353783]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [12.0818, 49.142]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [10.958106, 50.979811]
    }
  }, {
    type: 'Feature',
    properties: {
      fill: 'red',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'Point',
      coordinates: [13.500672, 52.362247]
    }
  }]
};
const simpleLineStringGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [[3.2958984375, 51.31001339554934], [6.30615234375, 52.696361078274485], [8.72314453125, 52.61639023304539]]
    }
  }, {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [[9.228515625, 48.922499263758255], [-4.21875, 47.338822694822], [-1.318359375, 54.521081495443596], [8.61328125, 55.3791104480105]]
    }
  }]
};
const simpleBoxGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      _type: 'box'
    },
    geometry: {
      type: 'Polygon',
      coordinates: [[[3.6764699246101573, 63.564699956881675], [3.6764699246101573, 60.0554711506609], [15.801179052423734, 60.0554711506609], [15.801179052423734, 63.564699956881675], [3.6764699246101573, 63.564699956881675]]]
    }
  }]
};
const simpleBoxGeoJSONWrongOrder = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      _type: 'box'
    },
    geometry: {
      type: 'Polygon',
      coordinates: [[[3.6764699246101573, 60.0554711506609], [15.801179052423734, 60.0554711506609], [15.801179052423734, 63.564699956881675], [3.6764699246101573, 63.564699956881675], [3.6764699246101573, 60.0554711506609]]]
    }
  }]
};
const simpleFlightRouteLineStringGeoJSON = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [[9.988228, 53.630389], [9.683522, 52.460214], [8.570456, 50.033306], [9.221964, 48.689878], [11.078008, 49.4987], [11.786086, 48.353783], [12.0818, 49.142], [10.958106, 50.979811], [13.500672, 52.362247]]
    }
  }]
};
const simpleMultiPolygon = {
  type: 'FeatureCollection',
  features: [{
    type: 'Feature',
    properties: {
      stroke: '#8F8',
      'stroke-width': 4,
      'stroke-opacity': 1,
      fill: '#33ccFF',
      'fill-opacity': 0.5
    },
    geometry: {
      type: 'MultiPolygon',
      coordinates: [[[[4.365729269907419, 55.315807175634454], [3.299427839634416, 55.605958027800156], [2.368817, 55.764314], [4.331914, 59.332644], [4.365729269907419, 55.315807175634454]]], [[[6.500001602574285, 54.735051191917506], [5.526314849017847, 58.0000007017522], [18.500002, 55.000002], [6.500001602574285, 54.735051191917506]]]]
    }
  }]
};
const intersectionFeatureNL = {
  type: 'Feature',
  geometry: {
    type: 'Polygon',
    coordinates: [[[5.0, 55.0], [4.331914, 55.332644], [3.368817, 55.764314], [2.761908, 54.379261], [3.15576, 52.913554], [2.000002, 51.500002], [3.370001, 51.369722], [3.370527, 51.36867], [3.362223, 51.320002], [3.36389, 51.313608], [3.373613, 51.309999], [3.952501, 51.214441], [4.397501, 51.452776], [5.078611, 51.391665], [5.848333, 51.139444], [5.651667, 50.824717], [6.011797, 50.757273], [5.934168, 51.036386], [6.222223, 51.361666], [5.94639, 51.811663], [6.405001, 51.830828], [7.053095, 52.237764], [7.031389, 52.268885], [7.063612, 52.346109], [7.065557, 52.385828], [7.133055, 52.888887], [7.14218, 52.898244], [7.191667, 53.3], [6.5, 53.666667], [6.500002, 55.000002], [5.0, 55.0]]]
  },
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'fir'
  })
};
const intersectionFeatureBE = {
  type: 'Feature',
  geometry: {
    type: 'Polygon',
    coordinates: [[[4.83163899984, 51.4375169997], [4.83971700004, 51.4211280003], [4.78471399965, 51.4327609998], [4.7811030004, 51.4302970004], [4.77944200025, 51.4175190002], [4.80665799987, 51.4100140003], [4.88973599992, 51.4160859998], [4.90944699968, 51.4091530002], [4.97808600027, 51.4238780003], [5.01693300026, 51.4755610004], [5.03137199972, 51.4852999999], [5.04139699977, 51.4866750004], [5.05027800013, 51.4747000003], [5.06139400013, 51.4719500003], [5.10058599984, 51.4399779999], [5.07663599975, 51.3988970003], [5.07864199963, 51.3916780001], [5.12780000027, 51.3497389997], [5.14000300004, 51.3208609998], [5.14945799989, 51.3171939998], [5.178908, 51.3166780003], [5.22806699979, 51.3122110002], [5.22523100031, 51.2688940001], [5.25029999985, 51.2633360001], [5.32971099986, 51.2621920004], [5.34724199982, 51.2694670004], [5.38752200024, 51.2677470002], [5.46779400039, 51.2827610001], [5.517222, 51.2958330004], [5.54250799964, 51.2769170004], [5.55780600005, 51.2639109996], [5.55666699968, 51.2558330003], [5.5572329997, 51.2313669998], [5.5591809996, 51.2244330002], [5.64942199993, 51.2016859998], [5.65555300027, 51.1964140004], [5.71055599976, 51.1822219997], [5.73553599969, 51.1916609998], [5.77696099985, 51.180258], [5.77942500016, 51.1694279997], [5.82806899958, 51.1669640003], [5.85500000045, 51.1510939996], [5.85601899978, 51.1473080004], [5.8472219997, 51.1352779996], [5.81695299958, 51.1225030004], [5.84056100009, 51.1058309996], [5.80641099997, 51.0958610003], [5.80280299996, 51.093339], [5.80222800042, 51.0616560003], [5.7752999997, 51.0597080004], [5.77140299985, 51.0475029997], [5.76332499965, 51.0394250004], [5.76303899995, 51.0352999999], [5.7747280003, 51.028594], [5.77696099985, 51.0258470001], [5.76418599978, 50.9899779999], [5.74802800019, 50.9819579997], [5.72276099962, 50.9652829996], [5.72390600026, 50.9577779998], [5.72831700043, 50.9561169996], [5.74418900032, 50.9577779998], [5.72974999996, 50.9141749999], [5.7150249999, 50.9105669999], [5.68406700039, 50.8831669999], [5.66555600021, 50.8769439998], [5.65331699968, 50.8783079997], [5.64776099977, 50.8677670001], [5.63750299976, 50.8502920001], [5.63945299976, 50.8430719999], [5.6472220001, 50.8324999998], [5.70001400015, 50.8069169999], [5.70052799957, 50.8033079999], [5.69806400015, 50.7999859996], [5.69694399977, 50.7897220002], [5.6933110004, 50.762514], [5.69308099969, 50.7591919997], [5.73719699984, 50.7594190003], [5.76195000009, 50.7763810001], [5.7747280003, 50.7816500002], [5.77862200001, 50.7794169998], [5.77999699956, 50.7758639996], [5.79747499977, 50.7694469996], [5.80417800041, 50.7647499997], [5.83888900033, 50.7616669996], [5.84720599986, 50.7669250002], [5.85471099971, 50.7591919997], [5.88307199997, 50.7664109999], [5.89250000037, 50.7544440002], [5.93251899955, 50.756956], [5.99888899983, 50.7538889998], [6.01163300026, 50.7566690003], [6.01611400006, 50.765264], [6.06666700028, 50.7166670001], [6.11666700018, 50.7333329997], [6.16666700008, 50.6666670002], [6.2500000002, 50.6499999996], [6.28333300042, 50.6166670003], [6.2500000002, 50.5999999997], [6.2500000002, 50.583333], [6.2000000003, 50.5499999998], [6.1986110001, 50.5194439999], [6.2500000002, 50.4833330002], [6.35, 50.4666669997], [6.37388899998, 50.4349999999], [6.38333300022, 50.4333330003], [6.40833300017, 50.3333329996], [6.3000000001, 50.316667], [6.29916700036, 50.303056], [6.23166700013, 50.2388889997], [6.1500000004, 50.1499999997], [6.13301100026, 50.1268060003], [6.11655799965, 50.0714689998], [6.12647199998, 50.0711830001], [6.11322499973, 50.0586359998], [6.13316099998, 50.0435579996], [6.14093299956, 49.9935890002], [6.16349999973, 49.9814360001], [6.15971699974, 49.9746389996], [6.17998600005, 49.9583389997], [6.18282499967, 49.9675310001], [6.18998899999, 49.9667970004], [6.21388899958, 49.955556], [6.22060299983, 49.9355170004], [6.22500000025, 49.9083329996], [6.23418300015, 49.897519], [6.30860799956, 49.8644609999], [6.32110000006, 49.8483030003], [6.32195800006, 49.8399940002], [6.34109700043, 49.8475000001], [6.36917200009, 49.8419420001], [6.4435970004, 49.8094560003], [6.50307200017, 49.8072219998], [6.51304200039, 49.8041860001], [6.5222080004, 49.8111170004], [6.52140600029, 49.7974810002], [6.50668100023, 49.7863670003], [6.51751099963, 49.7580609999], [6.49997799959, 49.7419610003], [6.5166500004, 49.7194439997], [6.49779999987, 49.7203029998], [6.50141100002, 49.7060940001], [6.46111099957, 49.6916670003], [6.42085300017, 49.6658169999], [6.42056400033, 49.6602579998], [6.43805600028, 49.6541670004], [6.42499999985, 49.638889], [6.42499999985, 49.6333330001], [6.37944399984, 49.5969440002], [6.3719220001, 49.5872059999], [6.3750139997, 49.5822220002], [6.36137800039, 49.5752889998], [6.37335300044, 49.549161], [6.36779699963, 49.5422280004], [6.36166700023, 49.4543310003], [6.2805329999, 49.4930689997], [6.21666699998, 49.5000000001], [6.15304999986, 49.5025220004], [6.15969699972, 49.4933560004], [6.12251400002, 49.4839029997], [6.11804199974, 49.469692], [6.09054199961, 49.4530749999], [6.06916899965, 49.4633329999], [6.04321399973, 49.4534189996], [5.9999999998, 49.4500000002], [5.95833300017, 49.4833330004], [5.8511029997, 49.5052720004], [5.80858100021, 49.5446939999], [5.75833299967, 49.5361109998], [5.48333300022, 49.5000000001], [5.4000000001, 49.6083330002], [5.31666699998, 49.6166669996], [5.3000000003, 49.6666670004], [5.17499999965, 49.6916670003], [5.06666699958, 49.7583329999], [5.0, 49.7833329998], [4.8500000003, 49.7833329998], [4.85833299967, 49.9166669999], [4.8000000004, 49.9500000001], [4.83888899963, 50.0472220004], [4.87222199985, 50.088889], [4.86749999973, 50.1152780002], [4.87022799962, 50.1267969999], [4.89647500042, 50.1402000001], [4.88140799988, 50.1486169998], [4.87752199964, 50.1560970003], [4.85856400043, 50.1539169996], [4.83766699993, 50.1585920002], [4.82454400001, 50.1676309998], [4.81676900029, 50.1657609999], [4.80753599989, 50.1536059996], [4.76428099987, 50.1377080003], [4.7499999996, 50.1180559997], [4.74027800003, 50.1083330001], [4.73317799996, 50.1093419998], [4.71540300045, 50.0977579998], [4.68333300002, 50.0], [4.48333300042, 49.9333330004], [4.18333300012, 49.9500000001], [4.12499999995, 50.0166669997], [4.14888899993, 50.0347220004], [4.1999999998, 50.0999999998], [4.11666699968, 50.1499999997], [4.16666699958, 50.2500000004], [4.17222200035, 50.2833329997], [4.01666699988, 50.3500000002], [3.9000000004, 50.316667], [3.79166700033, 50.3333329996], [3.7099999997, 50.3083329997], [3.6599999998, 50.3719440004], [3.61666699978, 50.4833330002], [3.40499999995, 50.4633329997], [3.27666700028, 50.5300000002], [3.22499999995, 50.6783330004], [3.11666699988, 50.7833329996], [2.8999999997, 50.7000000004], [2.81666699958, 50.7250000003], [2.71666699978, 50.8000000002], [2.63333299962, 50.8166669999], [2.5500000004, 51.0833329999], [1.9999999997, 51.1166670002], [1.9999999997, 51.4999999997], [3.0000000004, 51.4086179998], [3.10027800043, 51.3994440003], [3.36388899988, 51.3736109997], [3.38724200032, 51.3413280003], [3.38377200038, 51.3313580001], [3.36248900007, 51.3184579996], [3.37401700018, 51.3050059998], [3.36470599978, 51.3019560004], [3.36492800013, 51.2987639998], [3.3813890002, 51.2836109997], [3.42589999969, 51.2589609998], [3.43277200003, 51.2486970004], [3.45504400008, 51.2451440002], [3.49817799991, 51.2463390004], [3.53010600009, 51.2507780001], [3.52079199955, 51.2930780001], [3.52611400036, 51.2955750002], [3.54363099967, 51.2896110003], [3.56447200028, 51.2968220001], [3.58376100037, 51.2897499996], [3.58575599974, 51.3012609998], [3.59285000043, 51.3063919999], [3.6037139996, 51.3037579998], [3.64140600029, 51.2876689998], [3.65204699997, 51.2872530004], [3.65847799976, 51.2835080004], [3.69151399977, 51.2815669999], [3.71390800005, 51.2744940002], [3.73109999967, 51.2727249996], [3.75780600005, 51.2722750004], [3.76866900007, 51.2646470003], [3.77842500031, 51.2670030001], [3.79084200006, 51.2625669997], [3.79061899967, 51.2514719997], [3.79039700022, 51.2199890003], [3.80370000035, 51.2145779999], [3.84560600022, 51.2120829999], [3.86400799987, 51.2141639997], [3.87886100043, 51.2273390002], [3.88846099978, 51.2265610003], [3.88839700042, 51.217214], [3.88462800018, 51.2088920003], [3.91278600007, 51.209725], [3.92298299997, 51.220958], [3.9300779998, 51.2226220003], [3.93916899995, 51.2165219996], [3.94870299984, 51.215828], [3.97109700013, 51.2215139996], [4.10833300027, 51.2708330004], [4.25143300013, 51.3750060002], [4.33391100039, 51.3766670004], [4.34307800045, 51.3647500003], [4.3469750003, 51.3625169999], [4.41859199972, 51.3591360004], [4.43165799972, 51.3644639997], [4.42805599998, 51.3744440004], [4.3966499996, 51.4169469999], [4.40942499966, 51.4375169997], [4.40805000011, 51.440839], [4.39389999959, 51.4502919998], [4.46528900019, 51.4716639997], [4.4986109999, 51.4788890001], [4.51250000007, 51.4805559997], [4.54556100034, 51.4850140002], [4.54498900004, 51.4627830002], [4.53805599958, 51.4524690003], [4.53530599957, 51.4397500002], [4.62361099965, 51.4272219998], [4.67166899992, 51.4274890004], [4.67304400038, 51.4308109998], [4.66863300021, 51.440839], [4.68415800029, 51.4494329997], [4.69859699975, 51.4644440004], [4.76918600033, 51.502775], [4.80833299977, 51.4999999997], [4.8408059999, 51.4830670003], [4.84470299974, 51.4614079998], [4.83696700003, 51.4569389996], [4.83163899984, 51.4375169997]]]
  },
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'fir-be'
  })
};

const drawPolyStoryStyles = {
  MapDrawGeoJSONContainer: {
    display: 'grid',
    gridTemplateColumns: '70% 30%',
    gridTemplateRows: '150px 1fr',
    gridTemplateAreas: "'map controls'\n'map textarea'",
    width: '100%',
    height: '100vh'
  },
  MapDrawGeoJSONMapContainer: {
    gridArea: 'map',
    width: '100%',
    height: '100%'
  },
  MapDrawGeoJSONControlsContainer: {
    gridArea: 'controls',
    width: '100%',
    height: '100%'
  },
  MapDrawGeoJSONTextAreaContainer: {
    gridArea: 'textarea',
    width: '100%',
    height: '100%'
  },
  MapDrawGeoJSONTextArea: {
    padding: 0,
    border: 'none',
    width: '100%',
    height: '100%',
    fontSize: '10px'
  }
};
const useGeoJSON = () => {
  const editModes = [{
    key: 'POLYGON',
    value: 'Polygon'
  }, {
    key: 'POINT',
    value: 'Point'
  }, {
    key: 'BOX',
    value: 'Box'
  }, {
    key: 'LINESTRING',
    value: 'LineString'
  }];
  const [isInEditMode, setEditMode] = useState(false);
  const [geojson, setGeojson] = useState(lineString);
  const [geojsonText, setGeojsonText] = useState('');
  const [drawMode, setDrawMode] = useState('LINESTRING');
  const [currentFeatureNrToEdit, setCurrentFeatureNrToEdit] = useState(0);
  const changeDrawMode = selectedKey => {
    setDrawMode(selectedKey);
    if (selectedKey === 'POLYGON') {
      setGeojson(simplePolygonGeoJSON);
      setGeojsonText(JSON.stringify(simplePolygonGeoJSON, null, 2));
    }
    if (selectedKey === 'BOX') {
      setGeojson(simplePolygonGeoJSON);
      setGeojsonText(JSON.stringify(simpleBoxGeoJSON, null, 2));
    }
    if (selectedKey === 'LINESTRING') {
      setGeojson(lineString);
      setGeojsonText(JSON.stringify(lineString, null, 2));
    }
    if (selectedKey === 'POINT' || selectedKey === 'MULTIPOINT') {
      setGeojson(simplePointsGeojson);
      setGeojsonText(JSON.stringify(simplePointsGeojson, null, 2));
    }
  };
  return {
    editModes,
    isInEditMode,
    geojson,
    setGeojson,
    geojsonText,
    setGeojsonText,
    drawMode,
    currentFeatureNrToEdit,
    setCurrentFeatureNrToEdit,
    changeDrawMode,
    setEditMode
  };
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
const StoryLayoutGrid = ({
  mapComponent,
  children
}) => {
  return /*#__PURE__*/React__default.createElement(Paper, null, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'grid',
      gridTemplateColumns: '70% 30%',
      gridTemplateRows: '150px 1fr',
      gridTemplateAreas: "'map controls'\n'map textarea'",
      width: '100%',
      height: '100vh'
    }
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      gridArea: 'map',
      width: '100%',
      height: '100%'
    }
  }, mapComponent), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      gridArea: 'controls',
      width: '100%',
      height: '100%',
      padding: 2
    }
  }, /*#__PURE__*/React__default.createElement(Grid, {
    spacing: 2,
    container: true
  }, children))));
};

const SelectField = ({
  label,
  value,
  onChangeValue,
  options: _options = [],
  width: _width = '50%',
  showValueAsColor: _showValueAsColor = false,
  labelSuffix: _labelSuffix = ''
}) => {
  const onChange = event => {
    const {
      value
    } = event.target;
    onChangeValue(value);
  };
  const getLabel = currentValue => `${currentValue}${_labelSuffix}`;
  return /*#__PURE__*/React__default.createElement(FormControl, {
    variant: "filled",
    sx: {
      width: _width
    }
  }, /*#__PURE__*/React__default.createElement(InputLabel, null, label), /*#__PURE__*/React__default.createElement(Select, {
    value: value,
    onChange: onChange
  }, _options.map(value => ( /*#__PURE__*/React__default.createElement(MenuItem, {
    value: value,
    key: value
  }, _showValueAsColor ? ( /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      backgroundColor: value,
      width: 20,
      height: 20
    }
  })) : getLabel(value.toString()))))));
};

const IntersectionSelect = ({
  intersections,
  onChangeIntersection,
  isDisabled: _isDisabled = false
}) => {
  const [activeIntersection, setActiveIntersection] = React__default.useState(0);
  return /*#__PURE__*/React__default.createElement(Grid, {
    item: true,
    sm: 12
  }, /*#__PURE__*/React__default.createElement(FormControl, {
    variant: "filled",
    sx: {
      width: '100%'
    }
  }, /*#__PURE__*/React__default.createElement(InputLabel, {
    id: "demo-intersection"
  }, "Intersection shapes"), /*#__PURE__*/React__default.createElement(Select, {
    labelId: "demo-intersection",
    value: activeIntersection.toString(),
    onChange: event => {
      const featureIndex = parseInt(event.target.value, 10);
      setActiveIntersection(featureIndex);
      const {
        geojson,
        title
      } = intersections[featureIndex];
      onChangeIntersection(geojson, title);
    },
    disabled: _isDisabled
  }, intersections.map((listItem, index) => {
    return /*#__PURE__*/React__default.createElement(MenuItem, {
      key: listItem.title,
      value: index
    }, listItem.title);
  }))));
};

/**
 * Adds properties to the first geojson feature based on the given property object.
 * It only extends or changes the properties which are defined in styleConfig,
 * all other properties in the geojson are left unchanged.
 * @param geojson
 * @param featureProperties
 */
const addFeatureProperties = (geojson, featureProperties, featureIndex = 0) => {
  if (!geojson) {
    return null;
  }
  return produce(geojson, draft => {
    if (draft.features && draft.features.length > 0 && draft.features[featureIndex] !== undefined && draft.features[featureIndex].properties) {
      Object.keys(featureProperties).forEach(key => {
        draft.features[featureIndex].properties[key] = featureProperties[key];
      });
    }
  });
};
const getGeoJSONPropertyValue = (property, properties, polygonDrawMode, defaultProperties = defaultGeoJSONStyleProperties) => {
  // if a shape is set, extract the style from there
  if (properties[property] !== undefined) {
    return properties[property];
  }
  // if active polygon tool is preset, retreive style from there
  if (polygonDrawMode) {
    const polygonDrawModeProperty = polygonDrawMode.shape.type === 'Feature' && polygonDrawMode.shape.properties[property];
    if (polygonDrawModeProperty !== undefined) {
      return polygonDrawModeProperty;
    }
  }
  // otherwise get values from defaultStyle
  return defaultProperties[property];
};
/**
 * moves geoJSON feature as new feature. Mutates newGeoJSON
 * @constructor
 * @param {GeoJSON.FeatureCollection} currentGeoJSON - current geoJSON
 * @param {GeoJSON.FeatureCollection} newGeoJSON - new geoJSON
 * @param {number} featureLayerIndex - feature layer index
 * @param {string} text - reason of change
 */
const moveFeature = (currentGeoJSON, newGeoJSON, featureLayerIndex, reason) => {
  const feature = newGeoJSON.features[featureLayerIndex];
  const currentFeature = currentGeoJSON.features[featureLayerIndex];
  if (feature) {
    const {
      geometry
    } = feature;
    if (geometry.type === 'Polygon' && geometry.coordinates.length > 1 && reason === NEW_FEATURE_CREATED) {
      const lastCoordinate = geometry.coordinates.pop();
      const copyFeature = Object.assign(Object.assign({}, feature), {
        geometry: Object.assign(Object.assign({}, geometry), {
          coordinates: [lastCoordinate]
        })
      });
      newGeoJSON.features.push(copyFeature);
      return newGeoJSON.features.length - 1;
    }
    if (geometry.type === 'LineString' && geometry.coordinates.length > 2 && reason === NEW_LINESTRING_CREATED) {
      const lastCoordinate = geometry.coordinates.pop();
      const copyFeature = Object.assign(Object.assign({}, feature), {
        geometry: Object.assign(Object.assign({}, geometry), {
          coordinates: [lastCoordinate, lastCoordinate]
        })
      });
      newGeoJSON.features.push(copyFeature);
      return newGeoJSON.features.length - 1;
    }
    if (geometry.type === 'Point' && currentFeature && currentFeature.geometry.type === 'Point' && currentFeature.geometry.coordinates.length > 0 && reason === NEW_POINT_CREATED) {
      newGeoJSON.features.push(currentFeature);
      return newGeoJSON.features.length - 1;
    }
  }
  return undefined;
};
/**
 * Returns the intersection of two features. In case of a polygon, only the first feature is used.
 * @param a Feature A
 * @param b Feature B
 * @returns The intersection of the two features.
 */
const intersectGeoJSONS = (a, b, geoJSONProperties = {
  stroke: '#FF0000',
  'stroke-width': 10.0,
  'stroke-opacity': 1,
  fill: '#0000FF',
  'fill-opacity': 1.0
}) => {
  const featureA = turf.feature(a.features[0].geometry);
  const featureB = turf.feature(b.features[0].geometry);
  const options = {
    tolerance: 0.001,
    highQuality: true
  };
  const simplifiedA = turf.simplify(featureA, options);
  const simplifiedB = turf.simplify(featureB, options);
  const intersection = turf.intersect(simplifiedA, simplifiedB);
  return addFeatureProperties({
    type: 'FeatureCollection',
    features: intersection === null ? [{
      type: 'Feature',
      properties: {
        selectionType: 'poly'
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[]]
      }
    }] : [intersection]
  }, geoJSONProperties);
};
/**
 * Adds the intersectionStart and intersectionEnd properties to the GeoJSONS structure
 * @param geoJSONs
 * @returns GeoJSONS extend with intersections
 */
const createInterSections = (geojson, otherGeoJSON, geoJSONproperties = {
  stroke: '#f24a00',
  'stroke-width': 1.5,
  'stroke-opacity': 1,
  fill: '#f24a00',
  'fill-opacity': 0.5
}) => {
  const intersections = produce(geojson, () => {
    try {
      return addFeatureProperties(intersectGeoJSONS(geojson, otherGeoJSON), geoJSONproperties);
    } catch (error) {
      return addFeatureProperties(geojson, geoJSONproperties);
    }
  });
  return intersections;
};
const getGeoJson = (geojson, shouldAllowMultipleShapes) => {
  if (shouldAllowMultipleShapes || !geojson.features.length) {
    return geojson;
  }
  const {
    geometry
  } = geojson.features[0];
  if (geometry.type === 'Point' || geometry.type === 'LineString') {
    return geojson;
  }
  return geometry.coordinates.length > 1 ? produce(geojson, draft => {
    const draftFeature = draft.features[0];
    draftFeature.geometry.coordinates = [draftFeature.geometry.coordinates[1]];
  }) : geojson;
};
const getFeatureCollection = (geoJSONFeature, shouldAllowMultipleShapes, geoJSONFeatureCollection = emptyGeoJSON) => {
  if (geoJSONFeature.type === 'FeatureCollection') {
    return geoJSONFeature;
  }
  if (shouldAllowMultipleShapes) {
    return Object.assign(Object.assign({}, geoJSONFeatureCollection), {
      features: geoJSONFeatureCollection.features.concat(geoJSONFeature)
    });
  }
  return Object.assign(Object.assign({}, geoJSONFeatureCollection), {
    features: [geoJSONFeature]
  });
};
// compares 2 geoJSONs and returns true if both have properties.selectionType with the same value
const isGeoJSONFeatureCreatedByTool = (existingJSON, newGeoJSON) => {
  var _a, _b, _c;
  if (!existingJSON.features.length) {
    return false;
  }
  const lastUsedTool = (_a = existingJSON.features[0].properties) === null || _a === void 0 ? void 0 : _a.selectionType;
  const newTool = newGeoJSON.type === 'Feature' ? (_b = newGeoJSON.properties) === null || _b === void 0 ? void 0 : _b.selectionType : (_c = newGeoJSON.features[0].properties) === null || _c === void 0 ? void 0 : _c.selectionType;
  return lastUsedTool !== undefined && newTool !== undefined ? lastUsedTool === newTool : false;
};
const rewindGeometry = geoJSON => {
  return produce(geoJSON, geoJSONDraft => {
    if (geoJSONDraft && geoJSONDraft.features.length > 0 && geoJSONDraft.features[0].geometry.type === 'Polygon' && geoJSONDraft.features[0].geometry.coordinates[0].length > 1) {
      const {
        geometry
      } = geoJSON.features[0];
      const lineString = polygonToLineString(geometry);
      if (booleanClockwise(lineString.geometry.coordinates)) {
        // eslint-disable-next-line no-param-reassign
        geoJSONDraft.features[0].geometry = rewind(geoJSONDraft.features[0].geometry);
      }
    }
  });
};

const defaultIntersectionStyleProperties = Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
  'fill-opacity': 0.5
});
const emptyLineString = Object.assign(Object.assign({}, lineString.features[0]), {
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'linestring'
  })
});
const emptyPoint = Object.assign(Object.assign({}, featurePoint), {
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'point'
  })
});
const emptyPolygon = Object.assign(Object.assign({}, featurePolygon), {
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'poly'
  })
});
const emptyBox = Object.assign(Object.assign({}, featurePolygon), {
  properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
    selectionType: 'box'
  })
});
// TODO: improve this: without a feature, the custom shape does not work on first click
const emptyIntersectionShape = Object.assign(Object.assign({}, emptyGeoJSON), {
  features: [featurePolygon]
});
const getIcon = drawModeId => {
  switch (drawModeId) {
    case 'drawtools-point':
      return /*#__PURE__*/React__default.createElement(Location, null);
    case 'drawtools-polygon':
      return /*#__PURE__*/React__default.createElement(DrawPolygon, null);
    case 'drawtools-box':
      return /*#__PURE__*/React__default.createElement(DrawRegion, null);
    case 'drawtools-linestring':
      return /*#__PURE__*/React__default.createElement(Edit, null);
    case 'drawtools-delete':
      return /*#__PURE__*/React__default.createElement(Delete, null);
    default:
      return null;
  }
};
const defaultPoint = {
  drawModeId: 'drawtools-point',
  value: DRAWMODE.POINT,
  title: 'Point',
  shape: emptyPoint,
  isSelectable: true
};
const defaultPolygon = {
  drawModeId: 'drawtools-polygon',
  value: DRAWMODE.POLYGON,
  title: 'Polygon',
  shape: emptyPolygon,
  isSelectable: true
};
const defaultBox = {
  drawModeId: 'drawtools-box',
  value: DRAWMODE.BOX,
  title: 'Box',
  shape: emptyBox,
  isSelectable: true
};
const defaultLineString = {
  drawModeId: 'drawtools-linestring',
  value: DRAWMODE.LINESTRING,
  title: 'LineString',
  shape: emptyLineString,
  isSelectable: true
};
const defaultDelete = {
  drawModeId: 'drawtools-delete',
  value: 'DELETE',
  title: 'Delete',
  shape: emptyGeoJSON,
  isSelectable: false
};
const defaultModes = [defaultPoint, defaultPolygon, defaultBox, defaultLineString, defaultDelete];
const currentlySupportedDrawModes = [defaultPolygon, defaultDelete];
const useMapDrawTool = ({
  defaultDrawModes: _defaultDrawModes = defaultModes,
  shouldAllowMultipleShapes: _shouldAllowMultipleShapes = false,
  defaultGeoJSON: _defaultGeoJSON = emptyGeoJSON,
  defaultGeoJSONIntersection: _defaultGeoJSONIntersection = emptyIntersectionShape,
  defaultGeoJSONIntersectionBounds,
  defaultGeoJSONIntersectionProperties: _defaultGeoJSONIntersectionProperties = defaultIntersectionStyleProperties,
  geoJSONLayerId: _geoJSONLayerId = 'draw-layer',
  geoJSONIntersectionLayerId: _geoJSONIntersectionLayerId = 'intersection-layer',
  geoJSONIntersectionBoundsLayerId: _geoJSONIntersectionBoundsLayerId = 'static-layer'
}) => {
  // geoJSON feature collections
  const [geoJSON, setGeoJSON] = React__default.useState(_defaultGeoJSON);
  const [geoJSONIntersection, setGeoJSONIntersection] = React__default.useState(_defaultGeoJSONIntersection);
  const [geoJSONIntersectionBounds, setGeoJSONIntersectionBounds] = React__default.useState(defaultGeoJSONIntersectionBounds);
  // state
  const [drawModes, setDrawModes] = React__default.useState(_defaultDrawModes);
  const [activeTool, setActiveTool] = React__default.useState('');
  const [drawMode, setDrawMode] = React__default.useState('');
  const [isInEditMode, setEditMode] = React__default.useState(false);
  const [featureLayerIndex, setFeatureLayerIndex] = React__default.useState(0);
  const changeProperties = styleProperties => {
    // update all modes with new properties
    const newModes = drawModes.map(mode => {
      const shape = mode.shape.type === 'Feature' ? Object.assign(Object.assign({}, mode.shape), {
        properties: Object.assign(Object.assign({}, mode.shape.properties), styleProperties)
      }) : addFeatureProperties(mode.shape, styleProperties);
      return Object.assign(Object.assign({}, mode), {
        shape
      });
    });
    setDrawModes(newModes);
    // update current geoJSON with new properties
    const updateGeoJSON = addFeatureProperties(geoJSON, styleProperties, featureLayerIndex);
    setGeoJSON(updateGeoJSON);
  };
  const getProperties = () => {
    if (!geoJSON || !geoJSON.features.length) {
      return {};
    }
    return geoJSON.features[featureLayerIndex].properties;
  };
  const changeActiveTool = newMode => {
    const shouldDeleteShape = newMode.value === 'DELETE';
    // reset if same tool is selected
    if (newMode.drawModeId === activeTool || shouldDeleteShape) {
      reset(shouldDeleteShape);
      return;
    }
    setActiveTool(newMode.isSelectable ? newMode.drawModeId : '');
    // updates shape
    const isNewSelectedTool = !isGeoJSONFeatureCreatedByTool(geoJSON, newMode.shape);
    const shouldUpdateShape = !geoJSON.features.length || _shouldAllowMultipleShapes || isNewSelectedTool;
    if (shouldUpdateShape) {
      const updatedGeoJSON = changeGeoJSON(newMode.shape);
      setFeatureLayerIndex(updatedGeoJSON.features.length - 1);
    }
    // handle modes and update feature layer index
    setDrawMode(newMode.value);
    setEditMode(!!newMode.value && newMode.isSelectable);
  };
  const changeGeoJSON = (updatedGeoJSON, reason = '') => {
    const geoJSONFeatureCollection = getFeatureCollection(updatedGeoJSON, _shouldAllowMultipleShapes, geoJSON);
    const newGeoJSON = getGeoJson(geoJSONFeatureCollection, _shouldAllowMultipleShapes);
    if (_shouldAllowMultipleShapes) {
      const newFeatureIndex = moveFeature(geoJSON, newGeoJSON, featureLayerIndex, reason);
      if (newFeatureIndex !== undefined) {
        setFeatureLayerIndex(newFeatureIndex);
      }
    }
    setGeoJSON(newGeoJSON);
    if (geoJSONIntersectionBounds) {
      setGeoJSONIntersection(createInterSections(newGeoJSON, geoJSONIntersectionBounds, _defaultGeoJSONIntersectionProperties));
    }
    return newGeoJSON;
  };
  const onSetGeoJSONIntersectionBounds = newGeoJSON => {
    setGeoJSONIntersectionBounds(newGeoJSON);
    // reset all other geoJSONs
    setGeoJSONIntersection(emptyIntersectionShape);
    setGeoJSON(emptyGeoJSON);
  };
  const onSetDrawMode = drawMode => {
    setDrawMode(drawMode);
    const newActiveTool = drawModes.find(mode => mode.value === drawMode);
    if (newActiveTool) {
      setActiveTool(newActiveTool.drawModeId);
    }
  };
  const deactivateTool = () => {
    setEditMode(false);
    setActiveTool('');
    setDrawMode('');
  };
  const reset = (shouldClearState = false) => {
    deactivateTool();
    if (shouldClearState) {
      setGeoJSONIntersection(emptyIntersectionShape);
      setGeoJSON(emptyGeoJSON);
      setFeatureLayerIndex(0);
    }
  };
  const getLayer = (layerType, layerId) => {
    // geoJSON
    if (layerType === 'geoJSON') {
      return {
        id: layerId,
        geojson: geoJSON,
        isInEditMode,
        drawMode,
        updateGeojson: changeGeoJSON,
        selectedFeatureIndex: featureLayerIndex
      };
    }
    // intersections
    return {
      id: layerId,
      geojson: layerType === 'geoJSONIntersection' ? geoJSONIntersection : geoJSONIntersectionBounds,
      isInEditMode: false
    };
  };
  const layers = [...(geoJSONIntersectionBounds ? [getLayer('geoJSONIntersectionBounds', _geoJSONIntersectionBoundsLayerId), getLayer('geoJSONIntersection', _geoJSONIntersectionLayerId)] : []), getLayer('geoJSON', _geoJSONLayerId)];
  return {
    geoJSON,
    geoJSONIntersection,
    geoJSONIntersectionBounds,
    setGeoJSON: changeGeoJSON,
    setGeoJSONIntersectionBounds: onSetGeoJSONIntersectionBounds,
    drawModes,
    isInEditMode,
    changeDrawMode: onSetDrawMode,
    setEditMode,
    featureLayerIndex,
    setFeatureLayerIndex,
    activeTool,
    changeActiveTool,
    setActiveTool,
    layers,
    getLayer,
    deactivateTool,
    setDrawModes,
    changeProperties,
    getProperties
  };
};

const opacityOptions = [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0];
const fillOptions = [defaultGeoJSONStyleProperties.fill, '#6e1e91', '#FF00FF', '#33ccFF', '#8F8'];
const strokeWidthOptions = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
const getToolIcon = id => {
  const defaultIcon = getIcon(id);
  if (defaultIcon) {
    return defaultIcon;
  }
  if (id === 'tool-fir') {
    return /*#__PURE__*/React__default.createElement(DrawFIRLand, null);
  }
  if (id === 'tool-custom-line') {
    return /*#__PURE__*/React__default.createElement(ArrowUp, null);
  }
  return /*#__PURE__*/React__default.createElement(DrawFIRLand, null);
};
// custom buttons
const shapeButtonNL$2 = {
  drawModeId: 'tool-fir',
  value: DRAWMODE.POLYGON,
  title: 'Custom FIR NL polygon',
  shape: intersectionFeatureNL,
  isSelectable: false
};
const customLineButton = {
  drawModeId: 'tool-custom-line',
  value: DRAWMODE.LINESTRING,
  title: 'Custom LineString',
  shape: {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [[4.136829504703722, 50.944730810381465], [4.450134704406403, 53.81224530783831], [2.5703035061903217, 53.78911570560625], [5.252979278644519, 56.722490281934554], [8.758081200318252, 53.85846624679025], [6.290802752659646, 53.84691579421783], [6.623689527343743, 51.0556380592049], [4.136829504703722, 50.95706693142843]]
    },
    properties: defaultGeoJSONStyleProperties
  },
  isSelectable: false
};
const basicExampleDrawOptions = {
  defaultDrawModes: [...defaultModes, shapeButtonNL$2, customLineButton],
  defaultGeoJSON: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, defaultGeoJSONStyleProperties), {
        selectionType: 'box'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[9.451665078283622, 53.21804334226515], [4.734608638338736, 53.21804334226515], [4.734608638338736, 54.57650543915101], [9.451665078283622, 54.57650543915101], [9.451665078283622, 53.21804334226515]]]
      }
    }]
  }
};
const basicExampleMultipleShapeDrawOptions = {
  shouldAllowMultipleShapes: true,
  // add multiple tools, and update all with custom color
  defaultDrawModes: [...defaultModes, shapeButtonNL$2, customLineButton].map(mode => Object.assign(Object.assign({}, mode), {
    shape: Object.assign(Object.assign({}, mode.shape), {
      properties: Object.assign(Object.assign({}, mode.shape.properties), {
        stroke: '#8F8',
        'stroke-width': 4,
        'stroke-opacity': 1,
        fill: '#33ccFF',
        'fill-opacity': 0.5
      })
    })
  }))
};

const getIntersectionToolIcon = id => {
  const defaultIcon = getIcon(id);
  if (defaultIcon) {
    return defaultIcon;
  }
  if (id === 'fir-nl') {
    return /*#__PURE__*/React__default.createElement(DrawFIRLand, null);
  }
  if (id === 'fir-be') {
    return /*#__PURE__*/React__default.createElement(DrawFIRLand, {
      sx: {
        transform: `scaleY(-1)`
      }
    });
  }
  return /*#__PURE__*/React__default.createElement(DrawFIRLand, null);
};
// custom buttons
const shapeButtonNL$1 = {
  drawModeId: 'tool-fir',
  value: DRAWMODE.POLYGON,
  title: 'Custom FIR NL polygon',
  shape: intersectionFeatureNL,
  isSelectable: false
};
const shapeButtonBE$1 = {
  drawModeId: 'fir-be',
  value: DRAWMODE.POLYGON,
  title: 'Custom FIR BE polygon',
  shape: intersectionFeatureBE,
  isSelectable: false
};
const geoJSONIntersectionBoundsStyle$1 = {
  stroke: '#000000',
  'stroke-width': 1.5,
  'stroke-opacity': 1,
  fill: '#0075a9',
  'fill-opacity': 0.0
};
const featurePropsIntersectionEnd = {
  stroke: '#000000',
  'stroke-width': 1.5,
  'stroke-opacity': 1,
  fill: '#6e1e91',
  'fill-opacity': 0.5
};
const intersectionShapeNL$1 = {
  type: 'FeatureCollection',
  features: [Object.assign(Object.assign({}, intersectionFeatureNL), {
    properties: geoJSONIntersectionBoundsStyle$1
  })]
};
const intersectionShapeBE$1 = {
  type: 'FeatureCollection',
  features: [Object.assign(Object.assign({}, intersectionFeatureBE), {
    properties: geoJSONIntersectionBoundsStyle$1
  })]
};
const exampleIntersections = [{
  title: 'NL',
  geojson: intersectionShapeNL$1
}, {
  title: 'BE',
  geojson: intersectionShapeBE$1
}];
const exampleIntersectionOptions = {
  defaultGeoJSONIntersectionBounds: intersectionShapeNL$1,
  defaultDrawModes: [defaultPoint, defaultPolygon, defaultBox, shapeButtonNL$1, shapeButtonBE$1, defaultDelete]
};
const exampleIntersectionWithShapeOptions = {
  defaultGeoJSON: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, featurePropsIntersectionEnd), {
        selectionType: 'poly'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[5.711880814773656, 55.12656218004421], [7.663910745234145, 55.970965630799725], [10.075241835802988, 52.35182632872327], [6.673542618750517, 51.77828698943017], [4.506215269489237, 53.442721496909385], [5.711880814773656, 55.12656218004421]]]
      }
    }]
  },
  defaultGeoJSONIntersection: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, featurePropsIntersectionEnd), {
        selectionType: 'poly'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[4.506215269489237, 53.442721496909385], [6.515104876543458, 51.89996184648691], [7.053095, 52.237764], [7.031389, 52.268885], [7.063612, 52.346109], [7.065557, 52.385828], [7.133055, 52.888887], [7.14218, 52.898244], [7.191667, 53.3], [6.5, 53.666667], [6.500002, 55.000002], [5.621260209964854, 55.00000082834584], [4.506215269489237, 53.442721496909385]]]
      }
    }]
  },
  defaultGeoJSONIntersectionBounds: intersectionShapeNL$1,
  defaultDrawModes: [Object.assign(Object.assign({}, defaultPoint), {
    shape: Object.assign(Object.assign({}, defaultPoint.shape), {
      properties: Object.assign(Object.assign({}, defaultPoint.shape.properties), featurePropsIntersectionEnd)
    })
  }), Object.assign(Object.assign({}, defaultPolygon), {
    shape: Object.assign(Object.assign({}, defaultPolygon.shape), {
      properties: Object.assign(Object.assign({}, defaultPolygon.shape.properties), featurePropsIntersectionEnd)
    })
  }), Object.assign(Object.assign({}, defaultBox), {
    shape: Object.assign(Object.assign({}, defaultBox.shape), {
      properties: Object.assign(Object.assign({}, defaultBox.shape.properties), featurePropsIntersectionEnd)
    })
  }), Object.assign(Object.assign({}, shapeButtonNL$1), {
    shape: Object.assign(Object.assign({}, shapeButtonNL$1.shape), {
      properties: Object.assign(Object.assign({}, shapeButtonNL$1.shape.properties), featurePropsIntersectionEnd)
    })
  }), Object.assign(Object.assign({}, shapeButtonBE$1), {
    shape: Object.assign(Object.assign({}, shapeButtonBE$1.shape), {
      properties: Object.assign(Object.assign({}, shapeButtonBE$1.shape.properties), featurePropsIntersectionEnd)
    })
  }), defaultDelete],
  defaultGeoJSONIntersectionProperties: featurePropsIntersectionEnd
};

const firButtonDrawId = 'fir-button';
// styles and shapes
const featurePropertiesStart = defaultGeoJSONStyleProperties;
const featurePropertiesEnd = {
  stroke: '#6e1e91',
  'stroke-width': 1.5,
  'stroke-opacity': 1,
  fill: '#6e1e91',
  'fill-opacity': 0.25
};
const geoJSONIntersectionBoundsStyle = {
  stroke: '#000000',
  'stroke-width': 1.5,
  'stroke-opacity': 1,
  fill: '#0075a9',
  'fill-opacity': 0.0
};
const intersectionShapeNL = {
  type: 'FeatureCollection',
  features: [Object.assign(Object.assign({}, intersectionFeatureNL), {
    properties: Object.assign(Object.assign({}, geoJSONIntersectionBoundsStyle), {
      selectionType: 'fir'
    })
  })]
};
const intersectionShapeBE = {
  type: 'FeatureCollection',
  features: [Object.assign(Object.assign({}, intersectionFeatureBE), {
    properties: Object.assign(Object.assign({}, geoJSONIntersectionBoundsStyle), {
      selectionType: 'fir'
    })
  })]
};
// custom buttons
const shapeButtonNL = {
  drawModeId: firButtonDrawId,
  value: DRAWMODE.POLYGON,
  title: 'Custom FIR NL polygon',
  shape: intersectionShapeNL,
  isSelectable: false
};
const shapeButtonBE = {
  drawModeId: firButtonDrawId,
  value: DRAWMODE.POLYGON,
  title: 'Custom FIR BE polygon',
  shape: intersectionShapeBE,
  isSelectable: false
};
const exampleIntersectionsMultiDrawTool = [{
  title: 'NL',
  geojson: shapeButtonNL.shape
}, {
  title: 'BE',
  geojson: shapeButtonBE.shape
}];
const getDoubleControlToolIcon = drawMode => {
  const {
    drawModeId
  } = drawMode;
  const defaultIcon = getIcon(drawModeId);
  if (defaultIcon) {
    return defaultIcon;
  }
  if (drawModeId === firButtonDrawId) {
    return /*#__PURE__*/React__default.createElement(DrawFIRLand, {
      sx: Object.assign({}, drawMode.title === shapeButtonBE.title && {
        transform: `scaleY(-1)`
      })
    });
  }
  return /*#__PURE__*/React__default.createElement(DrawFIRLand, null);
};
const getEditModes = geoJSONProperties => [Object.assign(Object.assign({}, defaultPoint), {
  shape: Object.assign(Object.assign({}, defaultPoint.shape), {
    properties: Object.assign(Object.assign({}, defaultPoint.shape.properties), geoJSONProperties)
  })
}), Object.assign(Object.assign({}, defaultPolygon), {
  shape: Object.assign(Object.assign({}, defaultPolygon.shape), {
    properties: Object.assign(Object.assign({}, defaultPolygon.shape.properties), geoJSONProperties)
  })
}), Object.assign(Object.assign({}, defaultBox), {
  shape: Object.assign(Object.assign({}, defaultBox.shape), {
    properties: Object.assign(Object.assign({}, defaultBox.shape.properties), geoJSONProperties)
  })
}), Object.assign(Object.assign({}, shapeButtonNL), {
  shape: Object.assign(Object.assign({}, shapeButtonNL.shape), {
    features: [Object.assign(Object.assign({}, intersectionFeatureNL), {
      properties: Object.assign(Object.assign({}, intersectionShapeNL.features[0].properties), geoJSONProperties)
    })]
  })
}), defaultDelete];
const getUpdatedFirShape = isNL => isNL ? intersectionShapeNL : intersectionShapeBE;
const getFirTitle = isNL => isNL ? shapeButtonNL.title : shapeButtonBE.title;
const updateEditModeButtonsWithFir = (drawModes, newFirGeoJSON) => {
  const isNL = newFirGeoJSON === intersectionShapeNL;
  return drawModes.map(mode => {
    if (mode.drawModeId === firButtonDrawId) {
      return Object.assign(Object.assign({}, mode), {
        shape: getUpdatedFirShape(isNL),
        title: getFirTitle(isNL)
      });
    }
    return mode;
  });
};
// example configs
const startToolExampleConfig = {
  defaultGeoJSONIntersectionBounds: intersectionShapeNL,
  defaultDrawModes: getEditModes(featurePropertiesStart),
  defaultGeoJSON: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, featurePropertiesStart), {
        selectionType: 'box'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[9.451665078283622, 53.21804334226515], [4.734608638338736, 53.21804334226515], [4.734608638338736, 54.57650543915101], [9.451665078283622, 54.57650543915101], [9.451665078283622, 53.21804334226515]]]
      }
    }]
  },
  defaultGeoJSONIntersection: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, defaultIntersectionStyleProperties), {
        selectionType: 'box'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[4.734608638338736, 53.21804334226515], [7.181571844927457, 53.21804334226515], [7.191667, 53.3], [6.5, 53.666667], [6.500001364755953, 54.57650543915101], [4.734608638338736, 54.57650543915101], [4.734608638338736, 53.21804334226515]]]
      }
    }]
  }
};
const endToolExampleConfig = {
  defaultGeoJSONIntersectionBounds: intersectionShapeNL,
  defaultDrawModes: getEditModes(featurePropertiesEnd),
  defaultGeoJSONIntersectionProperties: featurePropertiesEnd,
  defaultGeoJSON: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, featurePropertiesEnd), {
        selectionType: 'poly'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[2.3408785046353606, 52.57033268070152], [8.870833501723242, 51.74975701659361], [-0.088053542798945, 50.72496422996941], [2.3408785046353606, 52.57033268070152]]]
      }
    }]
  },
  defaultGeoJSONIntersection: {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: Object.assign(Object.assign({}, featurePropertiesEnd), {
        selectionType: 'poly'
      }),
      geometry: {
        type: 'Polygon',
        coordinates: [[[2.000002, 51.500002], [3.370001, 51.369722], [3.362223, 51.320002], [3.36389, 51.313608], [3.373613, 51.309999], [3.952501, 51.214441], [4.397501, 51.452776], [5.078611, 51.391665], [5.249839243115953, 51.335557264081125], [6.173465572431757, 51.44120940537001], [5.94639, 51.811663], [6.405001, 51.830828], [6.708618091513499, 52.02146813052448], [2.8253551673020594, 52.50945174669971], [2.000002, 51.500002]]]
      }
    }]
  }
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * */
const ReactMapViewLayer = props => {
  const {
    id
  } = props;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%',
      padding: '2px',
      margin: 0,
      wordWrap: 'break-word',
      border: '1px solid rgba(0, 0, 0, 0.5)',
      lineHeight: '14px',
      fontSize: '10px',
      background: 'rgba(255, 255, 255, 0.5)',
      display: 'none'
    }
  }, /*#__PURE__*/React.createElement("div", null, id));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var aCallable$6 = aCallable$a;
var toObject = toObject$5;
var IndexedObject = indexedObject;
var lengthOfArrayLike$1 = lengthOfArrayLike$4;

var $TypeError$7 = TypeError;

// `Array.prototype.{ reduce, reduceRight }` methods implementation
var createMethod = function (IS_RIGHT) {
  return function (that, callbackfn, argumentsLength, memo) {
    var O = toObject(that);
    var self = IndexedObject(O);
    var length = lengthOfArrayLike$1(O);
    aCallable$6(callbackfn);
    var index = IS_RIGHT ? length - 1 : 0;
    var i = IS_RIGHT ? -1 : 1;
    if (argumentsLength < 2) while (true) {
      if (index in self) {
        memo = self[index];
        index += i;
        break;
      }
      index += i;
      if (IS_RIGHT ? index < 0 : length <= index) {
        throw new $TypeError$7('Reduce of empty array with no initial value');
      }
    }
    for (;IS_RIGHT ? index >= 0 : length > index; index += i) if (index in self) {
      memo = callbackfn(memo, self[index], index, O);
    }
    return memo;
  };
};

var arrayReduce = {
  // `Array.prototype.reduce` method
  // https://tc39.es/ecma262/#sec-array.prototype.reduce
  left: createMethod(false),
  // `Array.prototype.reduceRight` method
  // https://tc39.es/ecma262/#sec-array.prototype.reduceright
  right: createMethod(true)
};

var global$7 = global$k;
var classof$3 = classofRaw$2;

var engineIsNode = classof$3(global$7.process) === 'process';

var $$8 = _export;
var $reduce = arrayReduce.left;
var arrayMethodIsStrict = arrayMethodIsStrict$2;
var CHROME_VERSION = engineV8Version;
var IS_NODE$4 = engineIsNode;

// Chrome 80-82 has a critical bug
// https://bugs.chromium.org/p/chromium/issues/detail?id=1049982
var CHROME_BUG = !IS_NODE$4 && CHROME_VERSION > 79 && CHROME_VERSION < 83;
var FORCED = CHROME_BUG || !arrayMethodIsStrict('reduce');

// `Array.prototype.reduce` method
// https://tc39.es/ecma262/#sec-array.prototype.reduce
$$8({ target: 'Array', proto: true, forced: FORCED }, {
  reduce: function reduce(callbackfn /* , initialValue */) {
    var length = arguments.length;
    return $reduce(this, callbackfn, length, length > 1 ? arguments[1] : undefined);
  }
});

var makeBuiltIn = makeBuiltIn$3.exports;
var defineProperty = objectDefineProperty;

var defineBuiltInAccessor$1 = function (target, name, descriptor) {
  if (descriptor.get) makeBuiltIn(descriptor.get, name, { getter: true });
  if (descriptor.set) makeBuiltIn(descriptor.set, name, { setter: true });
  return defineProperty.f(target, name, descriptor);
};

var getBuiltIn$3 = getBuiltIn$7;
var defineBuiltInAccessor = defineBuiltInAccessor$1;
var wellKnownSymbol$7 = wellKnownSymbol$g;
var DESCRIPTORS$1 = descriptors;

var SPECIES$2 = wellKnownSymbol$7('species');

var setSpecies$1 = function (CONSTRUCTOR_NAME) {
  var Constructor = getBuiltIn$3(CONSTRUCTOR_NAME);

  if (DESCRIPTORS$1 && Constructor && !Constructor[SPECIES$2]) {
    defineBuiltInAccessor(Constructor, SPECIES$2, {
      configurable: true,
      get: function () { return this; }
    });
  }
};

var isPrototypeOf$1 = objectIsPrototypeOf;

var $TypeError$6 = TypeError;

var anInstance$1 = function (it, Prototype) {
  if (isPrototypeOf$1(Prototype, it)) return it;
  throw new $TypeError$6('Incorrect invocation');
};

var uncurryThis$3 = functionUncurryThis;
var fails$2 = fails$i;
var isCallable$4 = isCallable$j;
var classof$2 = classof$6;
var getBuiltIn$2 = getBuiltIn$7;
var inspectSource$1 = inspectSource$3;

var noop = function () { /* empty */ };
var construct = getBuiltIn$2('Reflect', 'construct');
var constructorRegExp = /^\s*(?:class|function)\b/;
var exec = uncurryThis$3(constructorRegExp.exec);
var INCORRECT_TO_STRING = !constructorRegExp.test(noop);

var isConstructorModern = function isConstructor(argument) {
  if (!isCallable$4(argument)) return false;
  try {
    construct(noop, [], argument);
    return true;
  } catch (error) {
    return false;
  }
};

var isConstructorLegacy = function isConstructor(argument) {
  if (!isCallable$4(argument)) return false;
  switch (classof$2(argument)) {
    case 'AsyncFunction':
    case 'GeneratorFunction':
    case 'AsyncGeneratorFunction': return false;
  }
  try {
    // we can't check .prototype since constructors produced by .bind haven't it
    // `Function#toString` throws on some built-it function in some legacy engines
    // (for example, `DOMQuad` and similar in FF41-)
    return INCORRECT_TO_STRING || !!exec(constructorRegExp, inspectSource$1(argument));
  } catch (error) {
    return true;
  }
};

isConstructorLegacy.sham = true;

// `IsConstructor` abstract operation
// https://tc39.es/ecma262/#sec-isconstructor
var isConstructor$1 = !construct || fails$2(function () {
  var called;
  return isConstructorModern(isConstructorModern.call)
    || !isConstructorModern(Object)
    || !isConstructorModern(function () { called = true; })
    || called;
}) ? isConstructorLegacy : isConstructorModern;

var isConstructor = isConstructor$1;
var tryToString$2 = tryToString$5;

var $TypeError$5 = TypeError;

// `Assert: IsConstructor(argument) is true`
var aConstructor$1 = function (argument) {
  if (isConstructor(argument)) return argument;
  throw new $TypeError$5(tryToString$2(argument) + ' is not a constructor');
};

var anObject$4 = anObject$c;
var aConstructor = aConstructor$1;
var isNullOrUndefined$1 = isNullOrUndefined$4;
var wellKnownSymbol$6 = wellKnownSymbol$g;

var SPECIES$1 = wellKnownSymbol$6('species');

// `SpeciesConstructor` abstract operation
// https://tc39.es/ecma262/#sec-speciesconstructor
var speciesConstructor$1 = function (O, defaultConstructor) {
  var C = anObject$4(O).constructor;
  var S;
  return C === undefined || isNullOrUndefined$1(S = anObject$4(C)[SPECIES$1]) ? defaultConstructor : aConstructor(S);
};

var NATIVE_BIND$1 = functionBindNative;

var FunctionPrototype = Function.prototype;
var apply$1 = FunctionPrototype.apply;
var call$6 = FunctionPrototype.call;

// eslint-disable-next-line es/no-reflect -- safe
var functionApply = typeof Reflect == 'object' && Reflect.apply || (NATIVE_BIND$1 ? call$6.bind(apply$1) : function () {
  return call$6.apply(apply$1, arguments);
});

var classofRaw = classofRaw$2;
var uncurryThis$2 = functionUncurryThis;

var functionUncurryThisClause = function (fn) {
  // Nashorn bug:
  //   https://github.com/zloirock/core-js/issues/1128
  //   https://github.com/zloirock/core-js/issues/1130
  if (classofRaw(fn) === 'Function') return uncurryThis$2(fn);
};

var uncurryThis$1 = functionUncurryThisClause;
var aCallable$5 = aCallable$a;
var NATIVE_BIND = functionBindNative;

var bind$4 = uncurryThis$1(uncurryThis$1.bind);

// optional / simple context binding
var functionBindContext = function (fn, that) {
  aCallable$5(fn);
  return that === undefined ? fn : NATIVE_BIND ? bind$4(fn, that) : function (/* ...args */) {
    return fn.apply(that, arguments);
  };
};

var $TypeError$4 = TypeError;

var validateArgumentsLength$1 = function (passed, required) {
  if (passed < required) throw new $TypeError$4('Not enough arguments');
  return passed;
};

var userAgent$2 = engineUserAgent;

// eslint-disable-next-line redos/no-vulnerable -- safe
var engineIsIos = /(?:ipad|iphone|ipod).*applewebkit/i.test(userAgent$2);

var global$6 = global$k;
var apply = functionApply;
var bind$3 = functionBindContext;
var isCallable$3 = isCallable$j;
var hasOwn = hasOwnProperty_1;
var fails$1 = fails$i;
var html = html$2;
var arraySlice = arraySlice$2;
var createElement = documentCreateElement$2;
var validateArgumentsLength = validateArgumentsLength$1;
var IS_IOS$1 = engineIsIos;
var IS_NODE$3 = engineIsNode;

var set = global$6.setImmediate;
var clear = global$6.clearImmediate;
var process$2 = global$6.process;
var Dispatch = global$6.Dispatch;
var Function$1 = global$6.Function;
var MessageChannel = global$6.MessageChannel;
var String$1 = global$6.String;
var counter = 0;
var queue$2 = {};
var ONREADYSTATECHANGE = 'onreadystatechange';
var $location, defer, channel, port;

fails$1(function () {
  // Deno throws a ReferenceError on `location` access without `--location` flag
  $location = global$6.location;
});

var run = function (id) {
  if (hasOwn(queue$2, id)) {
    var fn = queue$2[id];
    delete queue$2[id];
    fn();
  }
};

var runner = function (id) {
  return function () {
    run(id);
  };
};

var eventListener = function (event) {
  run(event.data);
};

var globalPostMessageDefer = function (id) {
  // old engines have not location.origin
  global$6.postMessage(String$1(id), $location.protocol + '//' + $location.host);
};

// Node.js 0.9+ & IE10+ has setImmediate, otherwise:
if (!set || !clear) {
  set = function setImmediate(handler) {
    validateArgumentsLength(arguments.length, 1);
    var fn = isCallable$3(handler) ? handler : Function$1(handler);
    var args = arraySlice(arguments, 1);
    queue$2[++counter] = function () {
      apply(fn, undefined, args);
    };
    defer(counter);
    return counter;
  };
  clear = function clearImmediate(id) {
    delete queue$2[id];
  };
  // Node.js 0.8-
  if (IS_NODE$3) {
    defer = function (id) {
      process$2.nextTick(runner(id));
    };
  // Sphere (JS game engine) Dispatch API
  } else if (Dispatch && Dispatch.now) {
    defer = function (id) {
      Dispatch.now(runner(id));
    };
  // Browsers with MessageChannel, includes WebWorkers
  // except iOS - https://github.com/zloirock/core-js/issues/624
  } else if (MessageChannel && !IS_IOS$1) {
    channel = new MessageChannel();
    port = channel.port2;
    channel.port1.onmessage = eventListener;
    defer = bind$3(port.postMessage, port);
  // Browsers with postMessage, skip WebWorkers
  // IE8 has postMessage, but it's sync & typeof its postMessage is 'object'
  } else if (
    global$6.addEventListener &&
    isCallable$3(global$6.postMessage) &&
    !global$6.importScripts &&
    $location && $location.protocol !== 'file:' &&
    !fails$1(globalPostMessageDefer)
  ) {
    defer = globalPostMessageDefer;
    global$6.addEventListener('message', eventListener, false);
  // IE8-
  } else if (ONREADYSTATECHANGE in createElement('script')) {
    defer = function (id) {
      html.appendChild(createElement('script'))[ONREADYSTATECHANGE] = function () {
        html.removeChild(this);
        run(id);
      };
    };
  // Rest old browsers
  } else {
    defer = function (id) {
      setTimeout(runner(id), 0);
    };
  }
}

var task$1 = {
  set: set,
  clear: clear
};

var global$5 = global$k;
var DESCRIPTORS = descriptors;

// eslint-disable-next-line es/no-object-getownpropertydescriptor -- safe
var getOwnPropertyDescriptor = Object.getOwnPropertyDescriptor;

// Avoid NodeJS experimental warning
var safeGetBuiltIn$1 = function (name) {
  if (!DESCRIPTORS) return global$5[name];
  var descriptor = getOwnPropertyDescriptor(global$5, name);
  return descriptor && descriptor.value;
};

var Queue$2 = function () {
  this.head = null;
  this.tail = null;
};

Queue$2.prototype = {
  add: function (item) {
    var entry = { item: item, next: null };
    var tail = this.tail;
    if (tail) tail.next = entry;
    else this.head = entry;
    this.tail = entry;
  },
  get: function () {
    var entry = this.head;
    if (entry) {
      var next = this.head = entry.next;
      if (next === null) this.tail = null;
      return entry.item;
    }
  }
};

var queue$1 = Queue$2;

var userAgent$1 = engineUserAgent;

var engineIsIosPebble = /ipad|iphone|ipod/i.test(userAgent$1) && typeof Pebble != 'undefined';

var userAgent = engineUserAgent;

var engineIsWebosWebkit = /web0s(?!.*chrome)/i.test(userAgent);

var global$4 = global$k;
var safeGetBuiltIn = safeGetBuiltIn$1;
var bind$2 = functionBindContext;
var macrotask = task$1.set;
var Queue$1 = queue$1;
var IS_IOS = engineIsIos;
var IS_IOS_PEBBLE = engineIsIosPebble;
var IS_WEBOS_WEBKIT = engineIsWebosWebkit;
var IS_NODE$2 = engineIsNode;

var MutationObserver = global$4.MutationObserver || global$4.WebKitMutationObserver;
var document$2 = global$4.document;
var process$1 = global$4.process;
var Promise$1 = global$4.Promise;
var microtask$1 = safeGetBuiltIn('queueMicrotask');
var notify$1, toggle, node, promise, then;

// modern engines have queueMicrotask method
if (!microtask$1) {
  var queue = new Queue$1();

  var flush = function () {
    var parent, fn;
    if (IS_NODE$2 && (parent = process$1.domain)) parent.exit();
    while (fn = queue.get()) try {
      fn();
    } catch (error) {
      if (queue.head) notify$1();
      throw error;
    }
    if (parent) parent.enter();
  };

  // browsers with MutationObserver, except iOS - https://github.com/zloirock/core-js/issues/339
  // also except WebOS Webkit https://github.com/zloirock/core-js/issues/898
  if (!IS_IOS && !IS_NODE$2 && !IS_WEBOS_WEBKIT && MutationObserver && document$2) {
    toggle = true;
    node = document$2.createTextNode('');
    new MutationObserver(flush).observe(node, { characterData: true });
    notify$1 = function () {
      node.data = toggle = !toggle;
    };
  // environments with maybe non-completely correct, but existent Promise
  } else if (!IS_IOS_PEBBLE && Promise$1 && Promise$1.resolve) {
    // Promise.resolve without an argument throws an error in LG WebOS 2
    promise = Promise$1.resolve(undefined);
    // workaround of WebKit ~ iOS Safari 10.1 bug
    promise.constructor = Promise$1;
    then = bind$2(promise.then, promise);
    notify$1 = function () {
      then(flush);
    };
  // Node.js without promises
  } else if (IS_NODE$2) {
    notify$1 = function () {
      process$1.nextTick(flush);
    };
  // for other environments - macrotask based on:
  // - setImmediate
  // - MessageChannel
  // - window.postMessage
  // - onreadystatechange
  // - setTimeout
  } else {
    // `webpack` dev server bug on IE global methods - use bind(fn, global)
    macrotask = bind$2(macrotask, global$4);
    notify$1 = function () {
      macrotask(flush);
    };
  }

  microtask$1 = function (fn) {
    if (!queue.head) notify$1();
    queue.add(fn);
  };
}

var microtask_1 = microtask$1;

var hostReportErrors$1 = function (a, b) {
  try {
    // eslint-disable-next-line no-console -- safe
    arguments.length === 1 ? console.error(a) : console.error(a, b);
  } catch (error) { /* empty */ }
};

var perform$3 = function (exec) {
  try {
    return { error: false, value: exec() };
  } catch (error) {
    return { error: true, value: error };
  }
};

var global$3 = global$k;

var promiseNativeConstructor = global$3.Promise;

/* global Deno -- Deno case */
var engineIsDeno = typeof Deno == 'object' && Deno && typeof Deno.version == 'object';

var IS_DENO$1 = engineIsDeno;
var IS_NODE$1 = engineIsNode;

var engineIsBrowser = !IS_DENO$1 && !IS_NODE$1
  && typeof window == 'object'
  && typeof document == 'object';

var global$2 = global$k;
var NativePromiseConstructor$3 = promiseNativeConstructor;
var isCallable$2 = isCallable$j;
var isForced = isForced_1;
var inspectSource = inspectSource$3;
var wellKnownSymbol$5 = wellKnownSymbol$g;
var IS_BROWSER = engineIsBrowser;
var IS_DENO = engineIsDeno;
var V8_VERSION = engineV8Version;

NativePromiseConstructor$3 && NativePromiseConstructor$3.prototype;
var SPECIES = wellKnownSymbol$5('species');
var SUBCLASSING = false;
var NATIVE_PROMISE_REJECTION_EVENT$1 = isCallable$2(global$2.PromiseRejectionEvent);

var FORCED_PROMISE_CONSTRUCTOR$5 = isForced('Promise', function () {
  var PROMISE_CONSTRUCTOR_SOURCE = inspectSource(NativePromiseConstructor$3);
  var GLOBAL_CORE_JS_PROMISE = PROMISE_CONSTRUCTOR_SOURCE !== String(NativePromiseConstructor$3);
  // V8 6.6 (Node 10 and Chrome 66) have a bug with resolving custom thenables
  // https://bugs.chromium.org/p/chromium/issues/detail?id=830565
  // We can't detect it synchronously, so just check versions
  if (!GLOBAL_CORE_JS_PROMISE && V8_VERSION === 66) return true;
  // We can't use @@species feature detection in V8 since it causes
  // deoptimization and performance degradation
  // https://github.com/zloirock/core-js/issues/679
  if (!V8_VERSION || V8_VERSION < 51 || !/native code/.test(PROMISE_CONSTRUCTOR_SOURCE)) {
    // Detect correctness of subclassing with @@species support
    var promise = new NativePromiseConstructor$3(function (resolve) { resolve(1); });
    var FakePromise = function (exec) {
      exec(function () { /* empty */ }, function () { /* empty */ });
    };
    var constructor = promise.constructor = {};
    constructor[SPECIES] = FakePromise;
    SUBCLASSING = promise.then(function () { /* empty */ }) instanceof FakePromise;
    if (!SUBCLASSING) return true;
  // Unhandled rejections tracking support, NodeJS Promise without it fails @@species test
  } return !GLOBAL_CORE_JS_PROMISE && (IS_BROWSER || IS_DENO) && !NATIVE_PROMISE_REJECTION_EVENT$1;
});

var promiseConstructorDetection = {
  CONSTRUCTOR: FORCED_PROMISE_CONSTRUCTOR$5,
  REJECTION_EVENT: NATIVE_PROMISE_REJECTION_EVENT$1,
  SUBCLASSING: SUBCLASSING
};

var newPromiseCapability$2 = {};

var aCallable$4 = aCallable$a;

var $TypeError$3 = TypeError;

var PromiseCapability = function (C) {
  var resolve, reject;
  this.promise = new C(function ($$resolve, $$reject) {
    if (resolve !== undefined || reject !== undefined) throw new $TypeError$3('Bad Promise constructor');
    resolve = $$resolve;
    reject = $$reject;
  });
  this.resolve = aCallable$4(resolve);
  this.reject = aCallable$4(reject);
};

// `NewPromiseCapability` abstract operation
// https://tc39.es/ecma262/#sec-newpromisecapability
newPromiseCapability$2.f = function (C) {
  return new PromiseCapability(C);
};

var $$7 = _export;
var IS_NODE = engineIsNode;
var global$1 = global$k;
var call$5 = functionCall;
var defineBuiltIn$1 = defineBuiltIn$6;
var setPrototypeOf = objectSetPrototypeOf;
var setToStringTag = setToStringTag$4;
var setSpecies = setSpecies$1;
var aCallable$3 = aCallable$a;
var isCallable$1 = isCallable$j;
var isObject$2 = isObject$a;
var anInstance = anInstance$1;
var speciesConstructor = speciesConstructor$1;
var task = task$1.set;
var microtask = microtask_1;
var hostReportErrors = hostReportErrors$1;
var perform$2 = perform$3;
var Queue = queue$1;
var InternalStateModule = internalState;
var NativePromiseConstructor$2 = promiseNativeConstructor;
var PromiseConstructorDetection = promiseConstructorDetection;
var newPromiseCapabilityModule$3 = newPromiseCapability$2;

var PROMISE = 'Promise';
var FORCED_PROMISE_CONSTRUCTOR$4 = PromiseConstructorDetection.CONSTRUCTOR;
var NATIVE_PROMISE_REJECTION_EVENT = PromiseConstructorDetection.REJECTION_EVENT;
var NATIVE_PROMISE_SUBCLASSING = PromiseConstructorDetection.SUBCLASSING;
var getInternalPromiseState = InternalStateModule.getterFor(PROMISE);
var setInternalState = InternalStateModule.set;
var NativePromisePrototype$1 = NativePromiseConstructor$2 && NativePromiseConstructor$2.prototype;
var PromiseConstructor = NativePromiseConstructor$2;
var PromisePrototype = NativePromisePrototype$1;
var TypeError$1 = global$1.TypeError;
var document$1 = global$1.document;
var process = global$1.process;
var newPromiseCapability$1 = newPromiseCapabilityModule$3.f;
var newGenericPromiseCapability = newPromiseCapability$1;

var DISPATCH_EVENT = !!(document$1 && document$1.createEvent && global$1.dispatchEvent);
var UNHANDLED_REJECTION = 'unhandledrejection';
var REJECTION_HANDLED = 'rejectionhandled';
var PENDING = 0;
var FULFILLED = 1;
var REJECTED = 2;
var HANDLED = 1;
var UNHANDLED = 2;

var Internal, OwnPromiseCapability, PromiseWrapper, nativeThen;

// helpers
var isThenable = function (it) {
  var then;
  return isObject$2(it) && isCallable$1(then = it.then) ? then : false;
};

var callReaction = function (reaction, state) {
  var value = state.value;
  var ok = state.state === FULFILLED;
  var handler = ok ? reaction.ok : reaction.fail;
  var resolve = reaction.resolve;
  var reject = reaction.reject;
  var domain = reaction.domain;
  var result, then, exited;
  try {
    if (handler) {
      if (!ok) {
        if (state.rejection === UNHANDLED) onHandleUnhandled(state);
        state.rejection = HANDLED;
      }
      if (handler === true) result = value;
      else {
        if (domain) domain.enter();
        result = handler(value); // can throw
        if (domain) {
          domain.exit();
          exited = true;
        }
      }
      if (result === reaction.promise) {
        reject(new TypeError$1('Promise-chain cycle'));
      } else if (then = isThenable(result)) {
        call$5(then, result, resolve, reject);
      } else resolve(result);
    } else reject(value);
  } catch (error) {
    if (domain && !exited) domain.exit();
    reject(error);
  }
};

var notify = function (state, isReject) {
  if (state.notified) return;
  state.notified = true;
  microtask(function () {
    var reactions = state.reactions;
    var reaction;
    while (reaction = reactions.get()) {
      callReaction(reaction, state);
    }
    state.notified = false;
    if (isReject && !state.rejection) onUnhandled(state);
  });
};

var dispatchEvent = function (name, promise, reason) {
  var event, handler;
  if (DISPATCH_EVENT) {
    event = document$1.createEvent('Event');
    event.promise = promise;
    event.reason = reason;
    event.initEvent(name, false, true);
    global$1.dispatchEvent(event);
  } else event = { promise: promise, reason: reason };
  if (!NATIVE_PROMISE_REJECTION_EVENT && (handler = global$1['on' + name])) handler(event);
  else if (name === UNHANDLED_REJECTION) hostReportErrors('Unhandled promise rejection', reason);
};

var onUnhandled = function (state) {
  call$5(task, global$1, function () {
    var promise = state.facade;
    var value = state.value;
    var IS_UNHANDLED = isUnhandled(state);
    var result;
    if (IS_UNHANDLED) {
      result = perform$2(function () {
        if (IS_NODE) {
          process.emit('unhandledRejection', value, promise);
        } else dispatchEvent(UNHANDLED_REJECTION, promise, value);
      });
      // Browsers should not trigger `rejectionHandled` event if it was handled here, NodeJS - should
      state.rejection = IS_NODE || isUnhandled(state) ? UNHANDLED : HANDLED;
      if (result.error) throw result.value;
    }
  });
};

var isUnhandled = function (state) {
  return state.rejection !== HANDLED && !state.parent;
};

var onHandleUnhandled = function (state) {
  call$5(task, global$1, function () {
    var promise = state.facade;
    if (IS_NODE) {
      process.emit('rejectionHandled', promise);
    } else dispatchEvent(REJECTION_HANDLED, promise, state.value);
  });
};

var bind$1 = function (fn, state, unwrap) {
  return function (value) {
    fn(state, value, unwrap);
  };
};

var internalReject = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  state.value = value;
  state.state = REJECTED;
  notify(state, true);
};

var internalResolve = function (state, value, unwrap) {
  if (state.done) return;
  state.done = true;
  if (unwrap) state = unwrap;
  try {
    if (state.facade === value) throw new TypeError$1("Promise can't be resolved itself");
    var then = isThenable(value);
    if (then) {
      microtask(function () {
        var wrapper = { done: false };
        try {
          call$5(then, value,
            bind$1(internalResolve, wrapper, state),
            bind$1(internalReject, wrapper, state)
          );
        } catch (error) {
          internalReject(wrapper, error, state);
        }
      });
    } else {
      state.value = value;
      state.state = FULFILLED;
      notify(state, false);
    }
  } catch (error) {
    internalReject({ done: false }, error, state);
  }
};

// constructor polyfill
if (FORCED_PROMISE_CONSTRUCTOR$4) {
  // 25.4.3.1 Promise(executor)
  PromiseConstructor = function Promise(executor) {
    anInstance(this, PromisePrototype);
    aCallable$3(executor);
    call$5(Internal, this);
    var state = getInternalPromiseState(this);
    try {
      executor(bind$1(internalResolve, state), bind$1(internalReject, state));
    } catch (error) {
      internalReject(state, error);
    }
  };

  PromisePrototype = PromiseConstructor.prototype;

  // eslint-disable-next-line no-unused-vars -- required for `.length`
  Internal = function Promise(executor) {
    setInternalState(this, {
      type: PROMISE,
      done: false,
      notified: false,
      parent: false,
      reactions: new Queue(),
      rejection: false,
      state: PENDING,
      value: undefined
    });
  };

  // `Promise.prototype.then` method
  // https://tc39.es/ecma262/#sec-promise.prototype.then
  Internal.prototype = defineBuiltIn$1(PromisePrototype, 'then', function then(onFulfilled, onRejected) {
    var state = getInternalPromiseState(this);
    var reaction = newPromiseCapability$1(speciesConstructor(this, PromiseConstructor));
    state.parent = true;
    reaction.ok = isCallable$1(onFulfilled) ? onFulfilled : true;
    reaction.fail = isCallable$1(onRejected) && onRejected;
    reaction.domain = IS_NODE ? process.domain : undefined;
    if (state.state === PENDING) state.reactions.add(reaction);
    else microtask(function () {
      callReaction(reaction, state);
    });
    return reaction.promise;
  });

  OwnPromiseCapability = function () {
    var promise = new Internal();
    var state = getInternalPromiseState(promise);
    this.promise = promise;
    this.resolve = bind$1(internalResolve, state);
    this.reject = bind$1(internalReject, state);
  };

  newPromiseCapabilityModule$3.f = newPromiseCapability$1 = function (C) {
    return C === PromiseConstructor || C === PromiseWrapper
      ? new OwnPromiseCapability(C)
      : newGenericPromiseCapability(C);
  };

  if (isCallable$1(NativePromiseConstructor$2) && NativePromisePrototype$1 !== Object.prototype) {
    nativeThen = NativePromisePrototype$1.then;

    if (!NATIVE_PROMISE_SUBCLASSING) {
      // make `Promise#then` return a polyfilled `Promise` for native promise-based APIs
      defineBuiltIn$1(NativePromisePrototype$1, 'then', function then(onFulfilled, onRejected) {
        var that = this;
        return new PromiseConstructor(function (resolve, reject) {
          call$5(nativeThen, that, resolve, reject);
        }).then(onFulfilled, onRejected);
      // https://github.com/zloirock/core-js/issues/640
      }, { unsafe: true });
    }

    // make `.constructor === Promise` work for native promise-based APIs
    try {
      delete NativePromisePrototype$1.constructor;
    } catch (error) { /* empty */ }

    // make `instanceof Promise` work for native promise-based APIs
    if (setPrototypeOf) {
      setPrototypeOf(NativePromisePrototype$1, PromisePrototype);
    }
  }
}

$$7({ global: true, constructor: true, wrap: true, forced: FORCED_PROMISE_CONSTRUCTOR$4 }, {
  Promise: PromiseConstructor
});

setToStringTag(PromiseConstructor, PROMISE, false);
setSpecies(PROMISE);

var wellKnownSymbol$4 = wellKnownSymbol$g;
var Iterators$1 = iterators;

var ITERATOR$2 = wellKnownSymbol$4('iterator');
var ArrayPrototype = Array.prototype;

// check on default Array iterator
var isArrayIteratorMethod$1 = function (it) {
  return it !== undefined && (Iterators$1.Array === it || ArrayPrototype[ITERATOR$2] === it);
};

var classof$1 = classof$6;
var getMethod$1 = getMethod$3;
var isNullOrUndefined = isNullOrUndefined$4;
var Iterators = iterators;
var wellKnownSymbol$3 = wellKnownSymbol$g;

var ITERATOR$1 = wellKnownSymbol$3('iterator');

var getIteratorMethod$2 = function (it) {
  if (!isNullOrUndefined(it)) return getMethod$1(it, ITERATOR$1)
    || getMethod$1(it, '@@iterator')
    || Iterators[classof$1(it)];
};

var call$4 = functionCall;
var aCallable$2 = aCallable$a;
var anObject$3 = anObject$c;
var tryToString$1 = tryToString$5;
var getIteratorMethod$1 = getIteratorMethod$2;

var $TypeError$2 = TypeError;

var getIterator$1 = function (argument, usingIterator) {
  var iteratorMethod = arguments.length < 2 ? getIteratorMethod$1(argument) : usingIterator;
  if (aCallable$2(iteratorMethod)) return anObject$3(call$4(iteratorMethod, argument));
  throw new $TypeError$2(tryToString$1(argument) + ' is not iterable');
};

var call$3 = functionCall;
var anObject$2 = anObject$c;
var getMethod = getMethod$3;

var iteratorClose$1 = function (iterator, kind, value) {
  var innerResult, innerError;
  anObject$2(iterator);
  try {
    innerResult = getMethod(iterator, 'return');
    if (!innerResult) {
      if (kind === 'throw') throw value;
      return value;
    }
    innerResult = call$3(innerResult, iterator);
  } catch (error) {
    innerError = true;
    innerResult = error;
  }
  if (kind === 'throw') throw value;
  if (innerError) throw innerResult;
  anObject$2(innerResult);
  return value;
};

var bind = functionBindContext;
var call$2 = functionCall;
var anObject$1 = anObject$c;
var tryToString = tryToString$5;
var isArrayIteratorMethod = isArrayIteratorMethod$1;
var lengthOfArrayLike = lengthOfArrayLike$4;
var isPrototypeOf = objectIsPrototypeOf;
var getIterator = getIterator$1;
var getIteratorMethod = getIteratorMethod$2;
var iteratorClose = iteratorClose$1;

var $TypeError$1 = TypeError;

var Result = function (stopped, result) {
  this.stopped = stopped;
  this.result = result;
};

var ResultPrototype = Result.prototype;

var iterate$2 = function (iterable, unboundFunction, options) {
  var that = options && options.that;
  var AS_ENTRIES = !!(options && options.AS_ENTRIES);
  var IS_RECORD = !!(options && options.IS_RECORD);
  var IS_ITERATOR = !!(options && options.IS_ITERATOR);
  var INTERRUPTED = !!(options && options.INTERRUPTED);
  var fn = bind(unboundFunction, that);
  var iterator, iterFn, index, length, result, next, step;

  var stop = function (condition) {
    if (iterator) iteratorClose(iterator, 'normal', condition);
    return new Result(true, condition);
  };

  var callFn = function (value) {
    if (AS_ENTRIES) {
      anObject$1(value);
      return INTERRUPTED ? fn(value[0], value[1], stop) : fn(value[0], value[1]);
    } return INTERRUPTED ? fn(value, stop) : fn(value);
  };

  if (IS_RECORD) {
    iterator = iterable.iterator;
  } else if (IS_ITERATOR) {
    iterator = iterable;
  } else {
    iterFn = getIteratorMethod(iterable);
    if (!iterFn) throw new $TypeError$1(tryToString(iterable) + ' is not iterable');
    // optimisation for array iterators
    if (isArrayIteratorMethod(iterFn)) {
      for (index = 0, length = lengthOfArrayLike(iterable); length > index; index++) {
        result = callFn(iterable[index]);
        if (result && isPrototypeOf(ResultPrototype, result)) return result;
      } return new Result(false);
    }
    iterator = getIterator(iterable, iterFn);
  }

  next = IS_RECORD ? iterable.next : iterator.next;
  while (!(step = call$2(next, iterator)).done) {
    try {
      result = callFn(step.value);
    } catch (error) {
      iteratorClose(iterator, 'throw', error);
    }
    if (typeof result == 'object' && result && isPrototypeOf(ResultPrototype, result)) return result;
  } return new Result(false);
};

var wellKnownSymbol$2 = wellKnownSymbol$g;

var ITERATOR = wellKnownSymbol$2('iterator');
var SAFE_CLOSING = false;

try {
  var called = 0;
  var iteratorWithReturn = {
    next: function () {
      return { done: !!called++ };
    },
    'return': function () {
      SAFE_CLOSING = true;
    }
  };
  iteratorWithReturn[ITERATOR] = function () {
    return this;
  };
  // eslint-disable-next-line es/no-array-from, no-throw-literal -- required for testing
  Array.from(iteratorWithReturn, function () { throw 2; });
} catch (error) { /* empty */ }

var checkCorrectnessOfIteration$1 = function (exec, SKIP_CLOSING) {
  try {
    if (!SKIP_CLOSING && !SAFE_CLOSING) return false;
  } catch (error) { return false; } // workaround of old WebKit + `eval` bug
  var ITERATION_SUPPORT = false;
  try {
    var object = {};
    object[ITERATOR] = function () {
      return {
        next: function () {
          return { done: ITERATION_SUPPORT = true };
        }
      };
    };
    exec(object);
  } catch (error) { /* empty */ }
  return ITERATION_SUPPORT;
};

var NativePromiseConstructor$1 = promiseNativeConstructor;
var checkCorrectnessOfIteration = checkCorrectnessOfIteration$1;
var FORCED_PROMISE_CONSTRUCTOR$3 = promiseConstructorDetection.CONSTRUCTOR;

var promiseStaticsIncorrectIteration = FORCED_PROMISE_CONSTRUCTOR$3 || !checkCorrectnessOfIteration(function (iterable) {
  NativePromiseConstructor$1.all(iterable).then(undefined, function () { /* empty */ });
});

var $$6 = _export;
var call$1 = functionCall;
var aCallable$1 = aCallable$a;
var newPromiseCapabilityModule$2 = newPromiseCapability$2;
var perform$1 = perform$3;
var iterate$1 = iterate$2;
var PROMISE_STATICS_INCORRECT_ITERATION$1 = promiseStaticsIncorrectIteration;

// `Promise.all` method
// https://tc39.es/ecma262/#sec-promise.all
$$6({ target: 'Promise', stat: true, forced: PROMISE_STATICS_INCORRECT_ITERATION$1 }, {
  all: function all(iterable) {
    var C = this;
    var capability = newPromiseCapabilityModule$2.f(C);
    var resolve = capability.resolve;
    var reject = capability.reject;
    var result = perform$1(function () {
      var $promiseResolve = aCallable$1(C.resolve);
      var values = [];
      var counter = 0;
      var remaining = 1;
      iterate$1(iterable, function (promise) {
        var index = counter++;
        var alreadyCalled = false;
        remaining++;
        call$1($promiseResolve, C, promise).then(function (value) {
          if (alreadyCalled) return;
          alreadyCalled = true;
          values[index] = value;
          --remaining || resolve(values);
        }, reject);
      });
      --remaining || resolve(values);
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});

var $$5 = _export;
var FORCED_PROMISE_CONSTRUCTOR$2 = promiseConstructorDetection.CONSTRUCTOR;
var NativePromiseConstructor = promiseNativeConstructor;
var getBuiltIn$1 = getBuiltIn$7;
var isCallable = isCallable$j;
var defineBuiltIn = defineBuiltIn$6;

var NativePromisePrototype = NativePromiseConstructor && NativePromiseConstructor.prototype;

// `Promise.prototype.catch` method
// https://tc39.es/ecma262/#sec-promise.prototype.catch
$$5({ target: 'Promise', proto: true, forced: FORCED_PROMISE_CONSTRUCTOR$2, real: true }, {
  'catch': function (onRejected) {
    return this.then(undefined, onRejected);
  }
});

// makes sure that native promise-based APIs `Promise#catch` properly works with patched `Promise#then`
if (isCallable(NativePromiseConstructor)) {
  var method = getBuiltIn$1('Promise').prototype['catch'];
  if (NativePromisePrototype['catch'] !== method) {
    defineBuiltIn(NativePromisePrototype, 'catch', method, { unsafe: true });
  }
}

var $$4 = _export;
var call = functionCall;
var aCallable = aCallable$a;
var newPromiseCapabilityModule$1 = newPromiseCapability$2;
var perform = perform$3;
var iterate = iterate$2;
var PROMISE_STATICS_INCORRECT_ITERATION = promiseStaticsIncorrectIteration;

// `Promise.race` method
// https://tc39.es/ecma262/#sec-promise.race
$$4({ target: 'Promise', stat: true, forced: PROMISE_STATICS_INCORRECT_ITERATION }, {
  race: function race(iterable) {
    var C = this;
    var capability = newPromiseCapabilityModule$1.f(C);
    var reject = capability.reject;
    var result = perform(function () {
      var $promiseResolve = aCallable(C.resolve);
      iterate(iterable, function (promise) {
        call($promiseResolve, C, promise).then(capability.resolve, reject);
      });
    });
    if (result.error) reject(result.value);
    return capability.promise;
  }
});

var $$3 = _export;
var newPromiseCapabilityModule = newPromiseCapability$2;
var FORCED_PROMISE_CONSTRUCTOR$1 = promiseConstructorDetection.CONSTRUCTOR;

// `Promise.reject` method
// https://tc39.es/ecma262/#sec-promise.reject
$$3({ target: 'Promise', stat: true, forced: FORCED_PROMISE_CONSTRUCTOR$1 }, {
  reject: function reject(r) {
    var capability = newPromiseCapabilityModule.f(this);
    var capabilityReject = capability.reject;
    capabilityReject(r);
    return capability.promise;
  }
});

var anObject = anObject$c;
var isObject$1 = isObject$a;
var newPromiseCapability = newPromiseCapability$2;

var promiseResolve$1 = function (C, x) {
  anObject(C);
  if (isObject$1(x) && x.constructor === C) return x;
  var promiseCapability = newPromiseCapability.f(C);
  var resolve = promiseCapability.resolve;
  resolve(x);
  return promiseCapability.promise;
};

var $$2 = _export;
var getBuiltIn = getBuiltIn$7;
var FORCED_PROMISE_CONSTRUCTOR = promiseConstructorDetection.CONSTRUCTOR;
var promiseResolve = promiseResolve$1;

getBuiltIn('Promise');

// `Promise.resolve` method
// https://tc39.es/ecma262/#sec-promise.resolve
$$2({ target: 'Promise', stat: true, forced: FORCED_PROMISE_CONSTRUCTOR }, {
  resolve: function resolve(x) {
    return promiseResolve(this, x);
  }
});

var $$1 = _export;
var $includes = arrayIncludes.includes;
var fails = fails$i;
var addToUnscopables = addToUnscopables$2;

// FF99+ bug
var BROKEN_ON_SPARSE = fails(function () {
  // eslint-disable-next-line es/no-array-prototype-includes -- detection
  return !Array(1).includes();
});

// `Array.prototype.includes` method
// https://tc39.es/ecma262/#sec-array.prototype.includes
$$1({ target: 'Array', proto: true, forced: BROKEN_ON_SPARSE }, {
  includes: function includes(el /* , fromIndex = 0 */) {
    return $includes(this, el, arguments.length > 1 ? arguments[1] : undefined);
  }
});

// https://tc39.es/ecma262/#sec-array.prototype-@@unscopables
addToUnscopables('includes');

var isObject = isObject$a;
var classof = classofRaw$2;
var wellKnownSymbol$1 = wellKnownSymbol$g;

var MATCH$1 = wellKnownSymbol$1('match');

// `IsRegExp` abstract operation
// https://tc39.es/ecma262/#sec-isregexp
var isRegexp = function (it) {
  var isRegExp;
  return isObject(it) && ((isRegExp = it[MATCH$1]) !== undefined ? !!isRegExp : classof(it) === 'RegExp');
};

var isRegExp = isRegexp;

var $TypeError = TypeError;

var notARegexp = function (it) {
  if (isRegExp(it)) {
    throw new $TypeError("The method doesn't accept regular expressions");
  } return it;
};

var wellKnownSymbol = wellKnownSymbol$g;

var MATCH = wellKnownSymbol('match');

var correctIsRegexpLogic = function (METHOD_NAME) {
  var regexp = /./;
  try {
    '/./'[METHOD_NAME](regexp);
  } catch (error1) {
    try {
      regexp[MATCH] = false;
      return '/./'[METHOD_NAME](regexp);
    } catch (error2) { /* empty */ }
  } return false;
};

var $ = _export;
var uncurryThis = functionUncurryThis;
var notARegExp = notARegexp;
var requireObjectCoercible = requireObjectCoercible$4;
var toString = toString$4;
var correctIsRegExpLogic = correctIsRegexpLogic;

var stringIndexOf = uncurryThis(''.indexOf);

// `String.prototype.includes` method
// https://tc39.es/ecma262/#sec-string.prototype.includes
$({ target: 'String', proto: true, forced: !correctIsRegExpLogic('includes') }, {
  includes: function includes(searchString /* , position = 0 */) {
    return !!~stringIndexOf(
      toString(requireObjectCoercible(this)),
      toString(notARegExp(searchString)),
      arguments.length > 1 ? arguments[1] : undefined
    );
  }
});

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * */
const getLayerUpdateInfo = (wmLayer, mapId) => {
  const origin = 'ReactMapViewParseLayer';
  const map = webmapUtils.getWMJSMapById(mapId);
  const mapDimensions = map === null || map === void 0 ? void 0 : map.mapdimensions.map(mapdim => {
    return {
      units: mapdim.units,
      currentValue: mapdim.currentValue,
      name: mapdim.name
    };
  });
  const layerDimensions = wmLayer.dimensions.map(dim => {
    const {
      name,
      units,
      synced
    } = dim;
    return {
      name,
      units,
      currentValue: dim.getValue(),
      minValue: dim.getFirstValue(),
      maxValue: dim.getLastValue(),
      timeInterval: dim.getDimInterval(),
      values: dim.getValues(),
      synced
    };
  });
  /* Update list of layers for service */
  const updateObject = {
    origin,
    layerStyle: {
      origin,
      layerId: wmLayer.ReactWMJSLayerId,
      style: wmLayer.currentStyle
    },
    mapDimensions: {
      mapId,
      origin,
      dimensions: mapDimensions !== null && mapDimensions !== void 0 ? mapDimensions : []
    },
    layerDimensions: {
      origin,
      layerId: wmLayer.id,
      dimensions: layerDimensions
    }
  };
  return updateObject;
};

/**
 * Returns filtered list of props with geoJson
 * @param children React.ReactNode, layers with geoJson
 */
const getFeatureLayers = children => children && Array.isArray(children) ? children
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
.reduce((acc, val) => acc.concat(val), []).filter(c => c && c.props && c.props.geojson).map(c => c.props).reverse() : [];
/**
 * Returns true if this is a maplayer and not a baselayer or overlayer
 * @param layer The LayerFoundation object, or the props from the ReactWMJSLayer
 */
const isAMapLayer = layer => layer.layerType === LayerType.mapLayer;
/**
 * Returns true if this is a geojsonlayer (layer containing geojson field)
 * @param layer The LayerFoundation object, or the props from the ReactWMJSLayer
 */
const isAGeoJSONLayer = layer => layer.geojson !== undefined;
function getIsInsideAcceptanceTime(acceptanceTimeInMinutes, mapDimensions, layerDimensions) {
  var _a, _b;
  const mapCurrentTime = (_a = mapDimensions === null || mapDimensions === void 0 ? void 0 : mapDimensions.find(dimension => dimension.name === 'time')) === null || _a === void 0 ? void 0 : _a.currentValue;
  const layerCurrentTime = (_b = layerDimensions === null || layerDimensions === void 0 ? void 0 : layerDimensions.find(dimension => dimension.name === 'time')) === null || _b === void 0 ? void 0 : _b.currentValue;
  if (acceptanceTimeInMinutes === undefined || !mapCurrentTime || !layerCurrentTime) {
    return true;
  }
  const minutesBetween = differenceInMinutes(new Date(mapCurrentTime), new Date(layerCurrentTime));
  if (Math.abs(minutesBetween) > acceptanceTimeInMinutes) {
    return false;
  }
  return true;
}
const makeLayerPropListFromChildren = (children, mapId) => {
  const myChildren = [];
  const takenIds = new Set();
  const wmjsMap = getWMJSMapById(mapId);
  React__default.Children.forEach(children, child => {
    var _a;
    if (child) {
      const {
        props: childProps
      } = child;
      if (childProps && childProps.id) {
        if (!takenIds.has(childProps.id)) {
          myChildren.push(childProps);
          takenIds.add(childProps.id);
        } else {
          (_a = childProps.onLayerError) === null || _a === void 0 ? void 0 : _a.call(childProps, childProps, new Error(`Duplicate layer id "${childProps.id}" encountered within this mapTypes.WebMap`), wmjsMap);
          console.warn('ReactWMJSLayer has a duplicate id', child);
        }
      } else {
        debugLogger(DebugType.Warning, 'ReactElement child ignored: has no props or id', child);
      }
    }
  });
  myChildren.reverse();
  return myChildren;
};
const parseWMJSLayer = (wmLayer, forceReload) => {
  return new Promise((resolve, reject) => {
    const callback = () => {
      if (wmLayer.hasError) {
        reject(new Error(wmLayer.lastError));
      } else {
        resolve(wmLayer);
      }
    };
    wmLayer.parseLayer(callback, forceReload, 'ReactMapView parseWMJSLayer');
  });
};
const setWMLayerPropsBasedOnChildProps = (child, wmLayer, mapViewProps) => {
  let needsRedraw = false;
  /* Set the name of the ADAGUC WMJSLayer */
  /* Set the Opacity of the ADAGUC WMJSLayer */
  if (child.opacity !== undefined && wmLayer.opacity !== child.opacity) {
    wmLayer.setOpacity(child.opacity);
    needsRedraw = false;
  }
  /* Set the Style of the ADAGUC WMJSLayer */
  if (child.style !== undefined && wmLayer.currentStyle !== child.style) {
    wmLayer.setStyle(child.style);
    needsRedraw = true;
  }
  const {
    enabled,
    acceptanceTimeInMinutes,
    dimensions: layerDimensions
  } = child;
  const isInsideAcceptanceTime = getIsInsideAcceptanceTime(acceptanceTimeInMinutes, mapViewProps.dimensions, layerDimensions);
  if (enabled !== undefined) {
    const wmLayerShouldBeShown = isInsideAcceptanceTime && enabled;
    if (wmLayerShouldBeShown !== wmLayer.enabled) {
      wmLayer.display(wmLayerShouldBeShown);
      needsRedraw = true;
    }
  }
  /* Set the dimensions of the ADAGUC WMJSLayer */
  if (child.dimensions !== undefined) {
    for (let d = 0; d < child.dimensions.length; d += 1) {
      const dim = child.dimensions[d];
      const wmjsDim = wmLayer.getDimension(dim.name);
      if (wmjsDim) {
        if (wmjsDim.currentValue !== dim.currentValue) {
          wmLayer.setDimension(dim.name, dim.currentValue, false);
          needsRedraw = true;
        }
        if (wmjsDim.synced !== dim.synced) {
          wmjsDim.synced = dim.synced;
          needsRedraw = true;
        }
      } else {
        debugLogger(DebugType.Warning, `MapView: Dimension does not exist, skipping ${child.name} :: ${dim.name} = ${dim.currentValue}`);
      }
    }
  }
  return needsRedraw;
};
const addWMLayerPropsBasedOnChildProps = (child, mapId, props, onUpdateLayerInformation) => {
  const wmjsMap = getWMJSMapById(mapId);
  const keepOnTop = child.layerType === LayerType.overLayer || false;
  const newWMLayer = new WMLayer(Object.assign(Object.assign({}, child), {
    keepOnTop
  }));
  registerWMLayer(newWMLayer, child.id);
  newWMLayer.ReactWMJSLayerId = child.id;
  const isBaselayer = !isAMapLayer(child) && !isAGeoJSONLayer(child);
  if (isBaselayer) {
    /* Add ADAGUC WebMapJS Baselayer */
    const baseLayers = wmjsMap.getBaseLayers();
    baseLayers.unshift(newWMLayer);
    wmjsMap.setBaseLayers(baseLayers);
  } else {
    /* Add ADAGUC WebMapJS Layer */
    wmjsMap.addLayer(newWMLayer).then(() => {
      if (newWMLayer.hasError === true) {
        child.onLayerError && child.onLayerError(newWMLayer, new Error(newWMLayer.lastError));
      }
      setWMLayerPropsBasedOnChildProps(child, newWMLayer, props);
      const layerInfo = getLayerUpdateInfo(newWMLayer, mapId);
      onUpdateLayerInformation && onUpdateLayerInformation(layerInfo);
      child.onLayerReady && child.onLayerReady(newWMLayer, wmjsMap);
      wmjsMap.draw();
    }).catch(e => {
      child.onLayerError && child.onLayerError(e);
    });
  }
};
const ORIGIN_REACTMAPVIEW_UTILS_ONUPDATELAYERINFO = 'ORIGIN_REACTMAPVIEW_UTILS_ONUPDATELAYERINFO';
const removeWMLayerFromMap = (wmLayer, mapId, onUpdateLayerInformation) => {
  // This will call the remove property of the WMJSLayer, which will adjust the layers array of WebMapJS */
  wmLayer.remove();
  /// Trigger update of all map dimensions
  const wmjsMap = getWMJSMapById(mapId);
  onUpdateLayerInformation && onUpdateLayerInformation({
    origin: ORIGIN_REACTMAPVIEW_UTILS_ONUPDATELAYERINFO,
    layerStyle: null,
    layerDimensions: null,
    mapDimensions: {
      origin,
      mapId,
      dimensions: wmjsMap.mapdimensions.map(({
        name,
        units,
        currentValue,
        synced
      }) => {
        return {
          name,
          units,
          currentValue,
          synced
        };
      })
    }
  });
};
// TODO: Improve performance in https://gitlab.com/opengeoweb/opengeoweb/-/issues/4566
const orderLayers = (wmjsMap, _reactMapViewLayers) => {
  const mapLayers = wmjsMap.getLayers();
  const mapLayersIds = mapLayers.map(ml => ml.id);
  const filtered = _reactMapViewLayers.filter(_l => mapLayersIds.includes(_l.id)).reverse();
  const order = mapLayers.map(wmLayer => {
    return filtered.findIndex(reactMapViewLayer => {
      return reactMapViewLayer.id === wmLayer.id;
    });
  });
  const wasReordered = wmjsMap.reorderLayers(order);
  return wasReordered;
};

const getDisplayText = currentAdagucTime => {
  const timeFormat = 'eee dd MMM yyyy HH:mm [UTC]';
  const adagucTime = dateUtils.utc(currentAdagucTime);
  const adagucTimeFormatted = dateUtils.dateToString(adagucTime, timeFormat);
  return adagucTimeFormatted;
};
const ORIGIN_REACTMAPVIEW_ONMAPCHANGEDIMENSION = 'ORIGIN_REACTMAPVIEW_ONMAPCHANGEDIMENSION';
const ORIGIN_REACTMAPVIEW_ONUPDATELAYERINFO = 'ORIGIN_REACTMAPVIEW_ONUPDATELAYERINFO';
class ReactMapView extends React.Component {
  constructor(props) {
    super(props);
    this.adaguc = {
      initialized: false,
      baseLayers: [],
      oldbbox: undefined,
      currentWidth: -1,
      currentHeight: -1,
      currentChildren: []
    };
    this.refetchTimer = null;
    this.onStartRefetchTimer = () => {
      /*
        TODO, Maarten Plieger, 2021-09-02, https://gitlab.com/opengeoweb/opengeoweb/-/issues/1159:
        This causes many issues, we should improve the update strategy.
        Certain presets have >20 layers. This causes 20 times parsing the GetCapabilities for a minor update of the store.
           
      */
      this.clearRefetchTimer();
      const fetchSpeed = 60000; // 1 minute
      this.refetchTimer = setInterval(() => {
        const {
          mapId
        } = this.props;
        const wmjsMap = webmapUtils.getWMJSMapById(mapId);
        const layers = wmjsMap.getLayers();
        layers.forEach(layer => {
          if (layer.enabled !== false) {
            parseWMJSLayer(layer, true).then(wmLayer => {
              const {
                props
              } = this;
              const {
                mapId,
                onUpdateLayerInformation
              } = props;
              const layerInfo = getLayerUpdateInfo(wmLayer, mapId);
              onUpdateLayerInformation && onUpdateLayerInformation(layerInfo);
            }).catch(e => {
              console.error(e);
            });
          }
        });
      }, fetchSpeed);
    };
    this.clearRefetchTimer = () => {
      if (this.refetchTimer) {
        clearInterval(this.refetchTimer);
      }
    };
    this.state = {
      adagucInitialised: false
    };
    this.resize = this.resize.bind(this);
    this.handleWindowResize = this.handleWindowResize.bind(this);
    this.drawDebounced = debounce(600, this.drawDebounced);
    this.updateWMJSMapProps = this.updateWMJSMapProps.bind(this);
    this.mountWMJSMap = this.mountWMJSMap.bind(this);
    this.onAfterSetBBoxListener = this.onAfterSetBBoxListener.bind(this);
    this.onUpdateBBoxListener = this.onUpdateBBoxListener.bind(this);
    this.onDimChangeListener = this.onDimChangeListener.bind(this);
    this.adagucContainerRef = /*#__PURE__*/React.createRef();
    this.adagucWebMapJSRef = /*#__PURE__*/React.createRef();
  }
  componentDidMount() {
    const {
      shouldAutoFetch,
      onWMJSMount,
      mapId
    } = this.props;
    /* If this components re-mounts, make sure that the initialized state is set back to false */
    this.setState({
      adagucInitialised: false
    });
    window.addEventListener('resize', this.handleWindowResize);
    this.mountWMJSMap();
    if (shouldAutoFetch) {
      this.onStartRefetchTimer();
    }
    onWMJSMount(mapId);
  }
  componentDidUpdate(prevProps) {
    this.updateWMJSMapProps(prevProps, this.props);
  }
  componentWillUnmount() {
    const {
      mapId
    } = this.props;
    window.removeEventListener('resize', this.handleWindowResize);
    this.clearRefetchTimer();
    webmapUtils.unRegisterWMJSMap(mapId);
    /* Reset adaguc properties */
    this.adaguc.initialized = false;
    this.adaguc.currentHeight = -1;
    this.adaguc.currentWidth = -1;
    this.adaguc.baseLayers = [];
    this.adaguc.oldbbox = new WMBBOX();
    this.adaguc.currentChildren = [];
  }
  handleWindowResize() {
    this.resize();
  }
  onDimChangeListener() {
    const {
      mapId,
      onMapChangeDimension
    } = this.props;
    const wmjsMap = webmapUtils.getWMJSMapById(mapId);
    if (wmjsMap) {
      const timeDimension = wmjsMap.getDimension('time');
      if (timeDimension) {
        onMapChangeDimension({
          mapId,
          origin: ORIGIN_REACTMAPVIEW_ONMAPCHANGEDIMENSION,
          dimension: {
            name: 'time',
            currentValue: timeDimension.currentValue
          }
        });
      }
    }
  }
  onAfterSetBBoxListener() {
    /* Update the map after 100 ms */
    window.setTimeout(() => {
      const {
        bbox: MapViewBBOX,
        mapId,
        onMapZoomEnd
      } = this.props;
      const wmjsMap = webmapUtils.getWMJSMapById(mapId);
      if (!wmjsMap) {
        return;
      }
      const projectionInfo = wmjsMap.getProjection();
      /* Trigger onMapZoomEnd callback */
      const mapBBOX = projectionInfo.bbox;
      if (MapViewBBOX.left !== mapBBOX.left || MapViewBBOX.right !== mapBBOX.right || MapViewBBOX.bottom !== mapBBOX.bottom || MapViewBBOX.top !== mapBBOX.top) {
        onMapZoomEnd({
          mapId,
          bbox: {
            left: projectionInfo.bbox.left,
            bottom: projectionInfo.bbox.bottom,
            right: projectionInfo.bbox.right,
            top: projectionInfo.bbox.top
          },
          srs: projectionInfo.srs
        });
      }
    }, 100);
  }
  onUpdateBBoxListener(newBbox) {
    const oldbbox = this.adaguc.oldbbox || new WMBBOX(ReactMapView.defaultProps.bbox);
    if (oldbbox.left !== newBbox.left || oldbbox.right !== newBbox.right || oldbbox.top !== newBbox.top || oldbbox.bottom !== newBbox.bottom) {
      oldbbox.left = newBbox.left;
      oldbbox.right = newBbox.right;
      oldbbox.top = newBbox.top;
      oldbbox.bottom = newBbox.bottom;
      this.adaguc.oldbbox = new WMBBOX(oldbbox);
    }
  }
  updateWMJSMapProps(prevProps, props) {
    if (!props) {
      return;
    }
    const {
      mapId,
      children
    } = props;
    if (prevProps && prevProps.mapId !== props.mapId) {
      console.warn('MapId has changed on an already mounted map!');
      const prevWMJSMap = webmapUtils.getWMJSMapById(prevProps.mapId);
      if (prevWMJSMap) {
        webmapUtils.registerWMJSMap(prevWMJSMap, mapId);
      }
    }
    const wmjsMap = webmapUtils.getWMJSMapById(mapId);
    if (!wmjsMap) {
      console.warn(`No wmjsMap ${mapId}`);
      return;
    }
    let needsRedraw = false;
    /* Check map props */
    if (!prevProps || prevProps.showLegend !== props.showLegend) {
      wmjsMap.displayLegendInMap(props.showLegend !== false);
    }
    if (!prevProps || prevProps.showScaleBar !== props.showScaleBar) {
      wmjsMap.displayScaleBarInMap(props.showScaleBar !== false);
      needsRedraw = true;
    }
    if (!prevProps || prevProps.timestep !== props.timestep) {
      wmjsMap.timestepInMinutes = props.timestep;
    }
    /* Check map dimensions */
    if (!prevProps || prevProps.dimensions !== props.dimensions) {
      if (props.dimensions) {
        for (let d = 0; d < props.dimensions.length; d += 1) {
          const propDimension = props.dimensions[d];
          const mapDim = wmjsMap.getDimension(propDimension.name);
          if (mapDim && mapDim.currentValue !== propDimension.currentValue || !mapDim) {
            wmjsMap.setDimension(propDimension.name, propDimension.currentValue, false, false);
          }
          if (props.displayTimeInMap && propDimension.name === 'time' && propDimension.currentValue !== undefined) {
            needsRedraw = true;
            const displayText = getDisplayText(propDimension.currentValue);
            wmjsMap.setTimeOffset(displayText);
          }
        }
      }
    }
    /* Check if srs and BBOX is updated */
    if (!prevProps || prevProps.bbox !== props.bbox) {
      if (props.bbox.left !== undefined) {
        const projectionInfo = wmjsMap.getProjection();
        const mapBBOX = projectionInfo.bbox;
        if (props.bbox.left !== mapBBOX.left || props.bbox.right !== mapBBOX.right || props.bbox.bottom !== mapBBOX.bottom || props.bbox.top !== mapBBOX.top) {
          wmjsMap.suspendEvent('onupdatebbox');
          wmjsMap.setProjection(props.srs, new WMBBOX(props.bbox));
          wmjsMap.resumeEvent('onupdatebbox');
          wmjsMap.draw();
        }
      }
    }
    /* Check if srs */
    if (!prevProps || prevProps.srs !== props.srs) {
      if (props.bbox.left !== undefined) {
        wmjsMap.suspendEvent('onupdatebbox');
        wmjsMap.setProjection(props.srs, new WMBBOX(props.bbox));
        wmjsMap.resumeEvent('onupdatebbox');
        wmjsMap.draw();
      }
    }
    /* Check display/hide map cursor */
    if (!prevProps || prevProps.displayMapPin !== props.displayMapPin) {
      if (props.displayMapPin === true) {
        wmjsMap.getMapPin().showMapPin();
      } else if (props.displayMapPin === false) {
        wmjsMap.getMapPin().hideMapPin();
      }
    }
    /* Set map cursor location */
    if (!prevProps || prevProps.mapPinLocation !== props.mapPinLocation) {
      if (props.mapPinLocation) {
        wmjsMap.getMapPin().positionMapPinByLatLon({
          x: props.mapPinLocation.lon,
          y: props.mapPinLocation.lat
        });
        wmjsMap.draw();
      }
    }
    /* Set disable map pin */
    if (!prevProps || prevProps.disableMapPin !== props.disableMapPin) {
      if (props.disableMapPin === true) {
        wmjsMap.getMapPin().setMapPinDisabled();
      } else if (props.disableMapPin === false) {
        wmjsMap.getMapPin().setMapPinEnabled();
      }
      wmjsMap.draw();
    }
    /* Change the animation delay */
    if (!prevProps || prevProps.animationDelay !== props.animationDelay) {
      if (props.animationDelay) {
        if (typeof wmjsMap.setAnimationDelay === 'function') {
          wmjsMap.setAnimationDelay(props.animationDelay);
        }
      }
    }
    /* Check if layer metadata should be shown */
    if (!prevProps || prevProps.showLayerInfo !== props.showLayerInfo) {
      if (props.showLayerInfo) {
        wmjsMap.showLayerInfo = props.showLayerInfo;
      }
    }
    const myChildren = makeLayerPropListFromChildren(children, mapId);
    // Only enter this loop if there are changes.
    if (!_.isEqual(myChildren, this.adaguc.currentChildren)) {
      this.adaguc.currentChildren = myChildren;
      // ReactWMJSLayer Layer Childs: Detect all ReactLayers connected to WMJSLayers, remove WMJSLayer if there is no ReactLayer
      const wmjsLayers = wmjsMap.getLayers();
      const {
        onUpdateLayerInformation,
        mapId: _mapId
      } = props;
      wmjsLayers.forEach(wmLayer => {
        const isWMLayerInReactMapViewChildren = myChildren.find(child => child.id === wmLayer.ReactWMJSLayerId);
        if (!isWMLayerInReactMapViewChildren) {
          removeWMLayerFromMap(wmLayer, _mapId, onUpdateLayerInformation);
        }
      });
      // ReactWMJSLayer BaseLayer Childs: For the baseLayers, detect all ReactLayers connected to WMJSLayers, remove WMJSLayer if there is no ReactLayer
      const webmapJSBaselayers = wmjsMap.getBaseLayers();
      for (let l = 0; l < webmapJSBaselayers.length; l += 1) {
        let wmjsBaseLayers = wmjsMap.getBaseLayers();
        if (myChildren.filter(child => child.id === wmjsBaseLayers[l].ReactWMJSLayerId && child.name).length === 0) {
          /*  TODO (Maarten Plieger, 2020-03-19):The remove property for the baselayer is not working yet */
          wmjsBaseLayers.splice(l, 1);
          wmjsMap.setBaseLayers(wmjsBaseLayers);
          wmjsBaseLayers = wmjsMap.getBaseLayers();
        }
      }
      // Loop through all React layers and update WMJSLayer properties where needed
      myChildren.filter(child => child.name).forEach(child => {
        const wmLayer = getWMLayerById(child.id);
        const layerIsInThisMap = wmjsMap.hasLayer(wmLayer);
        if (!layerIsInThisMap) {
          // Add a new layer, it is not in the this map
          addWMLayerPropsBasedOnChildProps(child, _mapId, props, onUpdateLayerInformation);
        } else {
          // Handle existing layer
          if (child.name !== undefined && wmLayer.name !== child.name) {
            // Handle layer name change
            wmLayer.setName(child.name).then(() => __awaiter(this, void 0, void 0, function* () {
              yield parseWMJSLayer(wmLayer, false);
              const layerInfo = getLayerUpdateInfo(wmLayer, _mapId);
              setWMLayerPropsBasedOnChildProps(child, wmLayer, props);
              onUpdateLayerInformation && onUpdateLayerInformation(layerInfo);
              wmjsMap.draw();
            })).catch(e => {
              child.onLayerError && child.onLayerError(wmLayer, e);
            });
            needsRedraw = true;
          }
          if (setWMLayerPropsBasedOnChildProps(child, wmLayer, props) === true) {
            needsRedraw = true;
          }
        }
      });
      // Check if order of layers has changed
      if (orderLayers(wmjsMap, myChildren)) {
        needsRedraw = true;
      }
      if (needsRedraw) {
        wmjsMap.draw();
      }
    }
  }
  drawDebounced() {
    const {
      mapId
    } = this.props;
    const wmjsMap = webmapUtils.getWMJSMapById(mapId);
    wmjsMap.getListener().suspendEvents();
    wmjsMap.draw();
    wmjsMap.getListener().resumeEvents();
  }
  mountWMJSMap() {
    const {
      mapId,
      listeners,
      srs,
      bbox,
      onMapPinChangeLocation
    } = this.props;
    /* Check if we have something to mount WMJSMap on */
    if (this.adagucWebMapJSRef.current === null) {
      console.warn('No this.adagucWebMapJSRef.current  yet ');
      return null;
    }
    /* Check if webmapjs was not already initialized */
    const existingWMJSMap = webmapUtils.getWMJSMapById(mapId);
    if (existingWMJSMap) {
      console.warn(`Somehow ${mapId}webmapUtils.unRegisterWMJSMap already exists`);
      webmapUtils.unRegisterWMJSMap(mapId);
    }
    const wmjsMap = new WMJSMap(this.adagucWebMapJSRef.current);
    this.adaguc.currentChildren = [];
    this.adaguc.baseLayers = [];
    webmapUtils.registerWMJSMap(wmjsMap, mapId);
    this.adaguc.initialized = true;
    /* Enable to show actual layer properties in the map */
    wmjsMap.showLayerInfo = false;
    wmjsMap.removeAllLayers();
    if (srs) {
      wmjsMap.setProjection(srs, new WMBBOX(bbox));
    }
    wmjsMap.setWMTileRendererTileSettings(tilesettings);
    if (listeners) {
      listeners.forEach(listener => {
        wmjsMap.addListener(listener.name, data => {
          listener.callbackfunction(wmjsMap, data);
        }, listener.keep);
      });
    }
    wmjsMap.addListener('ondimchange', () => {
      this.onDimChangeListener();
    }, true);
    wmjsMap.addListener('onmapready', () => {
      this.setState({
        adagucInitialised: true
      });
    });
    wmjsMap.addListener('aftersetbbox', () => {
      this.onAfterSetBBoxListener();
    }, true);
    wmjsMap.addListener('onupdatebbox', newBbox => {
      this.onUpdateBBoxListener(newBbox);
    }, true);
    wmjsMap.addListener('onsetmappin', mapPinLatLonCoordinate => {
      // only change location when mapPin is visible and enabled
      if (onMapPinChangeLocation &&
      // eslint-disable-next-line react/destructuring-assignment
      this.props.displayMapPin &&
      // eslint-disable-next-line react/destructuring-assignment
      !this.props.disableMapPin) {
        onMapPinChangeLocation({
          mapPinLocation: {
            lat: mapPinLatLonCoordinate.lat,
            lon: mapPinLatLonCoordinate.lon,
            projectionX: mapPinLatLonCoordinate.projectionX,
            projectionY: mapPinLatLonCoordinate.projectionY,
            screenOffsetX: mapPinLatLonCoordinate.screenOffsetX,
            screenOffsetY: mapPinLatLonCoordinate.screenOffsetY,
            srs: mapPinLatLonCoordinate.srs
          },
          mapId
        });
      }
    }, true);
    this.resize();
    wmjsMap.draw();
    this.updateWMJSMapProps(null, this.props);
    return wmjsMap;
  }
  resize() {
    const element = this.adagucContainerRef.current;
    if (element) {
      const newWidth = element.clientWidth;
      const newHeight = element.clientHeight;
      if (this.adaguc.currentWidth !== newWidth || this.adaguc.currentHeight !== newHeight) {
        this.adaguc.currentWidth = newWidth;
        this.adaguc.currentHeight = newHeight;
        const {
          mapId
        } = this.props;
        const wmjsMap = webmapUtils.getWMJSMapById(mapId);
        wmjsMap.setSize(newWidth, newHeight);
      }
    }
  }
  render() {
    const {
      passiveMap,
      children,
      onClick,
      mapId
    } = this.props;
    const {
      adagucInitialised
    } = this.state;
    const featureLayers = getFeatureLayers(children);
    // TODO: Improve in https://gitlab.com/opengeoweb/opengeoweb/-/issues/4579
    featureLayers.forEach(featureLayer => {
      const wmFeatureLayer = getWMLayerById(featureLayer.id);
      if (!wmFeatureLayer) {
        registerWMLayer(new WMLayer(Object.assign({
          layerType: LayerType.featureLayer,
          service: ''
        }, featureLayer)), featureLayer.id);
      } else if (featureLayer.geojson) {
        wmFeatureLayer.geojson = featureLayer.geojson;
      }
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "MapView",
      style: {
        height: '100%',
        width: '100%',
        border: 'none',
        display: 'block',
        overflow: 'hidden'
      }
    }, /*#__PURE__*/React.createElement("div", {
      ref: this.adagucContainerRef,
      style: {
        minWidth: 'inherit',
        minHeight: 'inherit',
        width: 'inherit',
        height: 'inherit',
        overflow: 'hidden',
        display: 'block',
        border: 'none'
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "MapViewComponent",
      style: {
        position: 'absolute',
        overflow: 'hidden',
        display: 'block',
        padding: '0',
        margin: '0',
        zIndex: 10
      }
    }, /*#__PURE__*/React.createElement("div", {
      role: "application",
      "aria-label": "map",
      ref: this.adagucWebMapJSRef
    })), /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'absolute',
        overflow: 'hidden',
        display: 'block',
        padding: '0',
        margin: '0',
        zIndex: 100
      }
    }, /*#__PURE__*/React.createElement("div", null, children), adagucInitialised && featureLayers && featureLayers.length ? ( /*#__PURE__*/React.createElement(MapDrawContainer, {
      featureLayers: featureLayers,
      mapId: mapId
    })) : null), passiveMap && (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/control-has-associated-label, jsx-a11y/interactive-supports-focus
    React.createElement("div", {
      style: {
        position: 'absolute',
        overflow: 'hidden',
        display: 'block',
        padding: '0',
        margin: '0',
        zIndex: 100,
        width: '100%',
        height: '100%'
      },
      onClick: onClick,
      role: "button"
    }))));
  }
}
ReactMapView.defaultProps = {
  srs: 'EPSG:3857',
  bbox: {
    left: -2324980.5498391856,
    bottom: 5890854.775012179,
    right: 6393377.702660825,
    top: 11652109.058827976
  },
  shouldAutoFetch: true,
  displayMapPin: false,
  disableMapPin: false,
  onWMJSMount: () => {},
  onMapChangeDimension: () => {
    /* nothing */
  },
  onUpdateLayerInformation: () => {
    /* nothing */
  },
  onMapZoomEnd: () => {
    /* nothing */
  }
};

const MapControlButton = _a => {
  var {
      onClick,
      children,
      title,
      placement = 'right',
      isActive = false
    } = _a,
    props = __rest(_a, ["onClick", "children", "title", "placement", "isActive"]);
  return /*#__PURE__*/React.createElement(CustomIconButton, Object.assign({
    variant: "tool",
    tooltipProps: {
      placement,
      title
    },
    onClick: onClick,
    isSelected: isActive
  }, props), children);
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * */
const ZoomControls = ({
  onZoomIn,
  onZoomOut,
  onZoomReset
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MapControlButton, {
    title: "Reset zoom",
    "data-testid": "zoom-reset",
    onClick: onZoomReset
  }, /*#__PURE__*/React.createElement(Home, null)), /*#__PURE__*/React.createElement(MapControlButton, {
    title: "Zoom in",
    "data-testid": "zoom-in",
    onClick: onZoomIn
  }, /*#__PURE__*/React.createElement(Add, null)), /*#__PURE__*/React.createElement(MapControlButton, {
    title: "Zoom out",
    "data-testid": "zoom-out",
    onClick: onZoomOut
  }, /*#__PURE__*/React.createElement(Minus, null)));
};

const MapControls = _a => {
  var {
      children
    } = _a,
    props = __rest(_a, ["children"]);
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    sx: {
      position: 'absolute',
      overflow: 'visible',
      display: 'block',
      left: 0,
      margin: 1,
      zIndex: 50,
      top: '96px',
      userSelect: 'none'
    }
  }, props), /*#__PURE__*/React.createElement(Grid, {
    sx: {
      '& > button': {
        marginBottom: 1
      }
    },
    container: true,
    direction: "column"
  }, children));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
const getTimeDimension = dimensions => dimensions.find(dimension => dimension.name === 'time' && dimension.currentValue !== undefined);
const formatTime = (time, timeFormat) => dateUtils.dateToString(dateUtils.utc(time), timeFormat);
const defaultTimeFormat = "EEE dd MMM yyyy HH:mm 'UTC'";
const MapTime = ({
  dimensions,
  timeFormat: _timeFormat = defaultTimeFormat
}) => {
  const timeDimension = getTimeDimension(dimensions);
  if (!timeDimension) {
    return null;
  }
  const mapTime = formatTime(timeDimension.currentValue, _timeFormat);
  return /*#__PURE__*/React.createElement(Box, {
    "data-testid": "map-time",
    sx: {
      zIndex: 10,
      justifySelf: 'center',
      bottom: '12px',
      position: 'absolute'
    }
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    sx: {
      fontWeight: 500
    }
  }, mapTime)));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
const MS_IN_HOUR = 3600 * 1000;
// The following is necessary to allow to assign to the CanvasRenderingContext2D ctx property
/* eslint-disable no-param-reassign */
/**
 * This component registers to a webmap instance and draws axis for height and time in the map using the Map Canvas.
 */
const ProfileAxis = ({
  mapId
}) => {
  const drawAxisOnMapDisplay = (ctx, webMap, axisStyle) => {
    const bbox = webMap.getBBOX();
    const width = webMap.getWidth();
    const height = webMap.getHeight();
    const offY = height - 50;
    const offX = 20;
    try {
      const startDate = new Date(bbox.left);
      const endDate = new Date(bbox.right);
      webMap.setMessage(`${startDate.toDateString()}, ${startDate.getUTCHours()} to ${endDate.getUTCHours()} hours UTC`);
    } catch (e) {
      webMap.setMessage('');
    }
    // Draw the horizontal time axis
    drawTimeAxis(ctx, bbox, offX, offY, width, height, axisStyle);
    // Draw the vertical elevation axis
    drawHeightAxis(ctx, bbox, offX, offY, width, height, axisStyle);
    // Draw the mouse cursor
    drawCursor(ctx, bbox, offX, offY, width, height, webMap, axisStyle);
    // Draw the layer name
    drawLayerName(ctx, bbox, offX, offY, width, height, webMap, axisStyle);
  };
  React__default.useEffect(() => {
    const axisStyle = {
      backgroundColor: 'white',
      color: 'black',
      fontStyleSmall: '10px Roboto, Helvetica, Arial',
      fontStyleBig: '14px Roboto, Helvetica, Arial'
    };
    // Draw on map canvas callback function
    const beforeCanvasDisplay = ctx => {
      drawAxisOnMapDisplay(ctx, getWMJSMapById(mapId), axisStyle);
    };
    // unMount logic
    const unMount = () => {
      const webMap = getWMJSMapById(mapId);
      if (webMap) {
        webMap.removeListener('beforecanvasdisplay', beforeCanvasDisplay);
      }
    };
    // Mount logic
    const webMap = getWMJSMapById(mapId);
    if (webMap && webMap.getProjection().srs === 'GFI:TIME_ELEVATION') {
      webMap.hideScaleBar();
      webMap.hideMouseCursorProperties();
      webMap.setTimeOffset('');
      webMap.addListener('beforecanvasdisplay', beforeCanvasDisplay, true);
      webMap.draw();
    }
    return unMount;
  }, [mapId]);
  return null;
};
/**
 * Calculates a time spacing. This is used to place labels on the X axis (time axis).
 * To make readable time labels, the time spacing is discretized to rounded times,
 * The time spacing changes according to zoom level.
 * @param bbox
 * @param width
 * @returns
 */
const getTimeSpacingInMs = (bbox, width) => {
  const hoursInView = Math.abs(bbox.right - bbox.left) / (MS_IN_HOUR * width / 500);
  return Math.pow(2, Math.round((Math.log10(hoursInView / 10) + 0.5) / Math.log10(2)) - 1) * MS_IN_HOUR;
};
const drawTimeAxis = (ctx, bbox, offsetX, offsetY, width, height, axisStyle) => {
  const {
    backgroundColor,
    color,
    fontStyleSmall,
    fontStyleBig
  } = axisStyle;
  // Draw rectangle with backgroundColor for axis
  ctx.textBaseline = 'middle';
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, offsetY, width, 50);
  ctx.font = fontStyleSmall;
  ctx.textAlign = 'left';
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  const ticksMs = getTimeSpacingInMs(bbox, width);
  const roundedTimeLeftMs = Math.round(bbox.left / ticksMs) * ticksMs;
  const roundedTimeRightMs = Math.round(bbox.right / ticksMs) * ticksMs;
  const numHoursInView = Math.min((roundedTimeRightMs - roundedTimeLeftMs) / ticksMs + 1, 100);
  for (let hoursToPlot = 0; hoursToPlot < numHoursInView; hoursToPlot += 1) {
    const timeMs = roundedTimeLeftMs + hoursToPlot * ticksMs;
    const posX = Math.round((timeMs - bbox.left) / (bbox.right - bbox.left) * width) + 0.5;
    ctx.beginPath();
    ctx.moveTo(posX, offsetY + 0);
    ctx.lineTo(posX, offsetY + 10);
    const date = new Date(timeMs);
    const hourString = `0${date.getUTCHours()}`.slice(-2);
    const minuteString = `:${`0${date.getUTCMinutes()}`.slice(-2)}`;
    const txt = `${hourString}${minuteString}`;
    ctx.stroke();
    ctx.fillText(`${txt}`, posX - 13, offsetY + 16);
  }
  // Draw units in map
  ctx.font = fontStyleBig;
  ctx.fillText('time UTC (h)', width / 2 - 10, offsetY + 38);
};
/**
 * Calculates a spacing for the height axis. This is used to place labels on the Y axis (height axis).
 * To make readable height labels, the height spacing is discretized to rounded elevations,
 * The spacing changes according to zoom level.
 * @param bbox
 * @param height
 * @returns
 */
const getHeightSpacingInMs = (bbox, height) => {
  const numMeterInView = Math.abs(bbox.bottom - bbox.top) / height;
  return Math.pow(10, Math.round(Math.log10(numMeterInView * 100) + 0.5) - 1);
};
const drawHeightAxis = (ctx, bbox, offsetX, offsetY, width, height, axisStyle) => {
  const {
    backgroundColor,
    color,
    fontStyleSmall,
    fontStyleBig
  } = axisStyle;
  // Draw rectangle in backgroundColor for axis
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, 60 + offsetX, height);
  ctx.font = fontStyleSmall;
  ctx.textAlign = 'right';
  ctx.fillStyle = color;
  const ticksM = getHeightSpacingInMs(bbox, height);
  const roundedElevBottomMeter = Math.round(bbox.bottom / ticksM) * ticksM;
  const roundedElevTopMeter = Math.round(bbox.top / ticksM) * ticksM;
  const numKmInView = Math.min((roundedElevTopMeter - roundedElevBottomMeter) / ticksM + 1, 100);
  for (let kmToPlot = 0; kmToPlot < numKmInView; kmToPlot += 1) {
    const valueInKm = roundedElevBottomMeter + kmToPlot * ticksM;
    const posY = Math.round((bbox.top - valueInKm) / (bbox.top - bbox.bottom) * height) + 0.5;
    ctx.beginPath();
    ctx.moveTo(offsetX + 50, posY);
    ctx.lineTo(offsetX + 60, posY);
    ctx.stroke();
    const txt = valueInKm;
    ctx.fillText(`${txt}`, offsetX + 48, posY + 2);
  }
  // Draw height unit in axis
  ctx.font = axisStyle.fontStyleBig;
  ctx.save();
  ctx.translate(5, height / 2 - 50);
  ctx.rotate(-Math.PI / 2);
  ctx.font = fontStyleBig;
  ctx.fillText('height (m)', 5, 5);
  ctx.restore();
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, height - 60, offsetX + 60, 60);
  // Draw line border for map
  ctx.moveTo(offsetX + 60, offsetY + 0.5);
  ctx.lineTo(width, offsetY + 0.5);
  ctx.moveTo(offsetX + 60.5, 0);
  ctx.lineTo(offsetX + 60.5, height - 50);
  ctx.stroke();
};
const drawCursor = (ctx, bbox, offsetX, offsetY, width, height, webMapJS, axisStyle) => {
  // Do not draw if mouse is not in the map
  if (webMapJS.mouseX < 5 && webMapJS.mouseY < 5) {
    return;
  }
  /* Draw cursor line */
  const mouseX = Math.round(webMapJS.mouseX) + 0.5;
  const mouseY = Math.round(webMapJS.mouseY) + 0.5;
  ctx.strokeStyle = '#303030';
  ctx.setLineDash([15, 5]);
  ctx.beginPath();
  ctx.moveTo(0, mouseY);
  ctx.lineTo(width, mouseY);
  ctx.moveTo(mouseX, 0);
  ctx.lineTo(mouseX, height);
  ctx.stroke();
  ctx.strokeStyle = axisStyle.color;
  ctx.lineWidth = 1;
  ctx.setLineDash([]);
};
const drawLayerName = (ctx, bbox, offsetX, offsetY, width, height, webMapJS, axisStyle) => {
  // Draw layername
  const layers = webMapJS.getLayers();
  if (layers.length > 0) {
    ctx.textBaseline = 'middle';
    ctx.fillStyle = axisStyle.backgroundColor;
    ctx.font = axisStyle.fontStyleBig;
    ctx.textAlign = 'left';
    ctx.fillStyle = axisStyle.color;
    ctx.strokeStyle = axisStyle.color;
    ctx.fillText(`Layer: ${layers[0].name}`, 10, offsetY + 38);
  }
};
// Re-enable the previousely disabled eslint rule
/* eslint-enable no-param-reassign */

const MapView = _a => {
  var {
      children,
      controls = {
        zoomControls: true
      },
      displayTimeInMap = true,
      onWMJSMount
    } = _a,
    // rest props
    props = __rest(_a, ["children", "controls", "displayTimeInMap", "onWMJSMount"]);
  const {
    dimensions,
    mapId: mapIdProp
  } = props;
  const wmjsMapRef = React.useRef(null);
  const mapId = React.useRef(mapIdProp).current;
  if (mapIdProp !== mapId) {
    console.warn(`Warning, MapId ${mapId} has been updated to ${mapIdProp} on an already mounted component. Keeping ${mapId}`);
  }
  const {
    ref,
    height,
    width
  } = useResizeDetector();
  React.useEffect(() => {
    if (wmjsMapRef.current && !!width && !!height) {
      const {
        height: mapHeight,
        width: mapWidth
      } = wmjsMapRef.current.getSize();
      if (width !== mapWidth || height !== mapHeight) {
        wmjsMapRef.current.setSize(Math.ceil(width), Math.ceil(height));
      }
    }
  }, [width, height]);
  const reactMapViewProps = Object.assign(Object.assign({}, props), {
    mapId
  });
  return /*#__PURE__*/React.createElement(Box, {
    ref: ref,
    sx: {
      display: 'grid',
      width: '100%',
      height: '100%',
      position: 'relative',
      overflow: 'hidden'
    }
  }, controls && controls.zoomControls && ( /*#__PURE__*/React.createElement(MapControls, {
    style: {
      top: 0,
      position: 'absolute'
    }
  }, /*#__PURE__*/React.createElement(ProfileAxis, {
    mapId: mapId
  }), /*#__PURE__*/React.createElement(ZoomControls, {
    onZoomIn: () => {
      const wmjsMap = webmapUtils.getWMJSMapById(mapId);
      wmjsMap.zoomIn(undefined);
    },
    onZoomOut: () => {
      const wmjsMap = webmapUtils.getWMJSMapById(mapId);
      wmjsMap.zoomOut();
    },
    onZoomReset: () => {
      const wmjsMap = webmapUtils.getWMJSMapById(mapId);
      wmjsMap.zoomToLayer(wmjsMap.getActiveLayer());
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      gridColumnStart: 1,
      gridRowStart: 1
    }
  }, /*#__PURE__*/React.createElement(ReactMapView, Object.assign({}, reactMapViewProps, {
    showLegend: false,
    displayTimeInMap: false,
    onWMJSMount: id => {
      wmjsMapRef.current = webmapUtils.getWMJSMapById(id);
      if (onWMJSMount) {
        onWMJSMount(id);
      }
    }
  }), children)), displayTimeInMap && dimensions && /*#__PURE__*/React.createElement(MapTime, {
    dimensions: dimensions
  }));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
const MapViewLayer = props => {
  // TODO: This component should not do anything, but just return null: https://gitlab.com/opengeoweb/opengeoweb/-/issues/4579
  const {
    id
  } = props;
  return React.useMemo(() => {
    return /*#__PURE__*/React.createElement("div", {
      "data-testid": "mapViewLayer",
      style: {
        display: 'none'
      }
    }, id);
  }, [id]);
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * */
const MapWarningProperties = ({
  selectedFeatureProperties,
  languageIndex,
  parentSize
}) => {
  const language = languageIndex || 0;
  const newWidth = parentSize ? parentSize - 100 : 350;
  return /*#__PURE__*/React__default.createElement("div", {
    style: {
      position: 'fixed',
      width: `${newWidth}px`,
      height: '1px',
      display: 'flex',
      justifyContent: 'center',
      left: '50px'
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    "data-testid": "map-warning-properties",
    style: {
      marginTop: '50px',
      width: '350px',
      height: '190px',
      padding: '0px 20px 20px',
      overflow: 'auto',
      fontSize: '11px',
      backgroundColor: '#CCCCCCC0'
    }
  }, /*#__PURE__*/React__default.createElement("h1", {
    "data-testid": "map-warning-properties-title"
  }, selectedFeatureProperties.languages && selectedFeatureProperties.languages[language].areaDesc), selectedFeatureProperties.languages && selectedFeatureProperties.languages[language].areaDesc && ( /*#__PURE__*/React__default.createElement("pre", {
    "data-testid": "map-warning-properties-row"
  }, "event:", ' ', selectedFeatureProperties.languages && selectedFeatureProperties.languages[language].event)), selectedFeatureProperties.onset && ( /*#__PURE__*/React__default.createElement("pre", {
    "data-testid": "map-warning-properties-row"
  }, "Sent:", ' ', dateUtils.dateToString(dateUtils.utc(selectedFeatureProperties.onset), 'dd MMM yyyy, HH:mm'), ' ', "UTC")), selectedFeatureProperties.expires && ( /*#__PURE__*/React__default.createElement("pre", {
    "data-testid": "map-warning-properties-row"
  }, "Expires:", ' ', dateUtils.dateToString(dateUtils.utc(selectedFeatureProperties.expires), 'dd MMM yyyy, HH:mm'), ' ', "UTC")), selectedFeatureProperties.languages && selectedFeatureProperties.languages[language].senderName && ( /*#__PURE__*/React__default.createElement("pre", {
    "data-testid": "map-warning-properties-row"
  }, "Sender:", ' ', selectedFeatureProperties.languages && selectedFeatureProperties.languages[language].senderName)), selectedFeatureProperties.identifier && ( /*#__PURE__*/React__default.createElement("pre", {
    "data-testid": "map-warning-properties-row"
  }, "Identifier: ", selectedFeatureProperties.identifier))));
};

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * */
const baseLayerGrey = {
  id: webmapUtils.generateLayerId(),
  name: 'WorldMap_Light_Grey_Canvas',
  type: 'twms',
  layerType: LayerType.baseLayer
};
// Over Layers List
const KNMIgeoservicesBaselayers = {
  name: 'KNMIgeoservicesBaselayers',
  url: 'https://geoservices.knmi.nl/wms?DATASET=baselayers&',
  id: 'KNMIgeoservicesBaselayers'
};
const overLayer = {
  service: KNMIgeoservicesBaselayers.url,
  name: 'countryborders',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.overLayer
};

var defaultLayers = /*#__PURE__*/Object.freeze({
  __proto__: null,
  baseLayerGrey: baseLayerGrey,
  overLayer: overLayer
});

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
const DWD = {
  name: 'DWD',
  url: 'https://maps.dwd.de/geoserver/ows?',
  id: 'dwd'
};
const nationaalgeoregister = {
  name: 'nationaalgeoregister.nl',
  url: 'https://geodata.nationaalgeoregister.nl/cbsgebiedsindelingen/wms?request=GetCapabilities&service=wms',
  id: 'nationaalgeoregister'
};
const KNMImsgcpp = {
  name: 'KNMImsgcpp',
  url: 'https://adaguc-server-msg-cpp-portal.pmc.knmi.cloud/wms?DATASET=msgrt&',
  id: 'msgcpp-ogc-realtime'
};
const HeiGit = {
  name: 'HeiGit',
  url: 'https://maps.heigit.org/osm-wms/service',
  id: 'HeiGit-osm-wms'
};
const ArcGisCanvas = {
  name: 'ArcGisCanvas',
  url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/',
  id: 'ArcGisCanvas'
};
const KNMIgeoservicesRadar = {
  name: 'KNMIgeoservicesRadar',
  url: 'https://geoservices.knmi.nl/wms?dataset=RADAR&',
  id: 'KNMIgeoservicesRadar'
};
const KNMIgeoservicesObs = {
  name: 'KNMIgeoservicesObs',
  url: 'https://geoservices.knmi.nl/adagucserver?dataset=OBS',
  id: 'KNMIgeoservicesObs'
};
const KNMIgeoservicesKlimaatAtlas = {
  name: 'KNMIgeoservicesKlimaatAtlas',
  url: 'https://geoservices.knmi.nl/wms?DATASET=gsie-dataset&SERVICE=WMS&',
  id: 'KNMIgeoservicesKlimaatAtlas'
};
const KNMIgeoservicesHarmonie = {
  name: 'KNMIgeoservicesHarmonie',
  url: 'https://geoservices.knmi.nl/wms?DATASET=HARM_N25&',
  id: 'KNMIgeoservicesKliKNMIgeoservicesHarmoniemaatAtlas'
};
const KNMIgeoservicesHarmonieMLService = {
  name: 'KNMI Harmonie ML',
  url: 'https://geoservices.knmi.nl/wms?DATASET=HARM_N25_ML&SERVICE=WMS&',
  id: 'harmonieML'
};
const MetNorwayService = {
  name: 'MetNorwayService',
  url: 'https://wms-e1.geoweb.met.no/wms?',
  id: 'MetNorwayService'
};
const FMIopenwms = {
  name: 'FMIopenwms',
  url: 'https://openwms.fmi.fi/geoserver/wms?',
  id: 'FMIopenwms'
};
const DWDObservations = {
  name: 'DWDObservations',
  url: 'https://maps.dwd.de/geoserver/dwd/Wetter_Beobachtungen/ows?',
  id: 'DWDObservations'
};
const DWDWarnings = {
  name: 'DWDWarnings',
  url: 'https://maps.dwd.de/geoserver/dwd/Warnungen_Gemeinden_vereinigt/ows?',
  id: 'DWDWarnings'
};
const DWDWXProdukt = {
  name: 'DWDWXProdukt',
  url: 'https://maps.dwd.de/geoserver/dwd/WX-Produkt/ows?',
  id: 'DWDWXProdukt'
};
const EUMETSAT = {
  name: 'EUMETSAT',
  url: 'https://view.eumetsat.int/geoserver/wms?',
  id: 'EUMETSAT'
};
const eumetviewEUMETSAT = {
  name: 'eumetviewEUMETSAT',
  url: 'https://eumetview.eumetsat.int/geoserv/wms?',
  id: 'eumetviewEUMETSAT'
};
const ECMWFPublicService = {
  name: 'ECMWF',
  url: 'https://eccharts.ecmwf.int/wms/?token=public',
  id: 'ecmwf'
};
const MeteoCanada = {
  name: 'Meteo Canada',
  url: 'https://geo.weather.gc.ca/geomet/?lang=en&',
  id: 'MeteoCanada'
};

var publicServices = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DWD: DWD,
  nationaalgeoregister: nationaalgeoregister,
  KNMImsgcpp: KNMImsgcpp,
  HeiGit: HeiGit,
  ArcGisCanvas: ArcGisCanvas,
  KNMIgeoservicesRadar: KNMIgeoservicesRadar,
  KNMIgeoservicesObs: KNMIgeoservicesObs,
  KNMIgeoservicesKlimaatAtlas: KNMIgeoservicesKlimaatAtlas,
  KNMIgeoservicesHarmonie: KNMIgeoservicesHarmonie,
  KNMIgeoservicesHarmonieMLService: KNMIgeoservicesHarmonieMLService,
  MetNorwayService: MetNorwayService,
  FMIopenwms: FMIopenwms,
  DWDObservations: DWDObservations,
  DWDWarnings: DWDWarnings,
  DWDWXProdukt: DWDWXProdukt,
  EUMETSAT: EUMETSAT,
  eumetviewEUMETSAT: eumetviewEUMETSAT,
  ECMWFPublicService: ECMWFPublicService,
  MeteoCanada: MeteoCanada
});

/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * */
const baseLayer = {
  name: 'arcGisSat',
  title: 'arcGisSat',
  type: 'twms',
  layerType: LayerType.baseLayer,
  enabled: true,
  id: webmapUtils.generateLayerId()
};
// Base Layers List
const baseLayerOpenStreetMapNL = {
  id: 'base-layer-2',
  name: 'OpenStreetMap_NL',
  type: 'twms',
  layerType: LayerType.baseLayer,
  enabled: true
};
const baseLayerArcGisCanvas = {
  service: ArcGisCanvas.url,
  id: webmapUtils.generateLayerId(),
  name: 'arcGisCanvas',
  type: 'twms',
  enabled: true,
  layerType: LayerType.baseLayer
};
const baseLayerWorldMap = {
  id: webmapUtils.generateLayerId(),
  name: 'WorldMap',
  type: 'twms',
  layerType: LayerType.baseLayer,
  enabled: true
};
const baseLayerHeiGit = {
  service: HeiGit.url,
  id: webmapUtils.generateLayerId(),
  name: 'osm_auto:all',
  layerType: LayerType.baseLayer
};
// KNMI msgcpp List
const msgCppLayer = {
  // doesn't work with https
  service: KNMImsgcpp.url,
  name: 'lwe_precipitation_rate',
  format: 'image/png',
  enabled: true,
  style: 'precip-transparent/nearest',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
// Nederlandse Nationaal Georegister Layers list
const veiligheidsRegiosGebiedsIndelingenLabels = {
  service: nationaalgeoregister.url,
  name: 'cbsgebiedsindelingen:cbs_veiligheidsregio_2020_labelpoint',
  style: 'CBS_Label',
  enabled: false,
  layerType: LayerType.mapLayer
};
const veiligheidsRegiosGebiedsIndelingen = {
  service: nationaalgeoregister.url,
  name: 'cbsgebiedsindelingen:cbs_veiligheidsregio_2020_gegeneraliseerd',
  style: 'CBS_Gebiedsindeling',
  enabled: false,
  layerType: LayerType.mapLayer
};
// KNMI Geoservices Layers List
const radarLayer = {
  service: KNMIgeoservicesRadar.url,
  name: 'RAD_NL25_PCP_CM',
  format: 'image/png',
  enabled: true,
  style: 'radar/nearest',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const radarLayerWithError = {
  service: KNMIgeoservicesRadar.url,
  name: 'radarLayer',
  format: 'image/png',
  enabled: true,
  style: 'knmiradar/nearest',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsAirTemperature = {
  service: KNMIgeoservicesObs.url,
  name: '10M/ta',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsWind = {
  service: KNMIgeoservicesObs.url,
  name: '10M/wind',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsAirPressureAtSeaLevel = {
  service: KNMIgeoservicesObs.url,
  name: '10M/pp',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsRelativeHumidity = {
  service: KNMIgeoservicesObs.url,
  name: '10M/rh',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsGlobalSolarRadiation = {
  service: KNMIgeoservicesObs.url,
  name: '10M/qg',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const obsPrecipitationIntensityPWS = {
  service: KNMIgeoservicesObs.url,
  name: '10M/pg',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const harmonieRelativeHumidityPl = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'relative_humidity__at_pl',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  enabled: true
};
const harmonieWindPl = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'wind__at_pl',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  enabled: true
};
const harmonieAirTemperature = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'air_temperature__at_2m',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  enabled: true
};
const harmoniePrecipitation = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'precipitation_flux',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  enabled: true
};
const harmoniePressure = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'air_pressure_at_sea_level',
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  enabled: true
};
const harmonieWindFlags = {
  service: KNMIgeoservicesHarmonie.url,
  name: 'wind__at_10m',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const klimaatAtlasTG3 = {
  service: KNMIgeoservicesKlimaatAtlas.url,
  name: 'TG3',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
// DWD Layers List
const dwdWarningLayer = {
  service: DWDWarnings.url,
  name: 'Warnungen_Gemeinden_vereinigt',
  format: 'image/png',
  // style: 'warnungen_gemeinden_vereinigt_event_seamless_param',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const dwdRadarLayer = {
  service: DWDWXProdukt.url,
  name: 'WX-Produkt',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
// this needs authentication to work
const dwdObservationsWetterLayer = {
  service: DWDObservations.url,
  name: 'Wetter_Beobachtungen',
  style: 'Wetter_Symbole',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
  // headers: [{ name: 'Authorization', value: 'Basic ...' }]
};
const dwdObservationsWetterLayerWithHeader = {
  service: DWDObservations.url,
  name: 'Wetter_Beobachtungen',
  style: 'Wetter_Symbole',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer,
  headers: [{
    name: 'authorization',
    value: 'Basic aW50cmFuZXQtdXNlcjpDQnMjMTEh'
  }]
};
// this needs authentication to work
const dwdObservationsWindLayer = {
  service: DWDObservations.url,
  name: 'Wetter_Beobachtungen',
  style: 'Wetter_Wind',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
// FMI open wms Layers List
const FMITemp = {
  service: FMIopenwms.url,
  name: 'temperature-forecast',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
// Met Norway Layers List
const MetNoTemp = {
  service: MetNorwayService.url,
  name: 'thredds_meps_latest_wind',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const metNorwayWind1 = {
  service: MetNorwayService.url,
  name: 'thredds_meps_latest_wind',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const metNorwayWind2 = {
  service: MetNorwayService.url,
  name: 'thredds_aromearctic_extracted_t',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const metNorwayWind3 = {
  service: MetNorwayService.url,
  name: 'thredds_nk800_temperature',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const metNorwayLatestT = {
  service: MetNorwayService.url,
  name: 'thredds_meps_latest_t',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const metNorwaySalinaty = {
  service: MetNorwayService.url,
  name: 'thredds_barents_2_5km_1h_salinity',
  format: 'image/png',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgFesEUMETSAT = {
  service: EUMETSAT.url,
  name: 'msg_fes:rgb_eview',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgNaturalenhncdEUMETSAT = {
  service: eumetviewEUMETSAT.url,
  name: 'meteosat:msg_naturalenhncd',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgAshEUMETSAT = {
  service: eumetviewEUMETSAT.url,
  name: 'meteosat:msg_ash',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgCthEUMETSAT = {
  service: eumetviewEUMETSAT.url,
  name: 'meteosat:msg_cth',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgFogEUMETSAT = {
  service: eumetviewEUMETSAT.url,
  name: 'meteosat:msg_fog',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};
const msgNaturalEUMETSAT = {
  service: eumetviewEUMETSAT.url,
  name: 'meteosat:msg_natural',
  enabled: true,
  id: webmapUtils.generateLayerId(),
  layerType: LayerType.mapLayer
};

var publicLayers = /*#__PURE__*/Object.freeze({
  __proto__: null,
  defaultLayers: defaultLayers,
  baseLayer: baseLayer,
  baseLayerOpenStreetMapNL: baseLayerOpenStreetMapNL,
  baseLayerArcGisCanvas: baseLayerArcGisCanvas,
  baseLayerWorldMap: baseLayerWorldMap,
  baseLayerHeiGit: baseLayerHeiGit,
  msgCppLayer: msgCppLayer,
  veiligheidsRegiosGebiedsIndelingenLabels: veiligheidsRegiosGebiedsIndelingenLabels,
  veiligheidsRegiosGebiedsIndelingen: veiligheidsRegiosGebiedsIndelingen,
  radarLayer: radarLayer,
  radarLayerWithError: radarLayerWithError,
  obsAirTemperature: obsAirTemperature,
  obsWind: obsWind,
  obsAirPressureAtSeaLevel: obsAirPressureAtSeaLevel,
  obsRelativeHumidity: obsRelativeHumidity,
  obsGlobalSolarRadiation: obsGlobalSolarRadiation,
  obsPrecipitationIntensityPWS: obsPrecipitationIntensityPWS,
  harmonieRelativeHumidityPl: harmonieRelativeHumidityPl,
  harmonieWindPl: harmonieWindPl,
  harmonieAirTemperature: harmonieAirTemperature,
  harmoniePrecipitation: harmoniePrecipitation,
  harmoniePressure: harmoniePressure,
  harmonieWindFlags: harmonieWindFlags,
  klimaatAtlasTG3: klimaatAtlasTG3,
  dwdWarningLayer: dwdWarningLayer,
  dwdRadarLayer: dwdRadarLayer,
  dwdObservationsWetterLayer: dwdObservationsWetterLayer,
  dwdObservationsWetterLayerWithHeader: dwdObservationsWetterLayerWithHeader,
  dwdObservationsWindLayer: dwdObservationsWindLayer,
  FMITemp: FMITemp,
  MetNoTemp: MetNoTemp,
  metNorwayWind1: metNorwayWind1,
  metNorwayWind2: metNorwayWind2,
  metNorwayWind3: metNorwayWind3,
  metNorwayLatestT: metNorwayLatestT,
  metNorwaySalinaty: metNorwaySalinaty,
  msgFesEUMETSAT: msgFesEUMETSAT,
  msgNaturalenhncdEUMETSAT: msgNaturalenhncdEUMETSAT,
  msgAshEUMETSAT: msgAshEUMETSAT,
  msgCthEUMETSAT: msgCthEUMETSAT,
  msgFogEUMETSAT: msgFogEUMETSAT,
  msgNaturalEUMETSAT: msgNaturalEUMETSAT
});

export { CanvasComponent, DRAWMODE, EDITMODE, EditModeButton as EditModeButtonField, FeatureLayers, GeoJSONTextField, IntersectionSelect, Legend, LegendDialog, LegendLayout, MapControlButton, MapControls, MapDrawContainer, MapTime, MapView, MapViewLayer, MapWarningProperties, NEW_FEATURE_CREATED, NEW_LINESTRING_CREATED, NEW_POINT_CREATED, ORIGIN_REACTMAPVIEW_ONMAPCHANGEDIMENSION, ORIGIN_REACTMAPVIEW_ONUPDATELAYERINFO, ReactMapView, ReactMapViewLayer, SelectField, StoryLayoutGrid, ZoomControls, addFeatureProperties, basicExampleDrawOptions, basicExampleMultipleShapeDrawOptions, checkHoverFeatures, createInterSections, currentlySupportedDrawModes, defaultBox, defaultDelete, defaultGeoJSONStyleProperties, defaultIntersectionStyleProperties, defaultLayers, defaultModes, defaultPoint, defaultPolygon, defaultTimeFormat, distance, drawPolyStoryStyles, emptyGeoJSON, endToolExampleConfig, exampleIntersectionOptions, exampleIntersectionWithShapeOptions, exampleIntersections, exampleIntersectionsMultiDrawTool, featureBox, featureMultiPoint, featurePoint, featurePolygon, fillOptions, firButtonDrawId, formatTime, getDoubleControlToolIcon, getDrawFunctionFromStore, getFeatureCollection, getFirTitle, getGeoJSONPropertyValue, getGeoJson, getIcon, getIntersectionToolIcon, getTimeDimension, getToolIcon, intersectGeoJSONS, intersectionFeatureBE, intersectionFeatureNL, isGeoJSONFeatureCreatedByTool, lineString, moveFeature, opacityOptions, publicLayers, publicServices, registerDrawFunction, rewindGeometry, simpleBoxGeoJSON, simpleBoxGeoJSONWrongOrder, simpleFlightRouteLineStringGeoJSON, simpleFlightRoutePointsGeoJSON, simpleLineStringGeoJSON, simpleMultiPolygon, simplePointsGeojson, simplePolygonGeoJSON, simpleSmallLineStringGeoJSON, startToolExampleConfig, strokeWidthOptions, updateEditModeButtonsWithFir, useGeoJSON, useMapDrawTool };
